import { DuplicateRef, DuplicateStatus, GalleryImage, highestShopPrice, Item, ItemCrop, McpCredential, mcpEndpoint, Note, NoteLevel, Position, Room, ShopLink } from "./Api.js";
import type { App } from "./InventoryApp.js";
import type { CompareCategory, CompareEntry, CompareKind, CompareResult, FieldDiff } from "./compare.js";

// SheetJS und JSZip werden als eigene globale Skripte geladen (siehe
// jsidea.config.json, uids "hausinventar-sheetjs"/"hausinventar-jszip"),
// nicht als ESM-Import — daher nur lose als globale Objekte deklariert,
// analog zu jQuery an anderer Stelle in dieser App.
declare const XLSX: any;
declare const JSZip: any;

const QUELLEN = ["Kaufbeleg", "Rechnung", "Online-Preis", "Schätzung"];

/**
 * Ein festes Symbol je Datentyp, überall gleich.
 *
 * Räume, Positionen und Gegenstände stehen an vielen Stellen nebeneinander —
 * in der Navigation, in aufgeklappten Listen, in Suchtreffern, in
 * Duplikat-Verweisen. Nur an der Beschriftung ist dort auf den ersten Blick
 * nicht zu erkennen, worauf ein Eintrag zeigt. Die drei Symbole sind deshalb
 * bewusst nirgends anders in Gebrauch.
 */
export const TYPE_ICONS = {
    room: "door-open",
    position: "boxes-stacked",
    item: "tag",
} as const;

export type DataKind = keyof typeof TYPE_ICONS;

/** Symbol eines Datentyps, fertig als HTML. */
export function typeIcon(kind: DataKind): string {
    // Eigene Klasse, damit sich das Symbol gezielt ansprechen lässt — in den
    // Listen tritt es zurück, in Überschriften nicht.
    return icon(TYPE_ICONS[kind], "hi-type-icon");
}

const TYPE_LABELS: Record<DataKind, { singular: string; plural: string }> = {
    room: { singular: "Raum", plural: "Räume" },
    position: { singular: "Position", plural: "Positionen" },
    item: { singular: "Gegenstand", plural: "Gegenstände" },
};

/**
 * Typ eines Treffers als eigenes Merkmal, nicht als Fließtext.
 *
 * In der Vorschlagsliste stehen Räume, Positionen und Gegenstände
 * durcheinander. Stand der Typ nur als erstes Wort der Ortszeile, verschwamm er
 * mit dem Fundort ("Gegenstand · Pos. 5 · Küche") — man musste lesen statt zu
 * sehen, was man da vor sich hat.
 */
export function typeChip(kind: DataKind): string {
    return `<span class="hi-type-chip hi-type-chip--${kind}">${typeIcon(kind)}${esc(TYPE_LABELS[kind].singular)}</span>`;
}

export function esc(v: unknown): string {
    return String(v ?? "").replace(/[&<>"']/g, (c) => ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
    }[c] as string));
}

export function fmt(n: number): string {
    return Number(n || 0).toLocaleString("de-DE") + " €";
}

/**
 * Die sichtbare Datensatz-Nummer als Knopf: Ein Klick legt sie in die
 * Zwischenablage — sie wird ständig gebraucht, um einen Gegenstand außerhalb
 * der App zu benennen. Der Knopf sieht aus wie Text; nur beim Überfahren
 * zeigt er sich als anklickbar.
 *
 * `extraClass` übernimmt die Klasse, die an der Stelle vorher am Text hing
 * (etwa `hi-cell-sub`), damit sich die Schriftgröße nicht ändert. Steht die
 * Nummer in einer anklickbaren Zeile, fängt der Knopf den Klick ab
 * (`handleCopyId` in InventoryApp.ts) — die Zeile öffnet sich dabei nicht.
 */
export function idTag(id: number, extraClass = ""): string {
    return `<button type="button" class="hi-id${extraClass ? " " + extraClass : ""}" data-copy-id="${id}" title="Nummer kopieren" aria-label="Nummer ${id} kopieren">#${id}</button>`;
}

/** Editierbarer Zahlenwert mit deutschem Komma statt Punkt als Dezimaltrennzeichen. */
function decimalValue(n: number): string {
    return String(n ?? 0).replace(".", ",");
}

function initials(name: string): string {
    const parts = (name || "").trim().split(/\s+/);
    return ((parts[0]?.[0] || "?") + (parts[1]?.[0] || "")).toUpperCase();
}

function option(value: string, label: string, selected: string): string {
    return `<option value="${esc(value)}"${value === selected ? " selected" : ""}>${esc(label)}</option>`;
}

function positionOf(app: App, id: number): Position | undefined {
    return app.positions.find((p) => p.id === id);
}

/**
 * Freitextfilter der drei Listen.
 *
 * Durchsucht wird alles, was die Liste in ihren Spalten zeigt — bis in die
 * aufklappbaren Unterzeilen hinein. Wer in der Raumliste eine Position sucht,
 * meint den Raum, in dem sie liegt; wer in der Positionsliste einen
 * Gegenstand eingibt, meint die Position darüber. Eine Suche, die nur den
 * Titel kennt, findet genau das nicht — und lässt den Eindruck zurück, der
 * Eintrag wäre gar nicht erfasst.
 *
 * Mehrere Wörter müssen alle vorkommen, in beliebiger Reihenfolge: „schrank
 * küche" soll den Küchenschrank finden, nicht alles mit „schrank".
 */
function matchesFilter(query: string, ...felder: (string | number)[]): boolean {
    const terms = searchTerms(query);
    if (terms.length === 0) return true;
    const haystack = felder.join(" ").toLowerCase();
    return terms.every((t) => haystack.includes(t));
}

/**
 * Trifft eine Sucheingabe wie "1953" oder "#1953" genau diese Post-ID?
 *
 * Bewusst exakt statt als Teilstring: Die IDs stehen seit der MCP-Anbindung
 * überall im UI, also soll ihre Eingabe den Datensatz direkt finden — aber
 * "20" darf nicht nebenbei jede ID treffen, in der "20" vorkommt.
 */
function matchesId(query: string, id: number): boolean {
    const m = query.trim().match(/^#?(\d+)$/);
    return m !== null && Number(m[1]) === id;
}

/**
 * Bündelt die Bezeichnungen aller Gegenstände je Position.
 *
 * Einmal aufgebaut statt je Zeile neu gesucht: Der Filter läuft bei jedem
 * Tastendruck, und itemsOf() sieht für jede der Positionen alle knapp
 * zweitausend Gegenstände durch.
 */
function itemTitlesByPosition(app: App): Map<number, string> {
    const map = new Map<number, string>();
    for (const item of app.items) {
        map.set(item.position, `${map.get(item.position) ?? ""} ${item.title}`);
    }
    return map;
}

function itemsOf(app: App, positionId: number): Item[] {
    return app.items
        .filter((i) => i.position === positionId)
        .sort((a, b) => a.order - b.order || a.id - b.id);
}

/**
 * Zählt dieser Gegenstand in die Versicherungssumme?
 *
 * Ein bestätigtes Duplikat ist dieselbe Sache, die an anderer Stelle bereits
 * bewertet wurde. Gelöscht wird sie nicht — der Nachweis, dass der Verdacht
 * geprüft wurde, ist mehr wert als eine aufgeräumte Liste —, gezählt aber
 * nirgends. Ein bloß *möglicher* Doppeleintrag zählt weiter mit: Solange nicht
 * geprüft ist, wäre das Weglassen eine stille Kürzung der Schadensumme.
 */
export function countsAsValue(item: Item): boolean {
    return item.duplikatStatus !== "bestaetigt";
}

/**
 * Alle Gegenstände einer Position, deren Ausschnitt auf diesem Foto liegt.
 *
 * Grundlage der Markierungen in der Lightbox: Auf dem Positionsfoto stehen
 * dann alle bereits erfassten Bereiche als Rechtecke — was außerhalb liegt,
 * ist noch nicht aufgenommen. Zugeordnet wird wie bei den Vorschauen in der
 * Zeile (cropSource) streng über die Attachment-ID, damit Foto und Zeilen
 * dasselbe zeigen.
 */
export function cropsOnPhoto(app: App, positionId: number, mediaId: number): { item: Item; crop: ItemCrop }[] {
    return app.items
        .filter((i) => i.position === positionId && i.bildausschnitt?.media === mediaId)
        .map((item) => ({ item, crop: item.bildausschnitt! }));
}

function itemValue(item: Item): number {
    return countsAsValue(item) ? item.wert * item.anzahl : 0;
}

function positionValue(app: App, positionId: number): number {
    return itemsOf(app, positionId).reduce((s, i) => s + itemValue(i), 0);
}

/** Summe der Einzelstücke — 3 Gegenstandszeilen können 60 Stück sein. */
function stueckzahl(items: Item[]): number {
    return items.reduce((s, i) => s + (i.anzahl || 0), 0);
}

/**
 * "Gegenstände/Stück" als kompakte Angabe, z. B. "1/5" für einen
 * Gegenstand, der fünfmal vorhanden ist.
 */
function countLabel(items: Item[]): string {
    return ratio(items.length, stueckzahl(items));
}

/** Einheitliche Schreibweise für Verhältnisangaben: "0 / 6", nicht "0/6". */
function ratio(a: number, b: number): string {
    return `${a} / ${b}`;
}

export function uploadProgressId(owner: string, ownerId: number): string {
    return `hi-progress-${owner}-${ownerId}`;
}

function photoGrid(fotos: Item["fotos"], owner: "item" | "room" | "position" | "new-item" | "new-position", ownerId: number): string {
    return `
    <div class="hi-photos">
        ${fotos.map((f) => `
            <div class="hi-photo">
                <img src="${esc(f.sizes?.thumbnail || f.url)}" alt="${esc(f.filename)}" data-action="view-photo" data-full="${esc(f.url)}" data-alt="${esc(f.filename)}" data-lb-kind="${owner}" data-lb-id="${ownerId}" data-media="${f.ID}">
                <button type="button" class="hi-photo__remove" data-action="remove-photo" data-owner="${owner}" data-id="${ownerId}" data-photo-id="${f.ID}" title="Foto entfernen" aria-label="Foto entfernen">${icon("xmark")}</button>
            </div>`).join("")}
    </div>
    <label class="hi-dropzone" data-dropzone="${owner}" data-id="${ownerId}">
        <input type="file" accept="image/*" multiple data-upload="${owner}" data-id="${ownerId}" hidden>
        <span class="hi-dropzone__text"><b>Foto wählen</b> oder hierher ziehen</span>
        <div class="hi-dropzone__progress" id="${uploadProgressId(owner, ownerId)}" hidden>
            <div class="hi-dropzone__progress-track"><div class="hi-dropzone__progress-fill" style="width:0%"></div></div>
            <span class="hi-dropzone__progress-label">0%</span>
        </div>
    </label>`;
}

function kv(label: string, value: string): string {
    return `<div class="hi-kv"><span class="hi-kv__label">${label}</span><span class="hi-kv__value">${value || "—"}</span></div>`;
}

/* --------------------------------------------------------- Shop-Links */

/** Datum in deutscher Schreibweise, leer bleibt leer (statt "Invalid Date"). */
function dateLabel(iso: string): string {
    if (!iso) return "";
    const date = new Date(`${iso}T12:00:00`);
    return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString("de-DE");
}

/** Domain als Kurzname des Shops — der volle Link wäre in der Zeile unlesbar. */
function shopHost(url: string): string {
    try {
        return new URL(url).hostname.replace(/^www\./, "");
    } catch {
        return url;
    }
}

/**
 * Vergleichsangebote in der Gegenstandszeile.
 *
 * Preis und Abrufdatum stehen bewusst direkt neben dem Link: Ohne sie ist
 * nicht nachvollziehbar, worauf `wert` beruht, und ein Shop-Preis von vor
 * einem halben Jahr ist etwas anderes als einer von gestern. Liegt ein
 * Angebot über dem eingetragenen Wert, wird das markiert — dann ist der Wert
 * zu niedrig angesetzt, und genau das kostet im Schadenfall Geld.
 */
function shopLinkList(item: Item): string {
    if (item.shopLinks.length === 0) return "";
    const hoechster = highestShopPrice(item.shopLinks);
    const zuNiedrig = hoechster > 0 && item.wert > 0 && hoechster > item.wert + 0.005;

    return `
    <div class="hi-shop-links">
        ${item.shopLinks.map((link) => `
            <a class="hi-shop-link" href="${esc(link.url)}" target="_blank" rel="noopener noreferrer" title="${esc(link.url)}">
                ${icon("arrow-up-right-from-square")}
                <span class="hi-shop-link__host">${esc(shopHost(link.url))}</span>
                ${link.preis > 0 ? `<span class="hi-shop-link__price">${fmt(link.preis)}</span>` : ""}
                ${link.datum ? `<span class="hi-shop-link__date">${esc(dateLabel(link.datum))}</span>` : ""}
            </a>`).join("")}
        ${zuNiedrig ? `<span class="hi-shop-links__warn">${icon("triangle-exclamation")} Höchstes Angebot ${fmt(hoechster)} liegt über dem eingetragenen Wert.</span>` : ""}
    </div>`;
}

/**
 * Eine Eingabezeile für ein Vergleichsangebot.
 *
 * Wird auch nachträglich von der App eingehängt (Knopf „+ Shop-Link"), ohne
 * dass die ganze Ansicht neu rendert — ein render() würde die bereits
 * getippten Formularwerte verwerfen.
 */
export function shopLinkRow(link?: ShopLink): string {
    const heute = new Date().toISOString().slice(0, 10);
    const url = link?.url ?? "";
    const preis = link && link.preis > 0 ? decimalValue(link.preis) : "";
    // Neue Zeilen tragen das heutige Datum: Der Abruf passiert genau jetzt.
    const datum = link ? link.datum : heute;

    return `
    <div class="hi-shop-row" data-shop-row>
        <input class="hi-input hi-shop-row__url" type="url" name="shop_url" value="${esc(url)}" placeholder="https://…">
        <input class="hi-input hi-shop-row__price" type="text" inputmode="decimal" name="shop_preis" value="${esc(preis)}" placeholder="Preis" aria-label="Preis im Shop">
        <input class="hi-input hi-shop-row__date" type="date" name="shop_datum" value="${esc(datum)}" aria-label="Datum des Abrufs">
        <button type="button" class="hi-btn hi-btn--icon hi-btn--secondary" data-action="use-shop-price" title="Preis als Wert je Stück übernehmen" aria-label="Preis als Wert je Stück übernehmen">${icon("arrow-turn-down-left")}</button>
        <button type="button" class="hi-btn hi-btn--icon hi-btn--secondary" data-action="remove-shop-link" title="Angebot entfernen" aria-label="Angebot entfernen">${icon("xmark")}</button>
    </div>`;
}

/**
 * Formularblock für alle Vergleichsangebote eines Gegenstands.
 *
 * Immer mindestens eine leere Zeile, damit ein Angebot ohne Umweg über einen
 * zusätzlichen Klick eingetragen werden kann.
 */
function shopLinkFields(links: ShopLink[]): string {
    const rows = links.length ? links.map((l) => shopLinkRow(l)).join("") : shopLinkRow();
    return `
    <div class="hi-field hi-shop-field">
        <span class="hi-field__label">Shop-Links (Vergleichsangebote)</span>
        <p class="hi-field__hint">Link, abgelesener Preis und Datum des Abrufs. Mehrere Angebote belegen die Preisspanne für genau dieses Produkt — maßgeblich ist der Neupreis eines gleichwertigen Geräts, nicht das billigste Angebot einer kleineren Ausführung.</p>
        <div class="hi-shop-rows" data-shop-rows>${rows}</div>
        <button type="button" class="hi-btn hi-btn--secondary hi-shop-field__add" data-action="add-shop-link">${icon("plus")} Shop-Link</button>
    </div>`;
}

/**
 * Eingabefeld für die Versandkosten — in allen drei Gegenstandsformularen gleich.
 *
 * Bewusst **je Bestellung**, nicht je Stück: Wer zwölf Spannbettlaken
 * nachbestellt, zahlt einmal Versand. Der Betrag geht deshalb auch in keine
 * Summe ein — er steht daneben, weil er sich später nicht mehr rekonstruieren
 * lässt, wenn der Beleg weg ist.
 */
function versandkostenField(value: number): string {
    return `
    <label class="hi-field">Versandkosten (€)
        <input class="hi-input" type="text" inputmode="decimal" name="versandkosten"
            value="${value ? decimalValue(value) : ""}" placeholder="0,00">
    </label>`;
}

/** Eingabefeld für die Wertbegründung — in allen drei Gegenstandsformularen gleich. */
function wertBegruendungField(value: string): string {
    return `
    <label class="hi-field">Wertbegründung
        <p class="hi-field__hint">Ein bis drei Sätze, die der Versicherung den Betrag nachvollziehbar machen: welches gleichwertige Produkt zugrunde liegt, woher der Preis stammt und was den Ansatz stützt oder mindert. Geht wie „Sonstiges" wörtlich an die Versicherung.</p>
        <textarea class="hi-input" name="wert_begruendung" rows="3"
            placeholder="z. B. Wiederbeschaffung eines gleichwertigen 17-Zoll-Monitors; vergleichbare Neugeräte liegen bei 170–180 €, angesetzt sind 178 €.">${esc(value)}</textarea>
    </label>`;
}

/**
 * Die Begründung des Werts in der Gegenstandszeile.
 *
 * Steht direkt unter den Vergleichsangeboten, weil sie zusammengehören: Die
 * Links belegen, *woher* der Preis stammt, die Begründung erklärt, *warum* er
 * für dieses Stück angemessen ist. Fehlt sie bei einem nennenswerten Betrag,
 * wird das angemerkt — im Schadenfall ist ein unbegründeter Wert der erste,
 * den die Versicherung kürzt.
 */
function wertBegruendung(item: Item): string {
    if (item.wertBegruendung) {
        return `
        <div class="hi-begruendung">
            <span class="hi-begruendung__label">${icon("scale-balanced")} Wertbegründung</span>
            <p class="hi-begruendung__text">${esc(item.wertBegruendung)}</p>
        </div>`;
    }
    // Kleinbeträge muss niemand rechtfertigen; die Schwelle hält den Hinweis
    // dort, wo er etwas bewirkt.
    if (item.wert * item.anzahl < 50) return "";
    return `
        <div class="hi-begruendung hi-begruendung--fehlt">
            <span class="hi-begruendung__label">${icon("scale-balanced")} Keine Wertbegründung hinterlegt</span>
        </div>`;
}

/* ---------------------------------------------------------- Duplikate */

const DUPLICATE_LABELS: Record<Exclude<DuplicateStatus, "">, string> = {
    moeglich: "Möglicher Doppeleintrag",
    bestaetigt: "Bestätigtes Duplikat",
    kein_duplikat: "Geprüft – kein Duplikat",
};

const DUPLICATE_ICONS: Record<Exclude<DuplicateStatus, "">, string> = {
    moeglich: "clone",
    bestaetigt: "ban",
    kein_duplikat: "circle-check",
};

export function duplicateLabel(status: DuplicateStatus): string {
    return status ? DUPLICATE_LABELS[status] : "Kein Verdacht";
}

/** Status-Plakette mit Beschriftung; leer ohne Verdacht. */
function dupBadge(status: DuplicateStatus): string {
    return status
        ? `<span class="hi-status hi-status--dup-${status}">${icon(DUPLICATE_ICONS[status])} ${esc(duplicateLabel(status))}</span>`
        : "";
}

/** Nur das Symbol, für enge Zeilen; die Beschriftung steht im Tooltip. */
function dupBadgeIcon(status: DuplicateStatus): string {
    return status
        ? `<span class="hi-status hi-status--dup-${status}" title="${esc(duplicateLabel(status))}">${icon(DUPLICATE_ICONS[status])}</span>`
        : "";
}

/**
 * Ein Duplikat-Verweis, wie er bei einem Gegenstand erscheint.
 *
 * Gespeichert wird ein Verweis nur dort, wo er eingetragen wurde (A → B). Der
 * Verdacht betrifft aber beide: Wer B ansieht, muss erfahren, dass A dieselbe
 * Sache sein könnte. Deshalb erscheint bei B ein abgeleiteter Gegenverweis —
 * ohne dass er doppelt gepflegt werden muss.
 */
export interface DuplicateLink extends DuplicateRef {
    /** Nur der andere Gegenstand verweist hierher; hier selbst steht nichts. */
    incoming: boolean;
}

let duplicateLinkCache: { stamp: number; links: Map<number, DuplicateLink[]> } | null = null;

/**
 * Alle Verweise je Gegenstand, in beide Richtungen.
 *
 * Erst die eigenen, dann die abgeleiteten: Hat B den Gegenverweis auf A selbst
 * eingetragen (der Import tut das mit dem Präfix „Gegenverweis:"), bleibt es
 * bei diesem einen — dasselbe Paar soll nicht zweimal dastehen. Hängt an
 * dataStamp wie der Suchindex: einmal je Datenstand statt je Zeile.
 */
function duplicateLinkMap(app: App): Map<number, DuplicateLink[]> {
    if (duplicateLinkCache && duplicateLinkCache.stamp === app.dataStamp) {
        return duplicateLinkCache.links;
    }
    const links = new Map<number, DuplicateLink[]>();
    const add = (id: number, link: DuplicateLink) => {
        const liste = links.get(id);
        if (liste) liste.push(link);
        else links.set(id, [link]);
    };
    const vorhanden = new Set(app.items.map((i) => i.id));

    for (const item of app.items) {
        for (const ref of item.duplikatRefs) add(item.id, { ...ref, incoming: false });
    }
    for (const item of app.items) {
        for (const ref of item.duplikatRefs) {
            if (!ref.item || ref.item === item.id || !vorhanden.has(ref.item)) continue;
            if ((links.get(ref.item) ?? []).some((l) => l.item === item.id)) continue;
            add(ref.item, { item: item.id, position: item.position, note: ref.note, incoming: true });
        }
    }

    duplicateLinkCache = { stamp: app.dataStamp, links };
    return links;
}

export function duplicateLinks(app: App, item: Item): DuplicateLink[] {
    return duplicateLinkMap(app).get(item.id) ?? [];
}

/**
 * Der Duplikat-Status, wie er bei einem Gegenstand angezeigt wird.
 *
 * Der eigene Status gilt immer. Fehlt er, obwohl ein anderer Gegenstand
 * hierher verweist, leitet er sich aus dem Verweisenden ab: Ist dort noch
 * offen, ob es ein Doppeleintrag ist, gilt das für dieses Paar hier genauso.
 * Ist dort entschieden, ist dieser hier das Gegenstück, das gezählt bleibt —
 * „Kein Duplikat". Abgeleitet wird nie „bestätigt": Das nähme einen
 * Gegenstand aus der Summe, den niemand dazu bestimmt hat. Gerechnet wird
 * ohnehin weiter nur mit dem eigenen Status (countsAsValue).
 */
export function effectiveDuplicateStatus(app: App, item: Item): DuplicateStatus {
    if (item.duplikatStatus) return item.duplikatStatus;
    const verweisende = duplicateLinks(app, item)
        .filter((l) => l.incoming)
        .map((l) => app.items.find((i) => i.id === l.item))
        .filter((i): i is Item => !!i);
    if (verweisende.some((i) => i.duplikatStatus === "moeglich")) return "moeglich";
    return verweisende.some((i) => i.duplikatStatus !== "") ? "kein_duplikat" : "";
}

/** Alle Gegenstände mit einem Duplikat-Eintrag, ungeprüfte zuerst. */
export function duplicateItems(app: App): Item[] {
    const rank: Record<string, number> = { moeglich: 0, bestaetigt: 1, kein_duplikat: 2 };
    const status = new Map(app.items.map((i) => [i.id, effectiveDuplicateStatus(app, i)]));
    return app.items
        .filter((i) => status.get(i.id) !== "")
        .sort((a, b) => (rank[status.get(a.id)!] ?? 3) - (rank[status.get(b.id)!] ?? 3) || a.title.localeCompare(b.title, "de"));
}

/**
 * Ein Duplikat-Fall: alle Gegenstände, die dieselbe Sache sein könnten.
 *
 * Mindestens einer, meist zwei — aber nicht zwangsläufig. Verdachte bilden
 * Ketten: Der Import vergleicht jedes Bild mit dem vorigen, und wenn dieselbe
 * Sache auf vier aufeinanderfolgenden Fotos liegt, entsteht A↔B, B↔C, C↔D. Das
 * ist ein einziger Fall mit vier Beteiligten, keine drei Fälle.
 */
export interface DuplicateGroup {
    key: string;
    items: Item[];
    /** Begründungen aus allen Verweisen der Gruppe, ohne Wiederholungen. */
    notes: string[];
    /** Verweise, deren Ziel es nicht mehr gibt — als Fundstelle in Worten. */
    missing: string[];
    status: DuplicateStatus;
}

/**
 * Duplikat-Verdachte als Gruppen — jeder Fall genau einmal.
 *
 * Der Import markiert beide Seiten (A verweist auf B, B auf A), damit der Fall
 * von jeder Position aus auffällt. In der Prüfansicht wäre das doppelt: Man
 * entschiede mehrfach über dieselbe Frage. Deshalb werden die Verweise hier als
 * Graph gelesen und in Zusammenhangskomponenten zerlegt — jede Komponente ist
 * ein Fall, unabhängig davon, wie viele Gegenstände daran hängen.
 */
export function duplicateGroups(app: App): DuplicateGroup[] {
    const suspects = duplicateItems(app);
    const byId = new Map(suspects.map((i) => [i.id, i]));

    // Nachbarschaft in beide Richtungen: Ein Verweis von A auf B verbindet die
    // beiden auch dann, wenn nur A ihn hinterlegt hat.
    const neighbours = new Map<number, Set<number>>();
    const link = (a: number, b: number) => {
        if (!neighbours.has(a)) neighbours.set(a, new Set());
        neighbours.get(a)!.add(b);
    };
    for (const item of suspects) {
        if (!neighbours.has(item.id)) neighbours.set(item.id, new Set());
        for (const ref of duplicateLinks(app, item)) {
            const other = ref.item ? byId.get(ref.item) : undefined;
            if (!other) continue;
            link(item.id, other.id);
            link(other.id, item.id);
        }
    }

    const seen = new Set<number>();
    const groups: DuplicateGroup[] = [];

    for (const start of suspects) {
        if (seen.has(start.id)) continue;

        // Breitensuche über die Verweise — sammelt die ganze Kette ein.
        const members: Item[] = [];
        const queue = [start.id];
        seen.add(start.id);
        while (queue.length) {
            const id = queue.shift()!;
            const item = byId.get(id);
            if (!item) continue;
            members.push(item);
            for (const next of neighbours.get(id) ?? []) {
                if (seen.has(next)) continue;
                seen.add(next);
                queue.push(next);
            }
        }

        // Begründungen einsammeln. Der Import schreibt den Gegenverweis mit dem
        // Präfix „Gegenverweis:" und sonst identischem Text — beides ist
        // derselbe Sachverhalt und soll nicht zweimal dastehen. Verglichen wird
        // deshalb ohne Präfix, angezeigt die Fassung ohne.
        const notes = new Map<string, string>();
        const missing = new Set<string>();
        for (const item of members) {
            for (const ref of duplicateLinks(app, item)) {
                // Ein abgeleiteter Gegenverweis trägt dieselbe Begründung wie
                // sein Original und kann kein fehlendes Ziel haben.
                if (ref.incoming) continue;
                if (ref.note) {
                    const text = ref.note.replace(/^\s*Gegenverweis:\s*/i, "");
                    const schluessel = text.toLocaleLowerCase("de");
                    if (!notes.has(schluessel)) notes.set(schluessel, text);
                }
                // Verweis, dessen Ziel nicht (mehr) unter den Verdachten ist:
                // Der Fall bleibt sichtbar, das fehlende Gegenstück benannt.
                if (!ref.item || !byId.has(ref.item)) {
                    const target = resolveRef(app, ref);
                    missing.add(refLocation(target) || "Verweis ohne auffindbares Ziel");
                }
            }
        }

        // Nach Position sortiert, nicht nach Titel: Beim Vergleichen geht der
        // Blick der Reihe nach durch die Aufnahmen, und die Positionsnummern
        // sind die Reihenfolge, in der fotografiert wurde.
        members.sort((a, b) => {
            const pa = app.positions.find((p) => p.id === a.position)?.nummer ?? 0;
            const pb = app.positions.find((p) => p.id === b.position)?.nummer ?? 0;
            return pa - pb || a.title.localeCompare(b.title, "de") || a.id - b.id;
        });
        groups.push({
            key: `group-${members.map((m) => m.id).sort((a, b) => a - b).join("-")}`,
            items: members,
            notes: [...notes.values()],
            missing: [...missing],
            status: groupStatus(members),
        });
    }

    const rank: Record<string, number> = { moeglich: 0, bestaetigt: 1, kein_duplikat: 2 };
    return groups.sort(
        (x, y) =>
            (rank[x.status] ?? 3) - (rank[y.status] ?? 3) ||
            x.items[0].title.localeCompare(y.items[0].title, "de")
    );
}

/**
 * Status einer Gruppe aus den Status ihrer Mitglieder.
 *
 * Ein noch offener Verdacht wiegt schwerer als eine bereits getroffene
 * Entscheidung: Solange irgendwo „möglich" steht, ist der Fall nicht erledigt.
 */
function groupStatus(items: Item[]): DuplicateStatus {
    if (items.some((i) => i.duplikatStatus === "moeglich")) return "moeglich";
    if (items.some((i) => i.duplikatStatus === "bestaetigt")) return "bestaetigt";
    if (items.some((i) => i.duplikatStatus === "kein_duplikat")) return "kein_duplikat";
    return "";
}

/** Offene Duplikat-Fälle (Gruppen, nicht Gegenstände). */
export function openDuplicateCount(app: App): number {
    return duplicateGroups(app).filter((g) => g.status === "moeglich").length;
}

/**
 * Löst einen Verweis in die tatsächlichen Datensätze auf.
 *
 * Die Position wird bevorzugt aus dem verwiesenen Gegenstand gelesen und nur
 * ersatzweise aus dem Verweis selbst: Ein Gegenstand kann verschoben worden
 * sein, seit der Verdacht notiert wurde — dann ist die gespeicherte
 * Positions-ID veraltet, die des Gegenstands aber richtig.
 */
function resolveRef(app: App, ref: DuplicateRef): { item?: Item; position?: Position; room?: Room } {
    const item = ref.item ? app.items.find((i) => i.id === ref.item) : undefined;
    const positionId = item ? item.position : ref.position;
    const position = positionId ? app.positions.find((p) => p.id === positionId) : undefined;
    const room = position ? app.rooms.find((r) => r.id === position.raum) : undefined;
    return { item, position, room };
}

/** Fundstelle in Worten: "Pos. 12 · Schuhregal Flur · Erdgeschoss". */
function refLocation(target: { item?: Item; position?: Position; room?: Room }): string {
    const parts: string[] = [];
    if (target.position) {
        parts.push(target.position.nummer ? `Pos. ${target.position.nummer}` : "Position");
        parts.push(target.position.title);
    }
    if (target.room) parts.push(target.room.title);
    return parts.join(" · ");
}

/**
 * Beschriftung eines Verweises für Suchfeld und Vorschlagsliste — enthält die
 * ID, weil zwei Gegenstände in verschiedenen Räumen exakt gleich heißen
 * können und die Zeile sonst nicht unterscheidbar wäre.
 */
export function duplicateItemLabel(app: App, item: Item): string {
    const position = app.positions.find((p) => p.id === item.position);
    const room = position ? app.rooms.find((r) => r.id === position.raum) : undefined;
    return `#${item.id} ${item.title} — ${refLocation({ item, position, room }) || "ohne Position"}`;
}

/**
 * Der Verweis in der Gegenstandszeile.
 *
 * Zeigt den Bildausschnitt des mutmaßlichen Doppeleintrags direkt an: Genau
 * daran — und nicht an der Bezeichnung — lässt sich in einer Sekunde
 * entscheiden, ob es dieselbe Sache ist. Ohne Ausschnitt bleibt der Verweis
 * trotzdem nachprüfbar, weil Position und Raum danebenstehen.
 */
function duplicateRefCard(app: App, ref: DuplicateLink): string {
    const target = resolveRef(app, ref);
    const ort = refLocation(target) || "Ziel nicht mehr auffindbar";
    const thumb = target.item && target.position ? cropThumb(target.item, target.position.fotos) : "";

    return `
    <div class="hi-dup-ref">
        ${thumb || `<span class="hi-dup-ref__nothumb">${icon("image-slash")}</span>`}
        <div class="hi-dup-ref__body">
            <div class="hi-dup-ref__title">${typeIcon("item")} ${esc(target.item ? target.item.title : "Unbekannter Gegenstand")}</div>
            <div class="hi-dup-ref__where">${esc(ort)}</div>
            ${ref.incoming ? `<div class="hi-dup-ref__from">${icon("arrow-turn-down-left")} Verweis von dort — hier selbst nicht eingetragen</div>` : ""}
            ${ref.note ? `<div class="hi-dup-ref__note">${esc(ref.note)}</div>` : ""}
        </div>
        ${target.position ? `<button type="button" class="hi-btn hi-btn--ghost hi-dup-ref__open"
            data-action="open-duplicate" data-id="${target.position.id}"${target.item ? ` data-item-id="${target.item.id}"` : ""}
            >${icon("arrow-right")} Ansehen</button>` : ""}
    </div>`;
}

/** Duplikat-Hinweis in der Gegenstandszeile; leer, wenn kein Verdacht besteht. */
function duplicateBlock(app: App, item: Item): string {
    const status = effectiveDuplicateStatus(app, item);
    if (!status) return "";
    const links = duplicateLinks(app, item);

    return `
    <div class="hi-dup hi-dup--${status}">
        <span class="hi-dup__badge">${icon(DUPLICATE_ICONS[status])} ${esc(DUPLICATE_LABELS[status])}${
            status === "bestaetigt" ? " – zählt nicht in die Summe" : ""
        }</span>
        ${links.length
            ? links.map((ref) => duplicateRefCard(app, ref)).join("")
            : `<div class="hi-dup__missing">${icon("triangle-exclamation")} Kein Verweis hinterlegt — ohne Gegenstück lässt sich der Verdacht nicht prüfen.</div>`}
    </div>`;
}

/**
 * Eine Verweiszeile im Bearbeiten-Formular.
 *
 * Das Ziel wird über ein Suchfeld gewählt, nicht über eine Auswahlliste: Bei
 * über tausend Gegenständen wäre ein <select> weder ladbar noch bedienbar.
 * Die eigentliche ID steht in verborgenen Feldern, das Suchfeld zeigt nur den
 * lesbaren Namen.
 */
export function duplicateRefRow(o?: { item: number; position: number; note: string; label: string }): string {
    return `
    <div class="hi-dup-row" data-dup-row>
        <input type="hidden" name="dup_item" value="${o?.item || 0}">
        <input type="hidden" name="dup_position" value="${o?.position || 0}">
        <div class="hi-dup-row__pick">
            <input class="hi-input hi-dup-row__search" type="text" data-dup-search autocomplete="off"
                value="${esc(o?.label ?? "")}" placeholder="Gegenstand suchen (Bezeichnung, Position, Raum)…">
            <div class="hi-dup-suggest" data-dup-suggest hidden></div>
        </div>
        <input class="hi-input hi-dup-row__note" type="text" name="dup_note" value="${esc(o?.note ?? "")}"
            placeholder="Warum der Verdacht besteht, z. B. „gleiches Regal, 27 s später fotografiert“">
        <button type="button" class="hi-btn hi-btn--icon hi-btn--secondary" data-action="remove-dup-ref"
            title="Verweis entfernen" aria-label="Verweis entfernen">${icon("xmark")}</button>
    </div>`;
}

/** Trefferliste unter dem Suchfeld einer Verweiszeile. */
export function duplicateSuggestions(app: App, query: string, exclude: number): string {
    const q = query.trim().toLowerCase();
    if (q.length < 2) {
        return `<div class="hi-dup-suggest__hint">Mindestens zwei Zeichen eingeben.</div>`;
    }
    const hits = app.items
        .filter((i) => i.id !== exclude)
        .map((i) => ({ item: i, label: duplicateItemLabel(app, i) }))
        .filter((h) => h.label.toLowerCase().includes(q))
        .slice(0, 8);

    if (hits.length === 0) {
        return `<div class="hi-dup-suggest__hint">Kein Gegenstand gefunden.</div>`;
    }
    return hits
        .map(
            (h) => `
        <button type="button" class="hi-dup-suggest__item" data-action="pick-dup-item"
            data-item-id="${h.item.id}" data-position-id="${h.item.position}" data-label="${esc(h.label)}">
            <span class="hi-dup-suggest__title">${esc(h.item.title)}</span>
            <span class="hi-dup-suggest__where">${esc(duplicateItemLabel(app, h.item).split(" — ")[1] || "")}</span>
        </button>`
        )
        .join("");
}

/**
 * Gegenverweise im Bearbeiten-Formular — nur zum Lesen.
 *
 * Sie stehen beim anderen Gegenstand und werden dort geändert; hier nicht
 * genannt, sähe es so aus, als gäbe es keinen Verdacht.
 */
function incomingDuplicateNote(app: App, item: Item): string {
    const eingehend = duplicateLinks(app, item).filter((l) => l.incoming);
    if (!eingehend.length) return "";
    const zeilen = eingehend.map((l) => {
        const quelle = app.items.find((i) => i.id === l.item);
        return `<li>${esc(quelle ? duplicateItemLabel(app, quelle) : `#${l.item}`)}</li>`;
    });
    return `
        <div class="hi-dup-field__incoming">
            <span>Außerdem verweist von dort hierher (dort zu ändern):</span>
            <ul>${zeilen.join("")}</ul>
        </div>`;
}

/** Formularblock „Duplikat-Verdacht" im Bearbeiten-Formular. */
function duplicateFields(app: App, item: Item): string {
    const statuses: DuplicateStatus[] = ["", "moeglich", "bestaetigt", "kein_duplikat"];
    const rows = item.duplikatRefs.length
        ? item.duplikatRefs
              .map((ref) => {
                  const target = resolveRef(app, ref);
                  const label = target.item ? duplicateItemLabel(app, target.item) : refLocation(target);
                  return duplicateRefRow({ item: ref.item, position: ref.position, note: ref.note, label });
              })
              .join("")
        : duplicateRefRow();

    return `
    <div class="hi-field hi-dup-field" data-dup-field data-item-id="${item.id}">
        <span class="hi-field__label">Duplikat-Verdacht</span>
        <p class="hi-field__hint">Steht dieselbe Sache womöglich schon einmal in der Aufstellung — etwa weil sie auf zwei Fotos mit verschiedenen Positionsnummern liegt? Der Verweis muss so genau sein, dass sich der Verdacht nachprüfen lässt. Ein bestätigtes Duplikat bleibt erhalten, zählt aber nicht in die Summe.</p>
        <select class="hi-input hi-dup-field__status" name="duplikat_status">
            ${statuses.map((s) => `<option value="${s}"${s === item.duplikatStatus ? " selected" : ""}>${esc(duplicateLabel(s))}</option>`).join("")}
        </select>
        <div class="hi-dup-rows" data-dup-rows>${rows}</div>
        <button type="button" class="hi-btn hi-btn--secondary hi-dup-field__add" data-action="add-dup-ref">${icon("plus")} Verweis</button>
        ${incomingDuplicateNote(app, item)}
    </div>`;
}

/**
 * Protokoll des automatischen Foto-Imports (ACF-Feld `import_log`).
 *
 * Bewusst zugeklappt und optisch abgesetzt: Der Text erklärt, wie die KI zu
 * Anzahl, Wert und Zuordnung gekommen ist, gehört aber weder in den Export
 * noch in die alltägliche Ansicht — er wird beim Nachprüfen aufgeklappt.
 * Zeilenumbrüche bleiben erhalten, das Protokoll ist zeilenweise aufgebaut.
 */
function importLog(text: string, label = "Import-Protokoll (KI)"): string {
    if (!text.trim()) return "";
    return `
    <details class="hi-import-log">
        <summary class="hi-import-log__summary">${icon("robot")} ${esc(label)}</summary>
        <div class="hi-import-log__body">${esc(text)}</div>
    </details>`;
}

/**
 * Schneidet den markierten Bereich per Canvas aus dem Foto heraus und liefert
 * ihn als Data-URL.
 *
 * Gegenüber einer Lösung mit `background-position`/`-size` hat das zwei
 * Vorteile: Das Ergebnis ist ein normales Bild, kann also nicht verzerren, und
 * es lässt sich überall gleich behandeln (Vorschau wie Lightbox, inkl. Zoom).
 *
 * Ergebnisse werden zwischengespeichert, weil die App bei jeder Änderung
 * komplett neu rendert und derselbe Ausschnitt sonst wieder und wieder
 * berechnet würde.
 */
const cropCache = new Map<string, string>();

export function cropCacheKey(url: string, crop: ItemCrop): string {
    return `${url}|${crop.x},${crop.y},${crop.w},${crop.h}`;
}

/**
 * Obergrenze für die Kantenlänge eines Zuschnitts.
 *
 * Der Ausschnitt wird in voller Originalauflösung geschnitten — mobile Safari
 * liefert bei sehr großen Canvas-Flächen aber eine leere statt einer
 * verkleinerten Grafik. 4096 px liegt über allem, was ein Handyfoto hergibt,
 * und greift damit erst bei künftigen, deutlich größeren Sensoren.
 */
const MAX_CROP_EDGE = 4096;

/**
 * Kurzlebiger Vorrat geladener Originalfotos.
 *
 * Alle Gegenstände einer Position beziehen sich meist auf dasselbe Foto —
 * ohne diesen Vorrat würde das Original je Ausschnitt erneut geladen und
 * dekodiert, und ein Original ist mehrere Megabyte groß. Der Aufrufer legt
 * die Map für einen Durchgang an und lässt sie danach fallen; dauerhaft
 * gehalten würde sie über eine lange Sitzung dutzende Vollbilder im Speicher
 * festhalten.
 */
export type ImagePool = Map<string, Promise<HTMLImageElement | null>>;

function loadImage(url: string, pool?: ImagePool): Promise<HTMLImageElement | null> {
    const cached = pool?.get(url);
    if (cached) return cached;

    const loading = new Promise<HTMLImageElement | null>((resolve) => {
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.addEventListener("load", () => resolve(image));
        image.addEventListener("error", () => resolve(null));
        image.src = url;
    });
    pool?.set(url, loading);
    return loading;
}

export async function cropToDataUrl(url: string, crop: ItemCrop, pool?: ImagePool): Promise<string | null> {
    const key = cropCacheKey(url, crop);
    const cached = cropCache.get(key);
    if (cached) return cached;

    const source = await loadImage(url, pool);
    if (!source) return null;

    const sw = Math.max(1, Math.round(crop.w * source.naturalWidth));
    const sh = Math.max(1, Math.round(crop.h * source.naturalHeight));
    const scale = Math.min(1, MAX_CROP_EDGE / Math.max(sw, sh));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(sw * scale));
    canvas.height = Math.max(1, Math.round(sh * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    ctx.drawImage(
        source,
        Math.round(crop.x * source.naturalWidth),
        Math.round(crop.y * source.naturalHeight),
        sw, sh, 0, 0, canvas.width, canvas.height
    );
    try {
        const data = canvas.toDataURL("image/jpeg", 0.92);
        cropCache.set(key, data);
        return data;
    } catch {
        // "Getainted" Canvas (fremde Herkunft) — Aufrufer zeigt dann
        // das ungeschnittene Foto.
        return null;
    }
}

/** Findet das Foto, auf das sich der Ausschnitt eines Gegenstands bezieht. */
function cropSource(crop: ItemCrop | null, fotos: GalleryImage[]): GalleryImage | undefined {
    if (!crop) return undefined;
    return fotos.find((f) => f.ID === crop.media);
}

/**
 * Vorschau des Bildausschnitts in der Gegenstandszeile. Zeigt nichts an,
 * wenn kein Ausschnitt gesetzt ist oder das referenzierte Foto nicht mehr
 * an der Position hängt.
 */
function cropThumb(item: Item, positionFotos: GalleryImage[]): string {
    const c = item.bildausschnitt;
    const source = cropSource(c, positionFotos);
    if (!source || !c) return "";
    return cropButton(item, source, c);
}

/**
 * Ausschnitt-Vorschau für Trefferlisten.
 *
 * Schneidet aus einer **kleineren Zwischengröße** statt aus dem Original: Eine
 * Suche listet Dutzende Treffer auf einmal auf, und jedes Original ist ein
 * 24-Megapixel-Bild. In der Detailansicht ist das Original richtig (dort zählt
 * die Schärfe eines einzelnen Ausschnitts, siehe cropButton), in einer
 * Ergebnisliste wäre es nur Wartezeit.
 */
function cropPreview(
    item: Item,
    positionFotos: GalleryImage[],
    extraClass = "",
    interactive = true,
    lazy = false
): string {
    const c = item.bildausschnitt;
    const source = cropSource(c, positionFotos);
    if (!source || !c) return "";
    const klein = source.sizes?.large || source.sizes?.medium || source.url;
    return cropButton(item, source, c, extraClass, klein, interactive, lazy);
}

/**
 * Anklickbare Ausschnitt-Vorschau. `extraClass` steuert nur die Größe — beide
 * Varianten werden nach dem Rendern von hydrateCropThumbs gefüllt.
 * `cropSrc` überschreibt die Bildquelle, aus der zugeschnitten wird.
 *
 * `interactive: false` liefert dieselbe Vorschau als <span> ohne eigene
 * Aktion. Nötig, wo die Vorschau in einem Knopf steckt (Suchvorschläge): Ein
 * <button> im <button> ist nicht erlaubt — der Parser schließt den äußeren
 * Knopf beim inneren, und der Rest der Zeile landet außerhalb.
 *
 * `lazy: true` überlässt das Füllen dem IntersectionObserver, statt es direkt
 * nach dem Rendern zu erledigen (siehe observeLazyCrops).
 */
function cropButton(
    item: Item,
    source: GalleryImage,
    c: ItemCrop,
    extraClass = "",
    cropSrc = "",
    interactive = true,
    lazy = false
): string {
    // Das Bild wird erst nach dem Rendern per Canvas zugeschnitten (siehe
    // hydrateCropThumbs) — hier steht nur die leere Hülle mit den Daten dafür.
    // Beim Klick wird der Ausschnitt großgezogen, nicht das ganze Foto.
    //
    // Zugeschnitten wird aus dem ORIGINAL, nicht aus einer Zwischengröße: Ein
    // Ausschnitt ist immer nur ein Bruchteil des Fotos, ein Fünftel eines
    // 1024px-"large" sind gerade 200px. In der Lightbox war das sichtbar
    // matschig, obwohl das Original ein Vielfaches an Auflösung hergibt. Der
    // Ausschnitt bekommt damit genau die Auflösung, die er im Original hat.
    // Kosten: Das Original wird einmal geladen — alle Ausschnitte desselben
    // Fotos teilen sich diesen Ladevorgang über den Browser-Cache, und die
    // fertigen Zuschnitte liegen danach im cropCache.
    //
    // `cropSrc` (siehe cropPreview) betrifft ausdrücklich NUR die Miniatur:
    // In Trefferlisten zählt die Ladezeit vieler Vorschauen mehr als die
    // Schärfe einer einzelnen. Die Lightbox liest weiter `data-crop-src` und
    // zieht den Ausschnitt damit unverändert aus dem Original groß.
    const tag = interactive ? "button" : "span";
    return `<${tag}${interactive ? ' type="button"' : ""} class="hi-crop-thumb${extraClass ? " " + extraClass : ""}"
        ${interactive ? `data-action="view-crop" data-lb-kind="item" data-lb-id="${item.id}"` : ""}
        ${lazy ? "data-crop-lazy" : ""}
        data-full="${esc(source.url)}" data-alt="${esc(item.title)}"
        data-crop-src="${esc(source.url)}"${cropSrc ? ` data-crop-thumb="${esc(cropSrc)}"` : ""}
        data-x="${c.x}" data-y="${c.y}" data-w="${c.w}" data-h="${c.h}"
        title="${esc(item.title)}${interactive ? " – Ausschnitt vergrößern" : ""}"><img alt=""></${tag}>`;
}

/**
 * Bildausschnitt im Prüf-Assistenten.
 *
 * Beim Prüfen liegt nur das Foto der ganzen Position vor — auf dem oft ein
 * Dutzend Teile nebeneinander liegen. Ohne den markierten Ausschnitt ist
 * daher nicht erkennbar, welcher Gegenstand gerade zu bestätigen ist. Die
 * Vorschau steht deshalb groß über dem Formular und nicht als Miniatur wie
 * in der Positionsliste.
 */
function reviewCrop(item: Item, positionFotos: GalleryImage[]): string {
    const c = item.bildausschnitt;
    const source = cropSource(c, positionFotos);

    if (source && c) {
        return `
        <div class="hi-review-crop">
            ${cropButton(item, source, c, "hi-crop-thumb--review")}
            <div class="hi-review-crop__side">
                <div class="hi-review-crop__label">Markierter Ausschnitt</div>
                <p class="hi-review-crop__hint">Zum Vergrößern anklicken.</p>
                <button type="button" class="hi-btn hi-btn--ghost" data-action="edit-crop" data-id="${item.id}">${icon("crop-simple")} Ausschnitt ändern</button>
            </div>
        </div>`;
    }

    if (!positionFotos.length) {
        return `<div class="hi-review-crop hi-review-crop--empty">
            <div class="hi-review-crop__side">
                <div class="hi-review-crop__label">Kein Ausschnitt möglich</div>
                <p class="hi-review-crop__hint">Die Position hat noch kein Foto.</p>
            </div>
        </div>`;
    }

    // Auch der Fall "Ausschnitt fehlt" gehört sichtbar hierher: Beim Prüfen
    // ist der Moment, in dem er sich am schnellsten nachtragen lässt.
    return `
    <div class="hi-review-crop hi-review-crop--empty">
        <div class="hi-review-crop__side">
            <div class="hi-review-crop__label">Kein Ausschnitt markiert</div>
            <p class="hi-review-crop__hint">Ohne Markierung bleibt offen, welcher Gegenstand auf dem Foto gemeint ist.</p>
            <button type="button" class="hi-btn hi-btn--secondary" data-action="edit-crop" data-id="${item.id}">${icon("crop-simple")} Ausschnitt markieren</button>
        </div>
    </div>`;
}

/* ------------------------------------------------------------- Lightbox */

/**
 * Kerndaten zum gezeigten Bild, für die einblendbare Tafel in der Lightbox.
 *
 * Bewusst knapp gehalten: Beim Betrachten eines Fotos geht es um die
 * Einordnung ("welcher Gegenstand, welcher Wert"), nicht um den vollen
 * Datensatz — der steht in der jeweiligen Ansicht.
 */
export function renderLightboxInfo(app: App, kind: string | null, id: number): string {
    if (kind === "item") {
        const item = app.items.find((i) => i.id === id);
        if (!item) return "";
        const pos = positionOf(app, item.position);
        const alter = item.alter;
        return `
            <div class="hi-lightbox__info-title">${esc(item.title)}</div>
            <div class="hi-lightbox__info-sub">${pos ? esc(pos.title) : "—"}${pos ? ` · ${esc(app.roomName(pos.raum))}` : ""}</div>
            <div class="hi-lightbox__info-grid">
                ${kv("Anzahl", String(item.anzahl))}
                ${kv("Wert je Stück", fmt(item.wert))}
                ${kv("Gesamtwert", fmt(item.wert * item.anzahl))}
                ${item.versandkosten ? kv("Versandkosten", `${fmt(item.versandkosten)} je Bestellung`) : ""}
                ${kv("Alter", esc(alter))}
                ${kv("Quelle", esc(item.quelle))}
                ${kv("Status", item.geprueft ? "Geprüft" : "Offen")}
            </div>
            ${item.sonstiges ? `<div class="hi-lightbox__info-note">${esc(item.sonstiges)}</div>` : ""}`;
    }

    if (kind === "position") {
        const pos = app.positions.find((p) => p.id === id);
        if (!pos) return "";
        const children = itemsOf(app, pos.id);
        return `
            <div class="hi-lightbox__info-title">${esc(pos.title)}</div>
            <div class="hi-lightbox__info-sub">Position ${pos.nummer || "—"} · ${esc(app.roomName(pos.raum))}</div>
            <div class="hi-lightbox__info-grid">
                ${kv("Gegenstände", String(children.length))}
                ${kv("Stück", String(stueckzahl(children)))}
                ${kv("Gesamtwert", fmt(positionValue(app, pos.id)))}
                ${kv("Geprüft", ratio(children.filter((i) => i.geprueft).length, children.length))}
            </div>
            ${pos.beschreibung ? `<div class="hi-lightbox__info-note">${esc(pos.beschreibung)}</div>` : ""}`;
    }

    if (kind === "room") {
        const room = app.rooms.find((r) => r.id === id);
        if (!room) return "";
        const positions = app.positions.filter((p) => p.raum === room.id);
        return `
            <div class="hi-lightbox__info-title">${esc(room.title)}</div>
            <div class="hi-lightbox__info-sub">Raum</div>
            <div class="hi-lightbox__info-grid">
                ${kv("Positionen", String(positions.length))}
                ${kv("Gesamtwert", fmt(roomStats(app, room.id).wert))}
            </div>
            ${room.beschreibung ? `<div class="hi-lightbox__info-note">${esc(room.beschreibung)}</div>` : ""}`;
    }

    // Noch nicht gespeicherte Position bzw. Gegenstand — dazu gibt es keine
    // Daten, die über das Foto selbst hinausgehen.
    return "";
}

/* ------------------------------------------------------- Globale Suche */

/**
 * Ein durchsuchbares Feld eines Datensatzes.
 *
 * `weight` entscheidet die Reihenfolge der Treffer: Wer "Bohrmaschine" sucht,
 * meint fast immer die Bezeichnung und nicht ein Import-Protokoll, in dem das
 * Wort beiläufig vorkommt.
 */
interface SearchField {
    label: string;
    text: string;
    weight: number;
    /**
     * Feld, das ohnehin in der Kopfzeile des Treffers steht (Bezeichnung,
     * Position, Raum). Es zählt für die Bewertung mit, wird aber nicht noch
     * einmal als Fundstelle ausgegeben — sonst wiederholt jeder Treffer
     * zweimal dasselbe.
     */
    header?: boolean;
}

export interface SearchEntry {
    kind: "position" | "item" | "room";
    id: number;
    title: string;
    /** Kurze Herkunftsangabe, z. B. "Pos. 12 · Flur". */
    where: string;
    /** Wohin der Klick führt. */
    view: "position" | "room";
    targetId: number;
    /** Nur bei Gegenständen: Sprungmarke innerhalb der Position. */
    itemId?: number;
    /** Zeile mit den wichtigsten Zahlen, damit ein Treffer ohne Öffnen einschätzbar ist. */
    meta: string;
    /**
     * Bild erst auf Abruf: Der Index haelt alle Raeume, Positionen und
     * Gegenstaende, angezeigt wird davon eine Handvoll. Vorab erzeugtes
     * Markup fuer zweitausend Vorschauen waere reiner Ballast.
     */
    thumb: (interactive: boolean) => string;
    fields: SearchField[];
}

export interface SearchResult {
    entry: SearchEntry;
    score: number;
    /** Fundstellen im Kontext, bereits mit Markierung und HTML-escaped. */
    matches: { label: string; html: string }[];
}

/**
 * Der Index wird zwischengespeichert, weil er bei jedem Tastendruck gebraucht
 * wird: 1900 Gegenstände × rund zehn Felder neu zusammenzusetzen wäre pro
 * Anschlag spürbar. `dataStamp` zählt bei jedem reload() hoch — damit wird der
 * Vorrat genau dann ungültig, wenn sich die Daten geändert haben.
 */
let searchIndexCache: { stamp: number; entries: SearchEntry[] } | null = null;

function money(n: number): string {
    return fmt(n);
}

export function searchIndex(app: App): SearchEntry[] {
    if (searchIndexCache && searchIndexCache.stamp === app.dataStamp) {
        return searchIndexCache.entries;
    }

    const entries: SearchEntry[] = [];
    const positionsById = new Map(app.positions.map((p) => [p.id, p]));
    const roomsById = new Map(app.rooms.map((r) => [r.id, r]));

    for (const room of app.rooms) {
        const stats = roomStats(app, room.id);
        entries.push({
            kind: "room",
            id: room.id,
            title: room.title,
            where: `${stats.positions.length} Positionen`,
            view: "room",
            targetId: room.id,
            meta: `${stats.positions.length} Positionen · ${stats.items.length} Gegenstände · ${money(stats.wert)}`,
            thumb: (interactive) => (room.fotos[0] ? photoThumb(room.fotos[0], room.title, interactive) : ""),
            fields: [
                { label: "Raum", text: room.title, weight: 40, header: true },
                { label: "Beschreibung", text: room.beschreibung, weight: 6 },
            ],
        });
    }

    for (const position of app.positions) {
        const room = roomsById.get(position.raum);
        const children = itemsOf(app, position.id);
        entries.push({
            kind: "position",
            id: position.id,
            title: position.title,
            where: [position.nummer ? `Pos. ${position.nummer}` : "ohne Nummer", room?.title].filter(Boolean).join(" · "),
            view: "position",
            targetId: position.id,
            meta: `${children.length} Gegenstände · ${stueckzahl(children)} Stück · ${money(positionValue(app, position.id))}`,
            thumb: (interactive) => (position.fotos[0] ? photoThumb(position.fotos[0], position.title, interactive) : ""),
            fields: [
                { label: "Position", text: position.title, weight: 40, header: true },
                { label: "Nummer", text: position.nummer ? String(position.nummer) : "", weight: 20, header: true },
                { label: "Raum", text: room?.title ?? "", weight: 12, header: true },
                { label: "Beschreibung", text: position.beschreibung, weight: 6 },
                { label: "Import-Protokoll", text: position.importLog, weight: 1 },
            ],
        });
    }

    for (const item of app.items) {
        const position = positionsById.get(item.position);
        const room = position ? roomsById.get(position.raum) : undefined;
        entries.push({
            kind: "item",
            id: item.id,
            title: item.title,
            where: [position?.nummer ? `Pos. ${position.nummer}` : null, position?.title, room?.title]
                .filter(Boolean)
                .join(" · "),
            view: "position",
            targetId: item.position,
            itemId: item.id,
            meta: [
                `${item.anzahl}×`,
                `${money(item.wert)} je Stück`,
                `${money(item.wert * item.anzahl)} gesamt`,
                item.quelle || "ohne Quelle",
                item.geprueft ? "geprüft" : "offen",
                effectiveDuplicateStatus(app, item) ? duplicateLabel(effectiveDuplicateStatus(app, item)) : "",
            ]
                .filter(Boolean)
                .join(" · "),
            thumb: (interactive) => (position ? cropPreview(item, position.fotos, "hi-crop-thumb--search", interactive) : ""),
            fields: [
                { label: "Bezeichnung", text: item.title, weight: 40, header: true },
                { label: "Position", text: position?.title ?? "", weight: 16, header: true },
                { label: "Raum", text: room?.title ?? "", weight: 12, header: true },
                { label: "Sonstiges", text: item.sonstiges, weight: 6 },
                { label: "Wertbegründung", text: item.wertBegruendung, weight: 5 },
                { label: "Alter", text: item.alter, weight: 3 },
                { label: "Quelle", text: item.quelle, weight: 2 },
                { label: "Wert", text: decimalValue(item.wert), weight: 2 },
                { label: "Datum", text: item.datum ? dateLabel(item.datum) : "", weight: 1 },
                { label: "Vergleichsangebote", text: item.shopLinks.map((l) => l.url).join(" "), weight: 1 },
                { label: "Duplikat-Verdacht", text: item.duplikatRefs.map((r) => r.note).join(" "), weight: 2 },
                { label: "Import-Protokoll", text: item.importLog, weight: 1 },
            ],
        });
    }

    searchIndexCache = { stamp: app.dataStamp, entries };
    return entries;
}

/**
 * Kleine Bildvorschau eines Fotos — bewusst die Thumbnail-Größe, nicht das
 * Original. `interactive: false` lässt die Lightbox-Aktion weg, damit das Bild
 * in einer anklickbaren Zeile nicht mit ihr um den Klick streitet.
 */
function photoThumb(foto: GalleryImage, alt: string, interactive = true): string {
    const src = foto.sizes?.thumbnail || foto.sizes?.medium || foto.url;
    const action = interactive ? ` data-action="view-photo" data-full="${esc(foto.url)}" data-alt="${esc(alt)}"` : "";
    return `<img class="hi-hit__photo" src="${esc(src)}" alt="${esc(alt)}" loading="lazy"${action}>`;
}

/** Zerlegt die Eingabe in Suchbegriffe; mehrere Wörter müssen alle vorkommen. */
function searchTerms(query: string): string[] {
    return query
        .toLowerCase()
        .split(/\s+/)
        .map((t) => t.trim())
        .filter((t) => t.length > 0);
}

/**
 * Schneidet die Fundstelle mit ihrem Umfeld heraus und markiert die Begriffe.
 *
 * Ein Treffer im Import-Protokoll ist ohne Umgebung wertlos — "27" allein sagt
 * nichts, "27 s später fotografiert" schon. Escaped wird erst nach dem Suchen
 * und vor dem Einsetzen der Markierung, damit die Positionen stimmen.
 */
function matchContext(text: string, terms: string[], radius = 60): string | null {
    const haystack = text.toLowerCase();
    let first = -1;
    for (const term of terms) {
        const at = haystack.indexOf(term);
        if (at >= 0 && (first < 0 || at < first)) first = at;
    }
    if (first < 0) return null;

    const start = Math.max(0, first - radius);
    const end = Math.min(text.length, first + radius * 2);
    // An Wortgrenzen abschneiden, damit kein halbes Wort am Rand steht.
    const cut = text.slice(start, end);
    const prefix = start > 0 ? "… " : "";
    const suffix = end < text.length ? " …" : "";

    return prefix + highlightTerms(cut, terms) + suffix;
}

/** Escaped den Text und hebt alle Suchbegriffe darin hervor. */
function highlightTerms(text: string, terms: string[]): string {
    const escaped = esc(text);
    if (terms.length === 0) return escaped;
    // Längste Begriffe zuerst, damit "schrank" nicht die Markierung von
    // "kleiderschrank" zerlegt.
    const sorted = [...terms].sort((a, b) => b.length - a.length);
    const pattern = sorted.map((t) => esc(t).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
    return escaped.replace(new RegExp(`(${pattern})`, "gi"), "<mark>$1</mark>");
}

/**
 * Durchsucht den Index.
 *
 * Bewertet wird nach Feldgewicht; ein Treffer am Wortanfang zählt doppelt,
 * weil "Bohr" eher "Bohrmaschine" meint als "Sonderbohrer". Einträge, in denen
 * nicht jeder Suchbegriff vorkommt, fallen heraus — sonst liefert eine
 * zweiwortige Suche mehr Treffer als eine einwortige.
 */
export function runSearch(app: App, query: string, limit = 0): SearchResult[] {
    const terms = searchTerms(query);
    if (terms.length === 0) return [];

    const results: SearchResult[] = [];
    for (const entry of searchIndex(app)) {
        let score = 0;
        const seen = new Set<string>();
        const matches: { label: string; html: string }[] = [];

        for (const term of terms) {
            let best = 0;
            for (const field of entry.fields) {
                if (!field.text) continue;
                const at = field.text.toLowerCase().indexOf(term);
                if (at < 0) continue;
                const wordStart = at === 0 || /[\s(\/,.-]/.test(field.text[at - 1]);
                best = Math.max(best, field.weight * (wordStart ? 2 : 1));
            }
            // Ein fehlender Begriff schließt den Eintrag aus.
            if (best === 0) {
                score = 0;
                break;
            }
            score += best;
        }
        // Eine ID-Eingabe ("1953" oder "#1953") trifft den Datensatz direkt
        // und steht vor allen Texttreffern.
        const idHit = matchesId(query, entry.id);
        if (idHit) {
            score += 1000;
            matches.push({ label: "ID", html: `#${entry.id}` });
        }
        if (score === 0) continue;

        // Wer "Wohnzimmer" eingibt, meint zuerst den Raum und nicht die
        // dreißig Positionen, die "(Wohnzimmer)" im Titel tragen. Ein Titel,
        // der die Eingabe genau trifft oder mit ihr beginnt, hat deshalb
        // Vorrang vor einem Treffer irgendwo im selben Feld.
        const titel = entry.title.toLowerCase();
        const eingabe = terms.join(" ");
        if (titel === eingabe) score += 100;
        else if (titel.startsWith(eingabe)) score += 40;

        for (const field of entry.fields) {
            if (!field.text || field.header || seen.has(field.label)) continue;
            const html = matchContext(field.text, terms);
            if (!html) continue;
            seen.add(field.label);
            matches.push({ label: field.label, html });
        }

        results.push({ entry, score, matches });
    }

    results.sort((a, b) => b.score - a.score || a.entry.title.localeCompare(b.entry.title, "de"));
    return limit > 0 ? results.slice(0, limit) : results;
}

/** Attribute, mit denen ein Treffer angesteuert wird — in Vorschlag und Ergebnisliste gleich. */
function hitTarget(entry: SearchEntry): string {
    return `data-action="open-search-hit" data-view="${entry.view}" data-id="${entry.targetId}"${
        entry.itemId ? ` data-item-id="${entry.itemId}"` : ""
    }`;
}

/**
 * Vorschlagsliste unter dem Suchfeld.
 *
 * Zeigt Bild, Bezeichnung mit markiertem Treffer, Fundort und die erste
 * Fundstelle im Kontext — mehr passt nicht in eine Zeile, und für alles
 * Weitere gibt es mit Enter die Ergebnisseite.
 */
export function renderSearchSuggestions(app: App, query: string): string {
    if (query.trim().length < 2) {
        return `<div class="hi-suggest__hint">Mindestens zwei Zeichen eingeben. Gesucht wird in Räumen, Positionen und Gegenständen — inklusive Sonstiges und Import-Protokoll.</div>`;
    }
    const terms = searchTerms(query);
    const all = runSearch(app, query);
    if (all.length === 0) {
        return `<div class="hi-suggest__hint">Keine Treffer für „${esc(query)}".</div>`;
    }
    const shown = all.slice(0, 8);

    return `
        ${shown
            .map((hit) => {
                const context = hit.matches[0];
                return `
        <button type="button" class="hi-suggest__item" ${hitTarget(hit.entry)}>
            <span class="hi-suggest__thumb">${hit.entry.thumb(false) || typeIcon(hit.entry.kind)}</span>
            <span class="hi-suggest__body">
                <span class="hi-suggest__title">${highlightTerms(hit.entry.title, terms)}</span>
                <span class="hi-suggest__where">
                    ${typeChip(hit.entry.kind)}${hit.entry.where ? `<span class="hi-suggest__place">${esc(hit.entry.where)}</span>` : ""}
                </span>
                ${context ? `<span class="hi-suggest__context"><b>${esc(context.label)}:</b> ${context.html}</span>` : ""}
            </span>
        </button>`;
            })
            .join("")}
        <button type="button" class="hi-suggest__all" data-action="run-search">
            ${icon("magnifying-glass")} Alle ${all.length} Treffer anzeigen
        </button>`;
}

/** Suchfeld im Kopf der Seitenleiste. */
function searchBox(app: App): string {
    return `
    <div class="hi-search" data-search-box>
        <label class="hi-search__field">
            ${icon("magnifying-glass")}
            <input class="hi-search__input" type="search" data-global-search autocomplete="off"
                value="${esc(app.searchQuery)}" placeholder="Suchen …"
                aria-label="Räume, Positionen und Gegenstände durchsuchen">
        </label>
        <div class="hi-suggest" data-search-suggest hidden></div>
    </div>`;
}

/** Ergebnisseite: alle Treffer mit sämtlichen Fundstellen. */
export function renderSearch(app: App): string {
    const query = app.searchQuery.trim();
    if (query.length < 2) {
        return `<div class="hi-empty">Suchbegriff im Feld oben links eingeben. Gesucht wird in allen Feldern — auch in Sonstiges und im Import-Protokoll.</div>`;
    }
    const terms = searchTerms(query);
    const results = runSearch(app, query);
    if (results.length === 0) {
        return `<div class="hi-empty">Keine Treffer für „${esc(query)}".</div>`;
    }

    const counts: Record<SearchEntry["kind"], number> = { room: 0, position: 0, item: 0 };
    for (const hit of results) counts[hit.entry.kind]++;

    // Ein sehr allgemeiner Begriff trifft fast den ganzen Bestand. Alle
    // Treffer samt Bildern auszugeben würde die Seite lahmlegen — und niemand
    // liest zweitausend Zeilen. Gekappt wird sichtbar, nicht stillschweigend.
    const LIMIT = 200;
    const shown = results.slice(0, LIMIT);
    const gekappt = results.length - shown.length;

    const groups: SearchEntry["kind"][] = ["item", "position", "room"];

    return `
    <div class="hi-stack">
        <div class="hi-list-summary">
            <span>${results.length} Treffer für „${esc(query)}"</span>
            <span>${counts.item} Gegenstände · ${counts.position} Positionen · ${counts.room} Räume</span>
        </div>
        ${gekappt > 0 ? `<div class="hi-hint">${icon("circle-info")} Angezeigt werden die ${LIMIT} bestbewerteten Treffer; ${gekappt} weitere sind ausgeblendet. Suchbegriff eingrenzen oder ein zweites Wort ergänzen.</div>` : ""}
        ${groups
            .filter((kind) => shown.some((hit) => hit.entry.kind === kind))
            .map(
                (kind) => `
        <div class="hi-card hi-card--flush">
            <div class="hi-card__title hi-hit-group">${typeIcon(kind)} ${TYPE_LABELS[kind].plural} <span class="hi-badge">${counts[kind]}</span></div>
            ${shown
                .filter((hit) => hit.entry.kind === kind)
                .map(
                    (hit) => `
            <div class="hi-hit">
                <div class="hi-hit__thumb">${hit.entry.thumb(true) || typeIcon(kind)}</div>
                <div class="hi-hit__body">
                    <button type="button" class="hi-hit__title" ${hitTarget(hit.entry)}>${highlightTerms(hit.entry.title, terms)}</button>
                    <div class="hi-hit__where">${esc(hit.entry.where)}</div>
                    <div class="hi-hit__meta">${esc(hit.entry.meta)}</div>
                    ${hit.matches
                        .map((m) => `<div class="hi-hit__match"><b>${esc(m.label)}:</b> ${m.html}</div>`)
                        .join("")}
                </div>
                <button type="button" class="hi-btn hi-btn--ghost hi-hit__open" ${hitTarget(hit.entry)}>${icon("arrow-right")} Öffnen</button>
            </div>`
                )
                .join("")}
        </div>`
            )
            .join("")}
    </div>`;
}

/* ---------------------------------------------------------------- Shell */

export function renderShell(app: App, content: string): string {
    const offenCount = app.items.filter((i) => !i.geprueft).length;
    // Die drei Datenansichten tragen die Symbole ihres Datentyps, die
    // Werkzeuge darunter eigene — so ist in der Leiste sofort zu sehen, was
    // Daten und was Vorgang ist.
    const nav: { key: string; label: string; icon: string; badge?: number }[] = [
        { key: "dashboard", label: "Übersicht", icon: "gauge" },
        { key: "rooms", label: "Räume", icon: TYPE_ICONS.room, badge: app.rooms.length },
        { key: "positions", label: "Positionen", icon: TYPE_ICONS.position, badge: app.positions.length },
        { key: "items", label: "Gegenstände", icon: TYPE_ICONS.item, badge: app.items.length },
    ];
    // Prüfen/Export sind eher Werkzeuge als Datenansichten — optisch von der
    // Navigation durch die Positionen/Räume abgesetzt.
    const dupCount = duplicateItems(app).length;
    const navSecondary: { key: string; label: string; icon: string; badge?: number }[] = [
        { key: "review", label: "Prüfen", icon: "list-check", badge: offenCount },
        // Der Eintrag erscheint nur, wenn es etwas zu prüfen gibt — ein
        // dauerhaft leerer Menüpunkt wäre nur Rauschen.
        ...(dupCount
            ? [{ key: "duplicates", label: "Duplikate", icon: "clone", badge: openDuplicateCount(app) }]
            : []),
        // Wie bei den Duplikaten: nur zeigen, wenn es Hinweise gibt. Der Zähler
        // meint die offenen — erledigte bleiben als Nachweis erhalten, sollen
        // aber nicht dauerhaft als Aufgabe erscheinen.
        ...(app.notes.length
            ? [{ key: "notes", label: "Import-Hinweise", icon: "triangle-exclamation", badge: openNoteCount(app) }]
            : []),
        { key: "export", label: "Export", icon: "file-arrow-down" },
        { key: "compare", label: "Vergleich", icon: "code-compare" },
        { key: "mcp", label: "KI-Zugriff", icon: "plug" },
    ];
    const activeNav = ["position", "form"].includes(app.view) ? "positions" : app.view === "room" ? "rooms" : app.view;

    const titleMap: Record<string, { title: string; subtitle: string }> = {
        dashboard: { title: "Übersicht", subtitle: "Überblick über Ihr Hausinventar" },
        positions: { title: "Positionen", subtitle: "Erfasste Positionen des Hausrats" },
        items: { title: "Gegenstände", subtitle: "Alle bewerteten Einzelstücke" },
        review: { title: "Prüf-Assistent", subtitle: "Gegenstände nacheinander prüfen" },
        rooms: { title: "Räume", subtitle: "Räume des Haushalts mit Fotos" },
        room: { title: "Raum", subtitle: "Details & Fotos" },
        position: { title: "Position", subtitle: "Gegenstände & Prüfung" },
        form: { title: "Neue Position", subtitle: "Position anlegen" },
        export: { title: "Export", subtitle: "Aufstellung erzeugen" },
        duplicates: { title: "Duplikate", subtitle: "Mögliche Doppeleinträge prüfen" },
        notes: { title: "Import-Hinweise", subtitle: "Was die Auswertung der Fotos offen gelassen hat" },
        search: { title: "Suche", subtitle: app.searchQuery.trim() ? `Treffer für „${app.searchQuery.trim()}"` : "Alle Felder durchsuchen" },
        mcp: { title: "KI-Zugriff (MCP)", subtitle: "Verbindungen für Claude, ChatGPT & Co. verwalten" },
        compare: { title: "Vergleich", subtitle: "Unterschiede zu einer anderen Instanz dieser Aufstellung" },
    };
    const meta = titleMap[app.view] || titleMap.dashboard;
    const showPositionActions = app.view === "dashboard" || app.view === "positions";
    const showRoomActions = app.view === "rooms";
    const navButton = (n: { key: string; label: string; icon: string; badge?: number }) => `
                    <button type="button" class="hi-nav__item${activeNav === n.key ? " hi-nav__item--active" : ""}" data-action="nav" data-view="${n.key}">
                        <span class="hi-nav__label">${icon(n.icon)} ${n.label}</span>
                        ${n.badge != null ? `<span class="hi-badge">${n.badge}</span>` : ""}
                    </button>`;

    return `
    <div class="hi-shell">
        <aside class="hi-sidebar">
            <input class="hi-instance-label" type="text" data-action="set-instance-label"
                value="${esc(app.instanceLabel)}" placeholder="Instanz benennen …" maxlength="60"
                title="Name dieser Instanz — hilft, mehrere gleichzeitig geöffnete Umgebungen auseinanderzuhalten">
            <button type="button" class="hi-wordmark" data-action="nav" data-view="dashboard"><b>Haus</b>Inventar</button>
            ${searchBox(app)}
            <nav class="hi-nav">
                ${nav.map(navButton).join("")}
                <div class="hi-nav__divider"></div>
                ${navSecondary.map(navButton).join("")}
            </nav>
            <div class="hi-sidebar__footer">
                <div class="hi-user">
                    <span class="hi-user__avatar">${esc(initials(app.userName))}</span>
                    <span class="hi-user__name">${esc(app.userName)}</span>
                    <button type="button" class="hi-icon-btn" data-action="logout" title="Abmelden" aria-label="Abmelden">${icon("power-off")}</button>
                </div>
            </div>
        </aside>
        <div class="hi-main">
            <header class="hi-header">
                <div>
                    <div class="hi-header__title">${meta.title}</div>
                    <div class="hi-header__subtitle">${meta.subtitle}</div>
                </div>
                ${showPositionActions ? `
                    <button type="button" class="hi-btn hi-btn--secondary" data-action="nav" data-view="export">Export</button>
                    <button type="button" class="hi-btn" data-action="add-position">+ Position</button>
                ` : ""}
                ${showRoomActions ? `
                    <button type="button" class="hi-btn" data-action="toggle-room-form">+ Raum</button>
                ` : ""}
            </header>
            <main class="hi-content" id="hi-content">${content}</main>
        </div>
    </div>`;
}

/* ------------------------------------------------------------ Dashboard */

/**
 * Farbfolge der Diagramme.
 *
 * Feste Reihenfolge, nie durchgewürfelt: Die sechs Töne sind als Folge gegen
 * Rot-/Grünschwäche geprüft (Abstand benachbarter Paare im OKLab-Raum), und
 * diese Prüfung hängt an genau dieser Reihenfolge. Eine siebte Kategorie
 * bekommt deshalb keine neue Farbe, sondern wandert in „Übrige" — ein
 * hinzuerfundener Ton wäre von einem der sechs nicht mehr zu unterscheiden.
 *
 * Die Werte selbst stehen als Tokens im Stylesheet, damit Diagramme und
 * Oberfläche aus derselben Quelle schöpfen.
 */
const CHART_COLORS = [
    "var(--hi-chart-1)",
    "var(--hi-chart-2)",
    "var(--hi-chart-3)",
    "var(--hi-chart-4)",
    "var(--hi-chart-5)",
    "var(--hi-chart-6)",
];
/** Sammelposten für alles jenseits der sechs Farben — bewusst grau, nicht bunt. */
const CHART_REST = "var(--hi-chart-rest)";
/**
 * Einfarbige Abstufung für geordnete Klassen.
 *
 * Wertspannen haben eine Reihenfolge; die soll man in der Farbe sehen (hell =
 * klein, dunkel = groß). Sechs verschiedene Farbtöne würden diese Ordnung
 * gerade verbergen.
 */
const CHART_RAMP = [
    "var(--hi-chart-ramp-1)",
    "var(--hi-chart-ramp-2)",
    "var(--hi-chart-ramp-3)",
    "var(--hi-chart-ramp-4)",
    "var(--hi-chart-ramp-5)",
];

/** Ein Segment eines Kreisdiagramms samt Legendenzeile. */
interface ChartSlice {
    label: string;
    value: number;
    color: string;
    /** Zusatz unter der Beschriftung, z. B. die Stückzahl hinter dem Wert. */
    sub?: string;
    /** data-Attribute, die die Legendenzeile anklickbar machen. */
    target?: string;
}

/** Anteil in Prozent; unter einem Prozent wird nicht auf 0 gerundet. */
function shareLabel(part: number, total: number): string {
    if (total <= 0) return "—";
    const anteil = (part / total) * 100;
    if (anteil > 0 && anteil < 1) return "< 1 %";
    return `${Math.round(anteil)} %`;
}

/** Gerundeter Eurobetrag — Kennzahlen brauchen keine Cent-Stellen. */
function fmtRound(n: number): string {
    return fmt(Math.round(n));
}

/**
 * Kreisdiagramm als Ring, gezeichnet aus einem einzigen Kreis je Segment.
 *
 * Der Ring entsteht über `stroke-dasharray`: Jedes Segment ist ein Kreis, von
 * dem nur sein Bogenstück gestrichelt sichtbar bleibt. Das kommt ohne
 * Pfadmathematik aus und hat den Nebeneffekt, dass die Trennung zwischen zwei
 * Segmenten eine echte Lücke ist statt einer aufgemalten Linie — nebeneinander
 * liegende Farben bleiben so auch dann unterscheidbar, wenn sie ähnlich sind.
 *
 * Die Legende trägt Wert und Anteil in Textfarbe: Ein Teil der Diagrammfarben
 * ist auf Weiß zu hell, um als Schrift zu taugen, und niemand soll Zahlen aus
 * einem Ring ablesen müssen.
 */
function donutChart(o: { title: string; hint?: string; slices: ChartSlice[]; centerLabel: string }): string {
    const sichtbar = o.slices.filter((s) => s.value > 0);
    const gesamt = sichtbar.reduce((s, x) => s + x.value, 0);

    if (gesamt <= 0) {
        return `
        <div class="hi-card hi-chart">
            <div class="hi-card__title">${esc(o.title)}</div>
            <div class="hi-empty">Noch keine Werte erfasst.</div>
        </div>`;
    }

    const radius = 62;
    const umfang = 2 * Math.PI * radius;
    // Lücke in Umfangs-Pixeln zwischen zwei Segmenten. Ein einziges Segment
    // bekommt keine — ein Vollkreis mit Schnitt sähe nach einem Fehler aus.
    const luecke = sichtbar.length > 1 ? 3 : 0;

    let start = 0;
    const arcs = sichtbar
        .map((s) => {
            const anteil = s.value / gesamt;
            // Sehr kleine Segmente würden von der Lücke ganz aufgezehrt und
            // verschwänden aus dem Ring, obwohl sie in der Legende stehen.
            const laenge = Math.max(anteil * umfang - luecke, 1.5);
            const arc = `
                <circle class="hi-donut__arc" cx="80" cy="80" r="${radius}" stroke="${s.color}"
                    stroke-dasharray="${laenge.toFixed(2)} ${(umfang - laenge).toFixed(2)}"
                    stroke-dashoffset="${(-start * umfang).toFixed(2)}">
                    <title>${esc(s.label)}: ${fmt(s.value)} (${shareLabel(s.value, gesamt)})</title>
                </circle>`;
            start += anteil;
            return arc;
        })
        .join("");

    const legende = o.slices
        .map((s) => {
            const inhalt = `
                <span class="hi-legend__dot" style="background:${s.color}"></span>
                <span class="hi-legend__label">${esc(s.label)}${s.sub ? `<span class="hi-legend__sub">${esc(s.sub)}</span>` : ""}</span>
                <span class="hi-legend__value">${fmt(s.value)}</span>
                <span class="hi-legend__share">${shareLabel(s.value, gesamt)}</span>`;
            return s.target
                ? `<button type="button" class="hi-legend__row hi-legend__row--link" ${s.target}>${inhalt}</button>`
                : `<div class="hi-legend__row">${inhalt}</div>`;
        })
        .join("");

    return `
    <div class="hi-card hi-chart">
        <div class="hi-card__title">${esc(o.title)}</div>
        ${o.hint ? `<div class="hi-chart__hint">${esc(o.hint)}</div>` : ""}
        <div class="hi-donut">
            <svg class="hi-donut__svg" viewBox="0 0 160 160" role="img" aria-label="${esc(o.title)}">
                <g transform="rotate(-90 80 80)">${arcs}
                </g>
            </svg>
            <div class="hi-donut__center">
                <span class="hi-donut__total">${fmt(gesamt)}</span>
                <span class="hi-donut__label">${esc(o.centerLabel)}</span>
            </div>
        </div>
        <div class="hi-legend">${legende}</div>
    </div>`;
}

/**
 * Fasst eine nach Wert sortierte Liste auf sechs Segmente zusammen.
 *
 * Alles ab dem siebten Eintrag wird zu „Übrige" — nicht aus Platzgründen,
 * sondern weil die Farbfolge nur sechs unterscheidbare Töne hat.
 */
function topSlices(eintraege: { label: string; value: number; sub?: string; target?: string }[]): ChartSlice[] {
    const sortiert = [...eintraege].filter((e) => e.value > 0).sort((a, b) => b.value - a.value || a.label.localeCompare(b.label, "de"));
    const oben = sortiert.slice(0, CHART_COLORS.length).map((e, index) => ({ ...e, color: CHART_COLORS[index] }));
    const rest = sortiert.slice(CHART_COLORS.length);
    if (rest.length > 0) {
        oben.push({
            label: "Übrige",
            sub: `${rest.length} weitere`,
            value: rest.reduce((s, e) => s + e.value, 0),
            color: CHART_REST,
        });
    }
    return oben;
}

/** Wert je Raum — die Verteilung, nach der die Versicherung zuerst fragt. */
function valueByRoom(app: App): ChartSlice[] {
    const eintraege = app.rooms.map((room) => {
        const stats = roomStats(app, room.id);
        return {
            label: room.title,
            value: stats.wert,
            sub: `${stats.positions.length} Positionen · ${stats.items.length} Gegenstände`,
            target: `data-action="open-room" data-id="${room.id}"`,
        };
    });
    // Positionen ohne Raum tauchen in keinem roomStats auf, ihr Wert aber im
    // Gesamtbetrag — ohne diesen Eintrag ergäbe die Summe der Segmente weniger
    // als die Kennzahl darüber, und niemand könnte sich den Unterschied erklären.
    const ohneRaum = app.positions
        .filter((p) => !app.rooms.some((r) => r.id === p.raum))
        .reduce((s, p) => s + positionValue(app, p.id), 0);
    if (ohneRaum > 0) eintraege.push({ label: "Ohne Raum", value: ohneRaum, sub: "", target: "" });
    return topSlices(eintraege);
}

/**
 * Wert je Quelle des Wertnachweises.
 *
 * Die wichtigste Verteilung für den Schadenfall: Ein Betrag, der auf einem
 * Kaufbeleg steht, wird anders behandelt als einer, der geschätzt ist.
 */
function valueBySource(app: App): ChartSlice[] {
    const buckets = new Map<string, { wert: number; anzahl: number }>();
    for (const item of app.items) {
        const key = item.quelle || "Ohne Quelle";
        const eintrag = buckets.get(key) ?? { wert: 0, anzahl: 0 };
        eintrag.wert += itemValue(item);
        eintrag.anzahl += 1;
        buckets.set(key, eintrag);
    }
    // Feste Reihenfolge statt Sortierung nach Wert: Die Quellen haben eine
    // Rangfolge in der Beweiskraft, und die soll in der Legende ablesbar sein.
    const reihenfolge = [...QUELLEN, "Ohne Quelle"];
    // Die Farbe hängt am festen Platz in dieser Reihenfolge, nicht an der
    // Position in der gefilterten Liste: Sonst bekäme „Schätzung" eine andere
    // Farbe, sobald irgendwo die erste Rechnung erfasst wird.
    return reihenfolge
        .map((quelle, index) => {
            const eintrag = buckets.get(quelle) ?? { wert: 0, anzahl: 0 };
            return {
                label: quelle,
                value: eintrag.wert,
                sub: `${eintrag.anzahl} Gegenstände`,
                color: CHART_COLORS[index % CHART_COLORS.length],
                target: `data-action="filter-items" data-quelle="${quelle === "Ohne Quelle" ? "__leer" : esc(quelle)}"`,
            };
        })
        .filter((s) => s.value > 0);
}

/** Grenzen der Wertklassen (Gesamtwert einer Zeile, also Wert × Anzahl). */
const VALUE_CLASSES: { label: string; max: number }[] = [
    { label: "bis 25 €", max: 25 },
    { label: "25 – 100 €", max: 100 },
    { label: "100 – 500 €", max: 500 },
    { label: "500 – 1.000 €", max: 1000 },
    { label: "über 1.000 €", max: Infinity },
];

/**
 * Wert je Größenklasse.
 *
 * Beantwortet die Frage, an der die Prüfarbeit hängt: Wie viel der
 * Schadensumme steckt in wenigen teuren Stücken und wie viel in der Masse der
 * kleinen? Geordnete Klassen, deshalb eine einfarbige Abstufung.
 */
function valueByClass(app: App): ChartSlice[] {
    const summen = VALUE_CLASSES.map(() => ({ wert: 0, anzahl: 0 }));
    for (const item of app.items) {
        const wert = itemValue(item);
        if (wert <= 0) continue;
        const index = VALUE_CLASSES.findIndex((k) => wert <= k.max);
        summen[index].wert += wert;
        summen[index].anzahl += 1;
    }
    return VALUE_CLASSES.map((klasse, index) => ({
        label: klasse.label,
        value: summen[index].wert,
        sub: `${summen[index].anzahl} Gegenstände`,
        color: CHART_RAMP[index],
    })).filter((s) => s.value > 0);
}

/** Mittlerer Wert einer Zeile — robuster als der Durchschnitt, wenn ein Einzelstück heraussticht. */
function median(werte: number[]): number {
    if (werte.length === 0) return 0;
    const sortiert = [...werte].sort((a, b) => a - b);
    const mitte = Math.floor(sortiert.length / 2);
    return sortiert.length % 2 ? sortiert[mitte] : (sortiert[mitte - 1] + sortiert[mitte]) / 2;
}

/**
 * Balkenliste der wertvollsten Positionen.
 *
 * Eine Reihe, ein Farbton — die Länge trägt die Aussage. Die Balken wachsen
 * aus einer gemeinsamen Grundlinie, damit sich die Längen vergleichen lassen.
 */
function topPositionsChart(app: App, anzahl = 8): string {
    const oben = app.positions
        .map((p) => ({ position: p, wert: positionValue(app, p.id) }))
        .filter((e) => e.wert > 0)
        .sort((a, b) => b.wert - a.wert)
        .slice(0, anzahl);

    if (oben.length === 0) {
        return `
        <div class="hi-card hi-chart">
            <div class="hi-card__title">Wertvollste Positionen</div>
            <div class="hi-empty">Noch keine bewerteten Positionen.</div>
        </div>`;
    }

    const groesster = oben[0].wert;
    return `
    <div class="hi-card hi-chart hi-chart--wide">
        <div class="hi-card__title">Wertvollste Positionen</div>
        <div class="hi-chart__hint">Die ${oben.length} Positionen mit dem höchsten Gesamtwert.</div>
        <div class="hi-barlist">
            ${oben
                .map(
                    (e) => `
            <button type="button" class="hi-barlist__row" data-action="open-position" data-id="${e.position.id}" title="Position öffnen">
                <span class="hi-barlist__label">${e.position.nummer ? `${e.position.nummer} · ` : ""}${esc(e.position.title)}<span class="hi-barlist__sub">${esc(app.roomName(e.position.raum))}</span></span>
                <span class="hi-barlist__track"><span class="hi-barlist__fill" style="width:${Math.max(2, Math.round((e.wert / groesster) * 100))}%"></span></span>
                <span class="hi-barlist__value">${fmt(e.wert)}</span>
            </button>`
                )
                .join("")}
        </div>
    </div>`;
}

/** Eine Fortschrittszeile der Nachweislage: Anteil am Gesamtwert. */
function coverageRow(label: string, gedeckt: number, gesamt: number, hint: string): string {
    const pct = gesamt > 0 ? Math.round((gedeckt / gesamt) * 100) : 0;
    return `
    <div class="hi-coverage">
        <div class="hi-coverage__head">
            <span class="hi-coverage__label">${esc(label)}</span>
            <span class="hi-coverage__value">${fmt(gedeckt)} <span class="hi-coverage__pct">${pct} %</span></span>
        </div>
        <div class="hi-bar"><div class="hi-bar__fill hi-bar__fill--geprueft" style="width:${pct}%"></div></div>
        <div class="hi-coverage__hint">${esc(hint)}</div>
    </div>`;
}

export function renderDashboard(app: App): string {
    const items = app.items;
    const gezaehlt = items.filter(countsAsValue);
    const total = items.reduce((s, i) => s + itemValue(i), 0);
    const stueck = stueckzahl(gezaehlt);
    const geprueftCount = items.filter((i) => i.geprueft).length;
    const pct = items.length ? Math.round((geprueftCount / items.length) * 100) : 0;

    const zeilenwerte = gezaehlt.map((i) => i.wert * i.anzahl);
    const proPosition = app.positions.length ? total / app.positions.length : 0;
    const proGegenstand = gezaehlt.length ? total / gezaehlt.length : 0;
    const proStueck = stueck ? total / stueck : 0;

    // Nachweislage: Was ist am Gesamtwert belegt, begründet, geprüft? Gezählt
    // wird in Euro, nicht in Zeilen — hundert offene Kleinteile wiegen weniger
    // als ein offenes Klavier.
    const geprueftWert = items.filter((i) => i.geprueft).reduce((s, i) => s + itemValue(i), 0);
    const begruendetWert = items.filter((i) => i.wertBegruendung).reduce((s, i) => s + itemValue(i), 0);
    const belegtWert = items.filter((i) => i.shopLinks.length > 0).reduce((s, i) => s + itemValue(i), 0);

    const teuerster = [...items].sort((a, b) => itemValue(b) - itemValue(a))[0];
    const groessterRaum = app.rooms
        .map((r) => ({ raum: r, wert: roomStats(app, r.id).wert }))
        .sort((a, b) => b.wert - a.wert)[0];
    const dupCount = items.filter((i) => i.duplikatStatus === "bestaetigt").length;

    return `
    <div class="hi-stack">
        <div class="hi-callout hi-callout--info">
            <b>So funktioniert die Erfassung</b>
            <p>Legen Sie je Sache oder Gruppe eine Position an und erfassen Sie darunter die einzelnen Gegenstände mit Wert und Nachweis. Bestätigen Sie jeden geprüften Gegenstand als „Geprüft".</p>
        </div>
        <div class="hi-stats">
            <div class="hi-stat" data-action="nav" data-view="rooms"><div class="hi-stat__value">${app.rooms.length}</div><div class="hi-stat__label">${typeIcon("room")} Räume</div></div>
            <div class="hi-stat" data-action="nav" data-view="positions"><div class="hi-stat__value">${app.positions.length}</div><div class="hi-stat__label">${typeIcon("position")} Positionen</div></div>
            <div class="hi-stat" data-action="nav" data-view="items"><div class="hi-stat__value">${countLabel(items)}</div><div class="hi-stat__label">${typeIcon("item")} Gegenstände / Stück</div></div>
            <div class="hi-stat"><div class="hi-stat__value">${fmt(total)}</div><div class="hi-stat__label">Gesamtwert</div></div>
        </div>
        <div class="hi-stats">
            <div class="hi-stat"><div class="hi-stat__value">${fmtRound(proPosition)}</div><div class="hi-stat__label">Ø Wert je Position</div></div>
            <div class="hi-stat"><div class="hi-stat__value">${fmtRound(proGegenstand)}</div><div class="hi-stat__label">Ø Wert je Gegenstand</div></div>
            <div class="hi-stat"><div class="hi-stat__value">${fmtRound(proStueck)}</div><div class="hi-stat__label">Ø Wert je Stück</div></div>
            <div class="hi-stat" title="Die Hälfte der Gegenstände liegt darunter, die Hälfte darüber."><div class="hi-stat__value">${fmtRound(median(zeilenwerte))}</div><div class="hi-stat__label">Median je Gegenstand</div></div>
        </div>
        <div class="hi-card">
            <div class="hi-detail-head">
                <div class="hi-card__title" style="margin-bottom:0">Prüfstand</div>
                ${items.length > geprueftCount ? `<button type="button" class="hi-btn" data-action="nav" data-view="review">Prüf-Assistent starten</button>` : ""}
            </div>
            <div class="hi-status-row">
                <span class="hi-status hi-status--geprueft">Geprüft</span>
                <div class="hi-bar"><div class="hi-bar__fill hi-bar__fill--geprueft" style="width:${pct}%"></div></div>
                <span class="hi-status-row__count">${geprueftCount} / ${items.length}</span>
            </div>
        </div>
        <div class="hi-charts">
            ${donutChart({
                title: "Wert nach Räumen",
                hint: "Woran hängt die Schadensumme?",
                centerLabel: "Gesamtwert",
                slices: valueByRoom(app),
            })}
            ${donutChart({
                title: "Wert nach Quelle",
                hint: "Worauf beruhen die Beträge?",
                centerLabel: "Gesamtwert",
                slices: valueBySource(app),
            })}
            ${donutChart({
                title: "Wert nach Größenklassen",
                hint: "Wie viel steckt in wenigen teuren Stücken?",
                centerLabel: "Gesamtwert",
                slices: valueByClass(app),
            })}
        </div>
        ${topPositionsChart(app)}
        <div class="hi-grid-2">
            <div class="hi-card">
                <div class="hi-card__title">Nachweislage</div>
                <div class="hi-chart__hint">Anteile am Gesamtwert von ${fmt(total)} — nicht an der Zahl der Zeilen.</div>
                ${coverageRow("Geprüft bestätigt", geprueftWert, total, `${geprueftCount} von ${items.length} Gegenständen sind als geprüft bestätigt.`)}
                ${coverageRow("Mit Wertbegründung", begruendetWert, total, "Die Begründung erklärt der Versicherung, warum der Betrag angemessen ist.")}
                ${coverageRow("Mit Vergleichsangebot", belegtWert, total, "Ein Angebot mit Preis und Abrufdatum belegt den Wiederbeschaffungswert.")}
            </div>
            <div class="hi-card">
                <div class="hi-card__title">Spitzenwerte</div>
                <div class="hi-kv-grid hi-kv-grid--2">
                ${kv("Ø Gegenstände je Position", app.positions.length ? (items.length / app.positions.length).toFixed(1).replace(".", ",") : "—")}
                ${kv("Ø Positionen je Raum", app.rooms.length ? (app.positions.length / app.rooms.length).toFixed(1).replace(".", ",") : "—")}
                ${kv("Teuerster Gegenstand", teuerster && itemValue(teuerster) > 0 ? `${esc(teuerster.title)} · ${fmt(itemValue(teuerster))}` : "—")}
                ${kv("Wertvollster Raum", groessterRaum && groessterRaum.wert > 0 ? `${esc(groessterRaum.raum.title)} · ${fmt(groessterRaum.wert)} (${shareLabel(groessterRaum.wert, total)})` : "—")}
                ${kv("Gegenstände ab 500 €", String(gezaehlt.filter((i) => i.wert * i.anzahl >= 500).length))}
                ${kv("Bestätigte Duplikate", dupCount ? `${dupCount} · zählen nicht mit` : "keine")}
                </div>
            </div>
        </div>
    </div>`;
}

/* --------------------------------------------------------- Prüf-Assistent */

export function renderReview(app: App): string {
    const total = app.items.length;
    const geprueftCount = app.items.filter((i) => i.geprueft).length;
    const pct = total ? Math.round((geprueftCount / total) * 100) : 0;

    // Warteschlange bereinigen: zwischenzeitlich geprüfte oder gelöschte
    // Gegenstände aus der Session-Reihenfolge entfernen.
    while (app.reviewQueue.length) {
        const candidate = app.items.find((i) => i.id === app.reviewQueue[0]);
        if (candidate && !candidate.geprueft) break;
        app.reviewQueue.shift();
    }

    const progress = `
    <div class="hi-card">
        <div class="hi-status-row">
            <span class="hi-status hi-status--geprueft">Gesamtfortschritt</span>
            <div class="hi-bar"><div class="hi-bar__fill hi-bar__fill--geprueft" style="width:${pct}%"></div></div>
            <span class="hi-status-row__count">${geprueftCount} / ${total}</span>
        </div>
    </div>`;

    const item = app.items.find((i) => i.id === app.reviewQueue[0]);

    if (!item) {
        return `
        <div class="hi-stack" style="max-width:640px">
            ${progress}
            <div class="hi-card" style="text-align:center">
                <div class="hi-card__title">Alle Gegenstände geprüft 🎉</div>
                <p>Es gibt aktuell keine offenen Gegenstände mehr.</p>
                <button type="button" class="hi-btn" data-action="nav" data-view="positions">Zur Übersicht</button>
            </div>
        </div>`;
    }

    const pos = positionOf(app, item.position);

    return `
    <div class="hi-stack" style="max-width:760px">
        ${progress}
        <div class="hi-card">
            <div class="hi-detail-head">
                <div>
                    <div class="hi-detail-sub">Position</div>
                    <div class="hi-detail-title">${typeIcon("position")} ${pos ? esc(pos.title) : "—"}</div>
                    <div class="hi-detail-sub">${pos ? esc(app.roomName(pos.raum)) : ""}</div>
                </div>
                <span class="hi-badge">${app.reviewQueue.length} verbleibend</span>
            </div>
            ${pos?.beschreibung ? `<div class="hi-item-row__note">${esc(pos.beschreibung)}</div>` : ""}
            ${pos ? importLog(pos.importLog, "Import-Protokoll der Position (KI)") : ""}
            <div class="hi-card__title" style="margin-top:var(--hi-space-4)">Fotos der Position</div>
            ${pos ? photoGrid(pos.fotos, "position", pos.id) : `<div class="hi-empty">Position nicht gefunden.</div>`}
        </div>

        <div class="hi-card">
            <div class="hi-card__title">Gegenstand</div>
            ${reviewCrop(item, pos?.fotos ?? [])}
            ${duplicateBlock(app, item)}
            ${importLog(item.importLog)}
            <form data-form="review-save" data-id="${item.id}" class="hi-form">
                <label class="hi-field">Bezeichnung *
                    <input class="hi-input" type="text" name="title" required value="${esc(item.title)}">
                </label>
                <div class="hi-grid-3">
                    <label class="hi-field">Anzahl *
                        <input class="hi-input" type="number" name="anzahl" min="1" required value="${item.anzahl}">
                    </label>
                    <label class="hi-field">Alter
                        <input class="hi-input" type="text" name="alter" value="${esc(item.alter)}" placeholder="z. B. ca. 3 Jahre">
                    </label>
                </div>
                <div class="hi-grid-3">
                    <label class="hi-field">Wert (€ je Stück) *
                        <input class="hi-input" type="text" inputmode="decimal" name="wert" required value="${decimalValue(item.wert)}">
                    </label>
                    ${versandkostenField(item.versandkosten)}
                    <label class="hi-field">Datum
                        <input class="hi-input" type="date" name="datum" value="${esc(item.datum)}">
                    </label>
                    <label class="hi-field">Quelle
                        <select class="hi-input" name="quelle">
                            <option value="">—</option>
                            ${QUELLEN.map((q) => option(q, q, item.quelle)).join("")}
                        </select>
                    </label>
                </div>
                ${shopLinkFields(item.shopLinks)}
                ${wertBegruendungField(item.wertBegruendung)}
                <label class="hi-field">Sonstiges
                    <textarea class="hi-input" name="sonstiges" rows="2">${esc(item.sonstiges)}</textarea>
                </label>
                <div class="hi-field">Fotos &amp; Belege
                    ${photoGrid(item.fotos, "item", item.id)}
                </div>
                <div class="hi-review-actions">
                    <button type="button" class="hi-btn hi-btn--ghost" data-action="nav" data-view="dashboard">Beenden</button>
                    <div class="hi-review-actions__main">
                        <button type="button" class="hi-btn hi-btn--secondary" data-action="review-skip" data-id="${item.id}">Überspringen</button>
                        <button type="submit" class="hi-btn hi-btn--secondary" data-intent="save">Änderungen speichern</button>
                        <button type="submit" class="hi-btn" data-intent="confirm">Als geprüft bestätigen</button>
                    </div>
                </div>
            </form>
        </div>
    </div>`;
}

/* ----------------------------------------------------- Listen-Tabellen */

/** Kennung einer virtualisierten Liste; sie verbindet Hülle und Zeilenlieferant. */
export type VirtualList = "positions" | "rooms" | "items";

/**
 * Eine Zeile für die virtualisierte Tabelle samt erwarteter Höhe.
 *
 * Die Höhe ist nur ein Startwert — gemessen wird, sobald die Zeile das erste
 * Mal gezeigt wird. Trotzdem lohnt eine gute Schätzung: Aus ihr entsteht die
 * Gesamthöhe der Bildlaufleiste, und die soll nicht unter der Hand wachsen,
 * während man sich durch die Liste arbeitet. Eine aufgeklappte Position mit
 * zwanzig Gegenständen ist zehnmal so hoch wie eine zugeklappte; das lässt
 * sich vorher ausrechnen, statt es der Messung zu überlassen.
 */
export interface VirtualRow {
    html: string;
    hoehe: number;
    /**
     * ID des Datensatzes hinter der Zeile.
     *
     * Damit lässt sich die Scrollposition an einer Zeile festmachen statt an
     * einem Pixelwert. Nötig, weil die Höhen nach einem Neuaufbau zunächst
     * wieder Schätzungen sind: Derselbe Pixelabstand zeigt dann auf eine
     * andere Zeile als vorher.
     */
    key: number;
}

/** Höhe einer Tabellenzeile in der Liste, gemessen im fertigen Layout. */
const ROW_HEIGHT = 47;
/** Höhe eines Eintrags in der aufgeklappten Unterzeile, plus deren Innenabstand. */
const SUBITEM_HEIGHT = 31;
const SUBROW_PADDING = 73;

/** Erwartete Höhe einer Zeile mit optional aufgeklappter Unterzeile. */
function rowEstimate(expanded: boolean, children: number): number {
    if (!expanded) return ROW_HEIGHT;
    // Ohne Einträge steht dort der Hinweis "Noch keine …".
    const inhalt = children > 0 ? children * SUBITEM_HEIGHT + SUBROW_PADDING : 52;
    return ROW_HEIGHT + inhalt;
}

/**
 * Rumpf einer Listentabelle — ohne Zeilen.
 *
 * Die Zeilen setzt die App nachträglich ein und hält immer nur den sichtbaren
 * Ausschnitt im DOM (siehe VirtualTable in InventoryApp.ts). Bei knapp
 * zweitausend Gegenständen sind das statt zweitausend Zeilen rund vierzig —
 * der Unterschied zwischen ruckelndem und flüssigem Scrollen.
 *
 * `data-columns` braucht der Platzhalter oben und unten in der Tabelle: Eine
 * Abstandszeile muss dieselbe Spaltenzahl überspannen, sonst verschiebt sie
 * das Spaltenraster.
 */
function tableShell(o: {
    app: App;
    virtual: VirtualList;
    columns: number;
    head: string;
    rows: () => VirtualRow[];
    extraClass?: string;
}): string {
    // Ohne Virtualisierung stehen alle Zeilen von Anfang an im Dokument. Das
    // ist bei kurzen Listen unauffällig und hat einen Vorteil, den die
    // Virtualisierung nicht bieten kann: Die Suche des Browsers findet Zeilen,
    // die gerade nicht sichtbar sind.
    const virtuell = o.app.virtualLists[o.virtual];
    const koerper = virtuell
        ? `<tbody data-virtual="${o.virtual}" data-columns="${o.columns}"></tbody>`
        : `<tbody>${o.rows().map((r) => r.html).join("")}</tbody>`;

    return `
    <div class="hi-card hi-card--flush hi-card--table">
        <div class="hi-table-scroll hi-table-scroll--fill" data-table-scroll>
            <table class="hi-table${o.extraClass ? " " + o.extraClass : ""}">
                <thead>
                    <tr>${o.head}
                    </tr>
                </thead>
                ${koerper}
            </table>
        </div>
    </div>`;
}

/**
 * Schalter für die Virtualisierung, im Werkzeugbalken jeder Liste.
 *
 * Die Zeilenzahl steht dabei, weil sie die Entscheidung trägt: Bei fünfzehn
 * Räumen ist der Unterschied nicht spürbar, bei zweitausend Gegenständen sehr.
 */
function virtualToggle(app: App, list: VirtualList, zeilen: number): string {
    const an = app.virtualLists[list];
    return `
    <label class="hi-checkbox hi-checkbox--quiet" title="Rendert nur die sichtbaren Zeilen. Aus: alle ${zeilen} Zeilen stehen im Dokument — langsamer, dafür findet die Browsersuche (Strg+F) auch nicht sichtbare Zeilen.">
        <input type="checkbox" data-action="toggle-virtual" data-list="${list}"${an ? " checked" : ""}>
        Nur sichtbare Zeilen rendern
    </label>`;
}

/* ------------------------------------------------- Mehrfachauswahl (Listen) */

/**
 * Zeilen-Attribute der Mehrfachauswahl (Strg/Shift-Klick, siehe
 * App.handleListSelection). Markierte Zeilen tragen die Klasse direkt im
 * HTML, damit auch Zeilen stimmen, die die virtuelle Tabelle erst beim
 * Scrollen einsetzt.
 */
function selectedRowAttr(app: App, kind: VirtualList, id: number): string {
    return app.listSelection.kind === kind && app.listSelection.ids.has(id)
        ? ` class="hi-row--selected" aria-selected="true"`
        : "";
}

/**
 * Auswahl-Summe für die Summenzeile einer Liste.
 *
 * Gezählt wird nur, was der aktuelle Filter zeigt — eine weggefilterte Zeile
 * soll die Summe nicht unsichtbar erhöhen. Die Auswahl selbst bleibt beim
 * Filtern erhalten und lebt wieder auf, sobald die Zeile zurückkommt.
 */
function selectionSummary(app: App, kind: VirtualList, sichtbar: number[], wert: (id: number) => number): string {
    if (app.listSelection.kind !== kind || app.listSelection.ids.size === 0) return "";
    const ids = sichtbar.filter((id) => app.listSelection.ids.has(id));
    if (ids.length === 0) return "";
    const summe = ids.reduce((s, id) => s + wert(id), 0);
    return `
        <span class="hi-list-summary__selection" title="Auswahl per Strg-Klick (einzeln) oder Shift-Klick (Bereich)">
            ${ids.length} markiert · <b>${fmt(summe)}</b>
            <button type="button" class="hi-selection-clear" data-action="clear-selection" title="Auswahl aufheben" aria-label="Auswahl aufheben">×</button>
        </span>`;
}

/* -------------------------------------------------------------- Positionen */

export function renderPositionsList(app: App): string {
    const allExpanded = app.positions.length > 0 && app.positions.every((p) => app.expandedPositions.has(p.id));

    return `
    <div class="hi-stack hi-list-view">
        <div class="hi-toolbar">
            <input type="search" class="hi-input" data-filter="q" placeholder="Position, Nummer, Raum oder Gegenstand suchen…" value="${esc(app.listFilter.q)}">
            <select class="hi-input" data-filter="raum">
                <option value="">Alle Räume</option>
                ${app.rooms.map((r) => option(String(r.id), r.title, app.listFilter.raum)).join("")}
            </select>
            <label class="hi-checkbox hi-checkbox--end">
                <input type="checkbox" data-action="toggle-all-position-items"${allExpanded ? " checked" : ""}>
                Gegenstände anzeigen
            </label>
            ${virtualToggle(app, "positions", app.positions.length)}
        </div>
        <div class="hi-list-view__results" id="hi-list-results">${renderPositionsListResults(app)}</div>
    </div>`;
}

const POSITION_SORT_COLUMNS: { key: App["listSort"]["key"]; label: string }[] = [
    { key: "nummer", label: "Pos." },
    { key: "title", label: "Bezeichnung" },
    { key: "raum", label: "Raum" },
    { key: "items", label: "Gegenstände" },
    { key: "stueck", label: "Stück" },
    { key: "wert", label: "Wert" },
    { key: "geprueft", label: "Geprüft" },
];

function geprueftRatio(app: App, positionId: number): number {
    const children = itemsOf(app, positionId);
    if (children.length === 0) return 0;
    return children.filter((i) => i.geprueft).length / children.length;
}

function comparePositions(app: App, a: Position, b: Position, key: App["listSort"]["key"]): number {
    switch (key) {
        case "title":
            return a.title.localeCompare(b.title, "de");
        case "raum":
            return app.roomName(a.raum).localeCompare(app.roomName(b.raum), "de");
        case "items":
            return itemsOf(app, a.id).length - itemsOf(app, b.id).length;
        case "stueck":
            return stueckzahl(itemsOf(app, a.id)) - stueckzahl(itemsOf(app, b.id));
        case "wert":
            return positionValue(app, a.id) - positionValue(app, b.id);
        case "geprueft":
            return geprueftRatio(app, a.id) - geprueftRatio(app, b.id);
        case "nummer":
        default:
            return (a.nummer || 0) - (b.nummer || 0);
    }
}

/** Positionen nach Filter und Sortierung — Grundlage von Zeilen und Summenzeile. */
export function filteredPositions(app: App): Position[] {
    const q = app.listFilter.q.trim();
    const { key: sortKey, dir } = app.listSort;
    const mul = dir === "desc" ? -1 : 1;
    // Alle Spalten der Liste, samt der Gegenstände aus der Spalte „Gegenstände".
    const titelJePosition = q ? itemTitlesByPosition(app) : null;
    return app.positions
        .filter((p) =>
            (!q || matchesId(q, p.id) || matchesFilter(
                q,
                p.nummer || "",
                p.title,
                app.roomName(p.raum),
                p.beschreibung,
                titelJePosition?.get(p.id) ?? ""
            )) &&
            (!app.listFilter.raum || p.raum === Number(app.listFilter.raum))
        )
        .sort((a, b) => comparePositions(app, a, b, sortKey) * mul);
}

export function renderPositionsListResults(app: App): string {
    const { key: sortKey, dir } = app.listSort;
    const filtered = filteredPositions(app);

    if (filtered.length === 0) {
        return `<div class="hi-empty">Keine Treffer. Passen Sie Suche oder Filter an.</div>`;
    }

    const totalValue = (list: Position[]) => list.reduce((s, p) => s + positionValue(app, p.id), 0);
    const sortArrow = (key: App["listSort"]["key"]) => (key === sortKey ? (dir === "desc" ? " ↓" : " ↑") : "");

    return `
    ${tableShell({
        app,
        virtual: "positions",
        rows: () => positionsListRows(app),
        columns: POSITION_SORT_COLUMNS.length + 2,
        head: `
                        <th></th>
                        ${POSITION_SORT_COLUMNS.map((c) => `<th><button type="button" class="hi-sort-th" data-action="sort-positions" data-sort-key="${c.key}">${c.label}${sortArrow(c.key)}</button></th>`).join("")}
                        <th></th>`,
    })}
    <div class="hi-list-summary">
        <span>${filtered.length} von ${app.positions.length} Positionen</span>
        ${selectionSummary(app, "positions", filtered.map((p) => p.id), (id) => positionValue(app, id))}
        <span>Gesamtwert Auswahl: ${fmt(totalValue(filtered))}</span>
    </div>`;
}

/** Die Zeilen der Positionsliste, eine je Eintrag — samt aufgeklappter Gegenstände. */
export function positionsListRows(app: App): VirtualRow[] {
    // Sortierspalten plus Aufklapp- und Chevron-Spalte — mitgezählt statt
    // fest verdrahtet, damit eine neue Spalte den Subrow-Colspan nicht bricht.
    const colCount = POSITION_SORT_COLUMNS.length + 2;
    const geprueftLabel = (p: Position) => {
        const children = itemsOf(app, p.id);
        return ratio(children.filter((i) => i.geprueft).length, children.length);
    };

    return filteredPositions(app).map((p): VirtualRow => {
        const expanded = app.expandedPositions.has(p.id);
        const children = itemsOf(app, p.id);
        return { key: p.id, hoehe: rowEstimate(expanded, children.length), html: `
                        <tr data-action="open-position" data-id="${p.id}" data-item-id="${p.id}"${selectedRowAttr(app, "positions", p.id)}>
                            <td><button type="button" class="hi-expand-toggle${expanded ? " hi-expand-toggle--open" : ""}" data-action="toggle-position-items" data-id="${p.id}" title="Gegenstände ${expanded ? "verbergen" : "anzeigen"}" aria-label="Gegenstände ${expanded ? "verbergen" : "anzeigen"}">${icon("chevron-right")}</button></td>
                            <td>${p.nummer || "—"}</td>
                            <td><div class="hi-cell-title">${typeIcon("position")} ${esc(p.title)}</div></td>
                            <td>${esc(app.roomName(p.raum))}</td>
                            <td>${children.length}</td>
                            <td>${stueckzahl(children)}</td>
                            <td>${fmt(positionValue(app, p.id))}</td>
                            <td>${geprueftLabel(p)}</td>
                            <td class="hi-table__chevron">${icon("chevron-right")}</td>
                        </tr>
                        ${expanded ? `
                        <tr class="hi-table__subrow">
                            <td colspan="${colCount}">
                                ${children.length ? `
                                <div class="hi-subitems">
                                    ${children.map((i) => `
                                        <button type="button" class="hi-subitem" data-action="open-item" data-id="${i.id}" title="${esc(i.title)} in der Position öffnen">
                                            <span class="hi-subitem__title">${typeIcon("item")} ${esc(i.title)}</span>
                                            <span>${i.anzahl}×</span>
                                            <span>${fmt(i.wert)} je Stück</span>
                                            <span><b>${fmt(i.wert * i.anzahl)}</b> gesamt</span>
                                            <span class="hi-status hi-status--${i.geprueft ? "geprueft" : "erfasst"}">${i.geprueft ? "Geprüft" : "Offen"}</span>
                                            ${dupBadgeIcon(effectiveDuplicateStatus(app, i))}
                                            <span class="hi-subitem__chevron">${icon("chevron-right")}</span>
                                        </button>`).join("")}
                                </div>` : `<div class="hi-empty" style="padding:var(--hi-space-3)">Noch keine Gegenstände erfasst.</div>`}
                            </td>
                        </tr>` : ""}` };
    });
}

/* ------------------------------------------------------- Gegenstände */

const ITEM_SORT_COLUMNS: { key: App["itemSort"]["key"]; label: string }[] = [
    { key: "id", label: "ID" },
    { key: "title", label: "Bezeichnung" },
    { key: "anzahl", label: "Anzahl" },
    { key: "wert", label: "€ je Stück" },
    { key: "gesamt", label: "Gesamt" },
    { key: "quelle", label: "Quelle" },
    { key: "position", label: "Position" },
    { key: "raum", label: "Raum" },
    { key: "geprueft", label: "Status" },
];

function compareItems(app: App, a: Item, b: Item, key: App["itemSort"]["key"]): number {
    const pos = (i: Item) => app.positions.find((p) => p.id === i.position);
    switch (key) {
        case "id":
            return a.id - b.id;
        case "anzahl":
            return a.anzahl - b.anzahl;
        case "wert":
            return a.wert - b.wert;
        case "gesamt":
            return a.wert * a.anzahl - b.wert * b.anzahl;
        case "quelle":
            return a.quelle.localeCompare(b.quelle, "de") || a.title.localeCompare(b.title, "de");
        case "position":
            return (pos(a)?.nummer || 0) - (pos(b)?.nummer || 0) || a.title.localeCompare(b.title, "de");
        case "raum":
            return app.roomName(pos(a)?.raum ?? 0).localeCompare(app.roomName(pos(b)?.raum ?? 0), "de")
                || a.title.localeCompare(b.title, "de");
        case "geprueft":
            return Number(a.geprueft) - Number(b.geprueft) || a.title.localeCompare(b.title, "de");
        case "title":
        default:
            return a.title.localeCompare(b.title, "de");
    }
}

/**
 * Filter der Gegenstandsliste.
 *
 * Die Auswahl folgt den Fragen, die beim Durcharbeiten der Aufstellung
 * tatsächlich anfallen: Wo steht das Stück (Raum), worauf beruht sein Wert
 * (Quelle), was ist noch offen (Status), und wo fehlt etwas, das die
 * Versicherung sehen will (Begründung, Beleg, Duplikat-Verdacht).
 */
const ITEM_STATE_FILTERS: { value: string; label: string; test: (i: Item, app: App) => boolean }[] = [
    { value: "offen", label: "Nur ungeprüfte", test: (i) => !i.geprueft },
    { value: "geprueft", label: "Nur geprüfte", test: (i) => i.geprueft },
    { value: "ohne_begruendung", label: "Ohne Wertbegründung", test: (i) => !i.wertBegruendung },
    { value: "ohne_beleg", label: "Ohne Vergleichsangebot", test: (i) => i.shopLinks.length === 0 },
    { value: "duplikat", label: "Mit Duplikat-Verdacht", test: (i, app) => effectiveDuplicateStatus(app, i) !== "" },
    { value: "ohne_crop", label: "Ohne Bildausschnitt", test: (i) => !i.bildausschnitt },
];

export function renderItemsList(app: App): string {
    return `
    <div class="hi-stack hi-list-view">
        <div class="hi-toolbar">
            <input type="search" class="hi-input" data-item-filter="q" placeholder="Gegenstand, Quelle, Position oder Raum suchen…" value="${esc(app.itemFilter.q)}">
            <select class="hi-input" data-item-filter="raum">
                <option value="">Alle Räume</option>
                ${app.rooms.map((r) => option(String(r.id), r.title, app.itemFilter.raum)).join("")}
            </select>
            <select class="hi-input" data-item-filter="quelle">
                <option value="">Alle Quellen</option>
                ${QUELLEN.map((q) => option(q, q, app.itemFilter.quelle)).join("")}
                ${option("__leer", "Ohne Quelle", app.itemFilter.quelle)}
            </select>
            <select class="hi-input" data-item-filter="state">
                <option value="">Alle Gegenstände</option>
                ${ITEM_STATE_FILTERS.map((f) => option(f.value, f.label, app.itemFilter.state)).join("")}
            </select>
            ${virtualToggle(app, "items", app.items.length)}
        </div>
        <div class="hi-list-view__results" id="hi-item-results">${renderItemsListResults(app)}</div>
    </div>`;
}

/** Gegenstände nach Filter und Sortierung — Grundlage von Zeilen und Summenzeile. */
export function filteredItems(app: App): Item[] {
    const { q, raum, quelle, state } = app.itemFilter;
    const suche = q.trim().toLowerCase();
    const stateFilter = ITEM_STATE_FILTERS.find((f) => f.value === state);
    const positionsById = new Map(app.positions.map((p) => [p.id, p]));

    return app.items
        .filter((i) => {
            const position = positionsById.get(i.position);
            // Alle Spalten der Liste: Bezeichnung, Quelle, Position (mit
            // Nummer) und Raum — dazu Sonstiges, das in der Zeile mitläuft.
            // Eine ID-Eingabe ("1953" oder "#1953") findet den Gegenstand direkt.
            if (suche && !matchesId(suche, i.id) && !matchesFilter(
                suche,
                i.title,
                i.sonstiges,
                i.quelle,
                position?.nummer || "",
                position?.title ?? "",
                position ? app.roomName(position.raum) : ""
            )) return false;
            if (raum && position?.raum !== Number(raum)) return false;
            if (quelle === "__leer" ? i.quelle !== "" : quelle && i.quelle !== quelle) return false;
            if (stateFilter && !stateFilter.test(i, app)) return false;
            return true;
        })
        .sort((a, b) => compareItems(app, a, b, app.itemSort.key) * (app.itemSort.dir === "desc" ? -1 : 1));
}

export function renderItemsListResults(app: App): string {
    const filtered = filteredItems(app);

    if (filtered.length === 0) {
        return `<div class="hi-empty">Keine Treffer. Passen Sie Suche oder Filter an.</div>`;
    }

    const sortArrow = (key: App["itemSort"]["key"]) =>
        key === app.itemSort.key ? (app.itemSort.dir === "desc" ? " ↓" : " ↑") : "";
    const summe = filtered.reduce((s, i) => s + itemValue(i), 0);
    const stueck = stueckzahl(filtered);

    return `
    ${tableShell({
        app,
        virtual: "items",
        rows: () => itemsListRows(app),
        columns: ITEM_SORT_COLUMNS.length + 2,
        extraClass: "hi-table--items",
        head: `
                        <th></th>
                        ${ITEM_SORT_COLUMNS.map((c) => `<th><button type="button" class="hi-sort-th" data-action="sort-items" data-sort-key="${c.key}">${c.label}${sortArrow(c.key)}</button></th>`).join("")}
                        <th></th>`,
    })}
    <div class="hi-list-summary">
        <span>${filtered.length} von ${app.items.length} Gegenständen · ${stueck} Stück</span>
        ${selectionSummary(app, "items", filtered.map((i) => i.id), (id) => {
            const item = app.items.find((i) => i.id === id);
            return item ? itemValue(item) : 0;
        })}
        <span>Gesamtwert Auswahl: ${fmt(summe)}</span>
    </div>`;
}

export function itemsListRows(app: App): VirtualRow[] {
    const positionsById = new Map(app.positions.map((p) => [p.id, p]));
    return filteredItems(app).map((i): VirtualRow => {
        // Die Zeilenhöhe hängt daran, ob Bezeichnung und Fundort umbrechen und
        // ob ein Hinweis unter dem Namen steht. Grob geschätzt genügt: Gemessen
        // wird ohnehin, sobald die Zeile das erste Mal sichtbar wird.
        const position = positionsById.get(i.position);
        const lang = i.title.length > 34 || (position?.title.length ?? 0) > 40;
        const hinweise = (effectiveDuplicateStatus(app, i) ? 1 : 0) + (!i.wertBegruendung && i.wert * i.anzahl >= 50 ? 1 : 0);
        return {
            key: i.id,
            hoehe: 76 + (lang ? 18 : 0) + hinweise * 20,
            html: itemsListRow(app, i, position),
        };
    });
}

/**
 * Eine Zeile der Gegenstandsliste.
 *
 * Der Bildausschnitt wird **nicht** wie sonst nach dem Rendern gefüllt: Bei
 * knapp zweitausend Zeilen würde das zweitausend Fotos laden, bevor die erste
 * sichtbar ist. Die Vorschau trägt hier nur die Daten für den Zuschnitt und
 * wird von einem IntersectionObserver nachgezogen, sobald die Zeile in den
 * Blick kommt (siehe observeLazyCrops).
 *
 * Sie ist ein eigener Knopf und öffnet die Lightbox. Ohne ihn würde der Klick
 * an die Zeile durchgereicht und die Detailansicht öffnen — wer auf ein Bild
 * klickt, will aber das Bild größer sehen.
 */
function itemsListRow(app: App, item: Item, position: Position | undefined): string {
    const thumb = position
        ? cropPreview(item, position.fotos, "hi-crop-thumb--cell", true, true)
        : "";
    const zelle = thumb || `<span class="hi-crop-thumb hi-crop-thumb--cell hi-crop-thumb--leer" title="Kein Bildausschnitt">${icon("image-slash")}</span>`;

    const raumId = position?.raum ?? 0;

    return `
    <tr data-action="open-item" data-id="${item.id}"${selectedRowAttr(app, "items", item.id)}>
        <td class="hi-table__thumb">${zelle}</td>
        <td class="c-num">${idTag(item.id)}</td>
        <td>
            <div class="hi-cell-title">${typeIcon("item")} ${esc(item.title)}</div>
            ${dupBadge(effectiveDuplicateStatus(app, item))}
            ${!item.wertBegruendung && item.wert * item.anzahl >= 50
                ? `<span class="hi-cell-warn">${icon("scale-balanced")} ohne Begründung</span>`
                : ""}
        </td>
        <td class="c-num">${item.anzahl}</td>
        <td class="c-num">${fmt(item.wert)}</td>
        <td class="c-num"><b>${fmt(item.wert * item.anzahl)}</b>${item.duplikatStatus === "bestaetigt" ? `<span class="hi-cell-sub">nicht gezählt</span>` : ""}</td>
        <td>${esc(item.quelle) || "—"}${item.shopLinks.length ? `<span class="hi-cell-sub">${item.shopLinks.length} Angebot(e)</span>` : ""}</td>
        <td>${position
            ? `<button type="button" class="hi-cell-link" data-action="open-position" data-id="${position.id}" title="Position öffnen">${typeIcon("position")} ${position.nummer ? `${position.nummer} · ` : ""}${esc(position.title)}</button>`
            : "—"}</td>
        <td>${raumId
            ? `<button type="button" class="hi-cell-link" data-action="open-room" data-id="${raumId}" title="Raum öffnen">${typeIcon("room")} ${esc(app.roomName(raumId))}</button>`
            : "—"}</td>
        <td><span class="hi-status hi-status--${item.geprueft ? "geprueft" : "erfasst"}">${item.geprueft ? "Geprüft" : "Offen"}</span></td>
        <td class="hi-table__chevron">${icon("chevron-right")}</td>
    </tr>`;
}

export function renderPositionForm(app: App): string {
    const nextNummer = Math.max(0, ...app.positions.map((p) => p.nummer)) + 1;

    return `
    <div class="hi-stack" style="max-width:600px">
        <button type="button" class="hi-link-back" data-action="nav" data-view="positions">${icon("chevron-left")} Abbrechen</button>
        <div class="hi-card">
            <div class="hi-card__title">Position anlegen</div>
            <form data-form="position-create" class="hi-form">
                <div class="hi-grid-2">
                    <label class="hi-field">Position
                        <input class="hi-input" type="number" name="nummer" min="1" value="${nextNummer}">
                    </label>
                    <label class="hi-field">Raum *
                        <select class="hi-input" name="raum" required>
                            <option value="">Bitte wählen…</option>
                            ${app.rooms.map((r) => `<option value="${r.id}">${esc(r.title)}</option>`).join("")}
                        </select>
                    </label>
                </div>
                <label class="hi-field">Bezeichnung *
                    <input class="hi-input" type="text" name="title" required placeholder="z. B. Bücherregal Wohnzimmer">
                </label>
                <label class="hi-field">Beschreibung
                    <textarea class="hi-input" name="beschreibung" rows="3" placeholder="Besonderheiten dieser Position …"></textarea>
                </label>
                <div class="hi-field">Fotos
                    ${photoGrid(app.newPositionPhotos, "new-position", 0)}
                </div>
                <div class="hi-form__actions">
                    <button type="button" class="hi-btn hi-btn--ghost" data-action="nav" data-view="positions">Abbrechen</button>
                    <button type="submit" class="hi-btn">Speichern &amp; Gegenstände erfassen</button>
                </div>
            </form>
        </div>
    </div>`;
}

/* --------------------------------------------------- Gegenstand-Zeilen */

/** Sprungmarke einer Gegenstandszeile — Ziel der Links aus der Positionsliste. */
export function itemAnchorId(itemId: number): string {
    return `hi-item-${itemId}`;
}

function renderItemDisplayRow(app: App, item: Item, positionFotos: GalleryImage[] = [], focused = false): string {
    const thumb = cropThumb(item, positionFotos);
    return `
    <div class="hi-item-row${thumb ? " hi-item-row--with-crop" : ""}${focused ? " hi-item-row--focus" : ""}${
        item.duplikatStatus === "bestaetigt" ? " hi-item-row--duplicate" : ""
    }" id="${itemAnchorId(item.id)}" data-item-id="${item.id}">
        ${thumb}
        <div class="hi-item-row__head">
            <div class="hi-item-row__title">${esc(item.title)} ${idTag(item.id, "hi-cell-sub")}</div>
        </div>
        <div class="hi-item-row__main">
            <label class="hi-quick-edit" title="Anzahl direkt ändern">
                <input class="hi-quick-edit__input" type="number" min="1" step="1" value="${item.anzahl}" data-quick-edit="anzahl" data-id="${item.id}">×
            </label>
            <span>${esc(item.alter) || "—"}</span>
            <label class="hi-quick-edit" title="Wert je Stück direkt ändern">
                <input class="hi-quick-edit__input" type="text" inputmode="decimal" value="${decimalValue(item.wert)}" data-quick-edit="wert" data-id="${item.id}"> € je Stück
            </label>
            <span>${fmt(item.wert * item.anzahl)} gesamt${item.duplikatStatus === "bestaetigt" ? " (nicht gezählt)" : ""}${item.versandkosten ? ` <span class="hi-cell-sub">+ ${fmt(item.versandkosten)} Versand</span>` : ""}</span>
            <span>${item.datum ? new Date(item.datum).toLocaleDateString("de-DE") : "—"}</span>
            <span>${esc(item.quelle) || "—"}</span>
            <span>${item.fotos.length ? item.fotos.length + " Foto(s)" : ""}</span>
            <span class="hi-status hi-status--${item.geprueft ? "geprueft" : "erfasst"}">${item.geprueft ? "Geprüft" : "Offen"}</span>
        </div>
        ${sonstigesQuickEdit(item)}
        ${shopLinkList(item)}
        ${wertBegruendung(item)}
        ${duplicateBlock(app, item)}
        ${importLog(item.importLog)}
        <div class="hi-item-row__actions">
            ${geprueftToggle(item)}
            ${positionFotos.length ? iconButton({
                icon: "crop-simple",
                label: item.bildausschnitt ? "Ausschnitt ändern" : "Ausschnitt markieren",
                action: "edit-crop",
                id: item.id,
            }) : ""}
            ${iconButton({ icon: "pen", label: "Bearbeiten", action: "edit-item-inline", id: item.id })}
            ${iconButton({ icon: "trash-can", label: "Löschen", action: "delete-item", id: item.id })}
        </div>
    </div>`;
}

/**
 * "Sonstiges" direkt in der Zeile — dasselbe Schnellfeld wie bei Anzahl und
 * Wert je Stück, damit eine Notiz nicht den Umweg über die Bearbeiten-Maske
 * braucht. Gespeichert wird beim Verlassen des Feldes; Enter setzt hier einen
 * Absatz, statt zu speichern (siehe handleQuickEdit in InventoryApp.ts).
 *
 * Das Feld steht auch dann da, wenn noch nichts drin ist: Eine Notiz, die man
 * erst aufklappen muss, wird nicht geschrieben. Leer bleibt es einzeilig und
 * wächst mit dem Text mit.
 */
function sonstigesQuickEdit(item: Item): string {
    const text = item.sonstiges;
    const zeilen = text
        ? Math.min(8, Math.max(2, text.split(/\r?\n/).length, Math.ceil(text.length / 70)))
        : 1;
    return `
        <label class="hi-note-edit">
            <span class="hi-note-edit__label">${icon("note-sticky")} Sonstiges</span>
            <textarea class="hi-note-edit__input" rows="${zeilen}" data-quick-edit="sonstiges" data-id="${item.id}"
                placeholder="Seriennummer, Zustand, Besonderheiten …">${esc(text)}</textarea>
        </label>`;
}

/**
 * Font Awesome Pro (Light) — der Stylesheet wird bereits über jsidea.core
 * eingebunden, hier wird nur die Klasse gesetzt. `aria-hidden`, weil die
 * Bedeutung am Knopf selbst als aria-label steht.
 */
export function icon(name: string, extraClass = ""): string {
    return `<i class="fal fa-${name}${extraClass ? " " + extraClass : ""}" aria-hidden="true"></i>`;
}

/**
 * Schalter für den Prüfstatus. Bewusst kein Icon-Knopf wie die übrigen
 * Aktionen, weil "geprüft" ein Zustand ist und kein einmaliger Befehl —
 * der Schieber zeigt ihn direkt an.
 */
function geprueftToggle(item: Item): string {
    const label = item.geprueft ? "Als offen markieren" : "Als geprüft bestätigen";
    return `<button type="button" class="hi-toggle${item.geprueft ? " hi-toggle--on" : ""}"
        role="switch" aria-checked="${item.geprueft}"
        data-action="toggle-geprueft" data-id="${item.id}" data-value="${item.geprueft ? "0" : "1"}"
        title="${esc(label)}" aria-label="${esc(label)}"><span class="hi-toggle__knob"></span></button>`;
}

/**
 * Aktionsknopf, der nur ein Symbol zeigt. Die Bedeutung steht im Tooltip und
 * — für Screenreader — im aria-label, damit der Knopf ohne sichtbaren Text
 * bedienbar bleibt.
 */
function iconButton(o: {
    icon: string;
    label: string;
    action: string;
    /** Entfällt bei Aktionen, die sich auf die gerade geöffnete Ansicht beziehen. */
    id?: number;
    primary?: boolean;
    extra?: string;
}): string {
    const cls = `hi-btn hi-btn--icon${o.primary ? "" : " hi-btn--secondary"}`;
    const id = o.id === undefined ? "" : ` data-id="${o.id}"`;
    return `<button type="button" class="${cls}" data-action="${o.action}"${id} ${o.extra || ""} title="${esc(o.label)}" aria-label="${esc(o.label)}">${icon(o.icon)}</button>`;
}

function renderItemEditRow(app: App, item: Item): string {
    return `
    <div class="hi-item-row hi-item-row--editing">
        <form data-form="item-edit" data-id="${item.id}" class="hi-form">
            <label class="hi-field">Bezeichnung *
                <input class="hi-input" type="text" name="title" required value="${esc(item.title)}" placeholder="z. B. Ledersofa, 3-Sitzer">
            </label>
            <div class="hi-grid-3">
                <label class="hi-field">Anzahl *
                    <input class="hi-input" type="number" name="anzahl" min="1" required value="${item.anzahl}">
                </label>
                <label class="hi-field">Alter
                    <input class="hi-input" type="text" name="alter" value="${esc(item.alter)}" placeholder="z. B. ca. 3 Jahre">
                </label>
            </div>
            <div class="hi-grid-3">
                <label class="hi-field">Wert (€ je Stück) *
                    <input class="hi-input" type="text" inputmode="decimal" name="wert" required value="${decimalValue(item.wert)}">
                </label>
                ${versandkostenField(item.versandkosten)}
                <label class="hi-field">Datum
                    <input class="hi-input" type="date" name="datum" value="${esc(item.datum)}">
                </label>
                <label class="hi-field">Quelle
                    <select class="hi-input" name="quelle">
                        <option value="">—</option>
                        ${QUELLEN.map((q) => option(q, q, item.quelle)).join("")}
                    </select>
                </label>
            </div>
            ${shopLinkFields(item.shopLinks)}
            ${wertBegruendungField(item.wertBegruendung)}
            ${duplicateFields(app, item)}
            <label class="hi-field">Sonstiges
                <textarea class="hi-input" name="sonstiges" rows="2">${esc(item.sonstiges)}</textarea>
            </label>
            <div class="hi-field">Fotos &amp; Belege
                ${photoGrid(item.fotos, "item", item.id)}
            </div>
            <div class="hi-form__actions">
                <button type="button" class="hi-btn hi-btn--ghost" data-action="cancel-edit-item-inline">Abbrechen</button>
                <button type="submit" class="hi-btn">Speichern</button>
            </div>
        </form>
    </div>`;
}

function renderItemCreateRow(app: App): string {
    return `
    <div class="hi-item-row hi-item-row--editing">
        <form data-form="item-create" class="hi-form">
            <label class="hi-field">Bezeichnung *
                <input class="hi-input" type="text" name="title" required placeholder="z. B. Ledersofa, 3-Sitzer">
            </label>
            <div class="hi-grid-3">
                <label class="hi-field">Anzahl *
                    <input class="hi-input" type="number" name="anzahl" min="1" value="1" required>
                </label>
                <label class="hi-field">Alter
                    <input class="hi-input" type="text" name="alter" placeholder="z. B. ca. 3 Jahre">
                </label>
            </div>
            <div class="hi-grid-3">
                <label class="hi-field">Wert (€ je Stück) *
                    <input class="hi-input" type="text" inputmode="decimal" name="wert" required placeholder="0,00">
                </label>
                ${versandkostenField(0)}
                <label class="hi-field">Datum
                    <input class="hi-input" type="date" name="datum">
                </label>
                <label class="hi-field">Quelle
                    <select class="hi-input" name="quelle">
                        <option value="">Bitte wählen…</option>
                        ${QUELLEN.map((q) => `<option value="${q}">${q}</option>`).join("")}
                    </select>
                </label>
            </div>
            ${shopLinkFields([])}
            ${wertBegruendungField("")}
            <label class="hi-field">Sonstiges
                <textarea class="hi-input" name="sonstiges" rows="2" placeholder="Seriennummer, Zustand, Besonderheiten …"></textarea>
            </label>
            <div class="hi-field">Fotos &amp; Belege
                ${photoGrid(app.newItemPhotos, "new-item", 0)}
            </div>
            <div class="hi-form__actions">
                <button type="button" class="hi-btn hi-btn--ghost" data-action="cancel-add-item">Abbrechen</button>
                <button type="submit" class="hi-btn">Gegenstand speichern</button>
            </div>
        </form>
    </div>`;
}

export function renderPositionDetail(app: App): string {
    const position = positionOf(app, app.selectedId ?? -1);
    if (!position) return `<div class="hi-empty">Position nicht gefunden.</div>`;

    const back = `<button type="button" class="hi-link-back" data-action="nav" data-view="positions">${icon("chevron-left")} Zurück zu den Positionen</button>`;
    const items = itemsOf(app, position.id);
    const value = positionValue(app, position.id);

    if (app.editingPosition) {
        return `
        <div class="hi-stack" style="max-width:760px">
            ${back}
            <div class="hi-card">
                <form data-form="position-edit" data-id="${position.id}" class="hi-form">
                    <div class="hi-grid-2">
                        <label class="hi-field">Position
                            <input class="hi-input" type="number" name="nummer" min="1" value="${position.nummer || ""}">
                        </label>
                        <label class="hi-field">Raum *
                            <select class="hi-input" name="raum" required>
                                ${app.rooms.map((r) => `<option value="${r.id}"${r.id === position.raum ? " selected" : ""}>${esc(r.title)}</option>`).join("")}
                            </select>
                        </label>
                    </div>
                    <label class="hi-field">Bezeichnung *
                        <input class="hi-input" type="text" name="title" required value="${esc(position.title)}">
                    </label>
                    <label class="hi-field">Beschreibung
                        <textarea class="hi-input" name="beschreibung" rows="3">${esc(position.beschreibung)}</textarea>
                    </label>
                    <div class="hi-form__actions">
                        <button type="button" class="hi-btn hi-btn--ghost" data-action="cancel-edit-position">Abbrechen</button>
                        <button type="submit" class="hi-btn">Speichern</button>
                    </div>
                </form>
            </div>
        </div>`;
    }

    return `
    <div class="hi-stack" style="max-width:960px">
        ${back}
        <div class="hi-grid-detail">
            <div class="hi-stack">
                <div class="hi-card">
                    <div class="hi-detail-head">
                        <div>
                            <div class="hi-detail-title">${typeIcon("position")} ${esc(position.title)}</div>
                            <div class="hi-detail-sub">${esc(app.roomName(position.raum))} · ID ${idTag(position.id)}</div>
                        </div>
                        <div class="hi-detail-actions">
                            ${iconButton({ icon: "pen", label: "Position bearbeiten", action: "edit-position" })}
                            ${iconButton({ icon: "trash-can", label: "Position löschen", action: "delete-position", id: position.id })}
                        </div>
                    </div>
                    ${position.beschreibung ? `<p>${esc(position.beschreibung)}</p>` : ""}
                    ${importLog(position.importLog)}
                    <div class="hi-kv-grid">
                        ${kv("Position", position.nummer ? String(position.nummer) : "")}
                        ${kv("Gegenstände / Stück", countLabel(items))}
                        ${kv("Gesamtwert", `<b>${fmt(value)}</b>`)}
                        ${kv("Geprüft", ratio(items.filter((i) => i.geprueft).length, items.length))}
                    </div>
                </div>

                <div class="hi-card">
                    <div class="hi-detail-head">
                        <div class="hi-card__title" style="margin-bottom:0">Gegenstände</div>
                        ${!app.addingItem ? `<button type="button" class="hi-btn hi-btn--secondary" data-action="add-item">+ Gegenstand</button>` : ""}
                    </div>
                    <div class="hi-item-rows">
                        ${items.map((i) => (app.editingItemId === i.id ? renderItemEditRow(app, i) : renderItemDisplayRow(app, i, position.fotos, app.focusItemId === i.id))).join("") || `<div class="hi-empty">Noch keine Gegenstände erfasst.</div>`}
                        ${app.addingItem ? renderItemCreateRow(app) : ""}
                    </div>
                </div>
            </div>
            <div class="hi-card">
                <div class="hi-card__title">Fotos</div>
                ${photoGrid(position.fotos, "position", position.id)}
            </div>
        </div>
    </div>`;
}

/* --------------------------------------------------------------- Räume */

/** Kennzahlen eines Raums, gebündelt — jede Spalte der Liste braucht sie. */
function roomStats(app: App, roomId: number): { positions: Position[]; items: Item[]; stueck: number; wert: number } {
    const positions = app.positions.filter((p) => p.raum === roomId);
    const items = positions.flatMap((p) => itemsOf(app, p.id));
    return {
        positions,
        items,
        stueck: stueckzahl(items),
        wert: positions.reduce((s, p) => s + positionValue(app, p.id), 0),
    };
}

const ROOM_SORT_COLUMNS: { key: App["roomSort"]["key"]; label: string }[] = [
    { key: "id", label: "ID" },
    { key: "title", label: "Raum" },
    { key: "positions", label: "Positionen" },
    { key: "items", label: "Gegenstände" },
    { key: "stueck", label: "Stück" },
    { key: "wert", label: "Wert" },
];

function compareRooms(app: App, a: Room, b: Room, key: App["roomSort"]["key"]): number {
    const sa = roomStats(app, a.id);
    const sb = roomStats(app, b.id);
    switch (key) {
        case "id":
            return a.id - b.id;
        case "title":
            return a.title.localeCompare(b.title, "de");
        case "positions":
            return sa.positions.length - sb.positions.length;
        case "items":
            return sa.items.length - sb.items.length;
        case "stueck":
            return sa.stueck - sb.stueck;
        case "wert":
            return sa.wert - sb.wert;
        default:
            return 0;
    }
}

export function renderRooms(app: App): string {
    const allExpanded = app.rooms.length > 0 && app.rooms.every((r) => app.expandedRooms.has(r.id));

    return `
    <div class="hi-stack hi-list-view">
        ${app.roomFormOpen ? `
        <div class="hi-card">
            <form data-form="room-create" class="hi-form">
                <label class="hi-field">Raumname *
                    <input class="hi-input" type="text" name="title" required placeholder="z. B. Gästezimmer">
                </label>
                <div class="hi-form__actions">
                    <button type="button" class="hi-btn hi-btn--ghost" data-action="toggle-room-form">Abbrechen</button>
                    <button type="submit" class="hi-btn">Speichern</button>
                </div>
            </form>
        </div>` : ""}
        <div class="hi-toolbar">
            <input type="search" class="hi-input" data-room-filter="q" placeholder="Raum oder Position suchen…" value="${esc(app.roomFilter.q)}">
            <label class="hi-checkbox hi-checkbox--end">
                <input type="checkbox" data-action="toggle-all-room-positions"${allExpanded ? " checked" : ""}>
                Positionen anzeigen
            </label>
            ${virtualToggle(app, "rooms", app.rooms.length)}
        </div>
        <div class="hi-list-view__results" id="hi-room-results">${renderRoomsListResults(app)}</div>
    </div>`;
}

/** Räume nach Filter und Sortierung — Grundlage von Zeilen und Summenzeile. */
export function filteredRooms(app: App): Room[] {
    const q = app.roomFilter.q.trim();
    const { key: sortKey, dir } = app.roomSort;
    const mul = dir === "desc" ? -1 : 1;
    // Die Spalte „Positionen" zählt nicht nur, sie klappt sie auch auf — also
    // findet eine Positionsnummer oder ihr Name hier den Raum dazu.
    const positionenJeRaum = new Map<number, string>();
    if (q) {
        for (const p of app.positions) {
            positionenJeRaum.set(p.raum, `${positionenJeRaum.get(p.raum) ?? ""} ${p.nummer || ""} ${p.title}`);
        }
    }
    return app.rooms
        .filter((r) => !q || matchesId(q, r.id) || matchesFilter(q, r.title, r.beschreibung, positionenJeRaum.get(r.id) ?? ""))
        .sort((a, b) => compareRooms(app, a, b, sortKey) * mul);
}

export function renderRoomsListResults(app: App): string {
    const { key: sortKey, dir } = app.roomSort;
    const filtered = filteredRooms(app);

    if (filtered.length === 0) {
        return `<div class="hi-empty">Keine Treffer. Passen Sie die Suche an.</div>`;
    }

    const sortArrow = (key: App["roomSort"]["key"]) => (key === sortKey ? (dir === "desc" ? " ↓" : " ↑") : "");
    const gesamt = filtered.reduce((s, r) => s + roomStats(app, r.id).wert, 0);

    return `
    ${tableShell({
        app,
        virtual: "rooms",
        rows: () => roomsListRows(app),
        columns: ROOM_SORT_COLUMNS.length + 2,
        head: `
                        <th></th>
                        ${ROOM_SORT_COLUMNS.map((c) => `<th><button type="button" class="hi-sort-th" data-action="sort-rooms" data-sort-key="${c.key}">${c.label}${sortArrow(c.key)}</button></th>`).join("")}
                        <th></th>`,
    })}
    <div class="hi-list-summary">
        <span>${filtered.length} von ${app.rooms.length} Räumen</span>
        ${selectionSummary(app, "rooms", filtered.map((r) => r.id), (id) => roomStats(app, id).wert)}
        <span>Gesamtwert Auswahl: ${fmt(gesamt)}</span>
    </div>`;
}

/** Die Zeilen der Raumliste — samt aufgeklappter Positionen. */
export function roomsListRows(app: App): VirtualRow[] {
    // Sortierspalten plus Aufklapp- und Chevron-Spalte.
    const colCount = ROOM_SORT_COLUMNS.length + 2;

    return filteredRooms(app).map((r): VirtualRow => {
                        const expanded = app.expandedRooms.has(r.id);
                        const s = roomStats(app, r.id);
                        return { key: r.id, hoehe: rowEstimate(expanded, s.positions.length), html: `
                        <tr data-action="open-room" data-id="${r.id}"${selectedRowAttr(app, "rooms", r.id)}>
                            <td><button type="button" class="hi-expand-toggle${expanded ? " hi-expand-toggle--open" : ""}" data-action="toggle-room-positions" data-id="${r.id}" title="Positionen ${expanded ? "verbergen" : "anzeigen"}" aria-label="Positionen ${expanded ? "verbergen" : "anzeigen"}">${icon("chevron-right")}</button></td>
                            <td class="c-num">${idTag(r.id)}</td>
                            <td><div class="hi-cell-title">${typeIcon("room")} ${esc(r.title)}</div></td>
                            <td>${s.positions.length}</td>
                            <td>${s.items.length}</td>
                            <td>${s.stueck}</td>
                            <td>${fmt(s.wert)}</td>
                            <td class="hi-table__chevron">${icon("chevron-right")}</td>
                        </tr>
                        ${expanded ? `
                        <tr class="hi-table__subrow">
                            <td colspan="${colCount}">
                                ${s.positions.length ? `
                                <div class="hi-subitems">
                                    ${[...s.positions].sort((a, b) => a.nummer - b.nummer).map((p) => {
                                        const children = itemsOf(app, p.id);
                                        return `
                                        <button type="button" class="hi-subitem" data-action="open-position" data-id="${p.id}" title="Position ${esc(p.title)} öffnen">
                                            <span class="hi-subitem__title">${typeIcon("position")} ${p.nummer ? `${p.nummer} · ` : ""}${esc(p.title)}</span>
                                            <span>${children.length} Gegenstände</span>
                                            <span>${stueckzahl(children)} Stück</span>
                                            <span><b>${fmt(positionValue(app, p.id))}</b> gesamt</span>
                                            <span class="hi-subitem__chevron">${icon("chevron-right")}</span>
                                        </button>`;
                                    }).join("")}
                                </div>` : `<div class="hi-empty" style="padding:var(--hi-space-3)">Noch keine Positionen in diesem Raum.</div>`}
                            </td>
                        </tr>` : ""}` };
    });
}

export function renderRoomDetail(app: App): string {
    const room = app.rooms.find((r) => r.id === app.selectedId);
    if (!room) return `<div class="hi-empty">Raum nicht gefunden.</div>`;

    const back = `<button type="button" class="hi-link-back" data-action="nav" data-view="rooms">${icon("chevron-left")} Zurück zu den Räumen</button>`;
    const roomPositions = app.positions.filter((p) => p.raum === room.id);
    const value = roomPositions.reduce((s, p) => s + positionValue(app, p.id), 0);

    if (app.editingRoom) {
        return `
        <div class="hi-stack" style="max-width:920px">
            ${back}
            <div class="hi-card">
                <form data-form="room-edit" data-id="${room.id}" class="hi-form">
                    <label class="hi-field">Raumname *
                        <input class="hi-input" type="text" name="title" required value="${esc(room.title)}">
                    </label>
                    <label class="hi-field">Beschreibung
                        <textarea class="hi-input" name="beschreibung" rows="3">${esc(room.beschreibung)}</textarea>
                    </label>
                    <div class="hi-form__actions">
                        <button type="button" class="hi-btn hi-btn--ghost" data-action="cancel-edit-room">Abbrechen</button>
                        <button type="submit" class="hi-btn">Speichern</button>
                    </div>
                </form>
            </div>
        </div>`;
    }

    return `
    <div class="hi-stack" style="max-width:920px">
        ${back}
        <div class="hi-grid-detail">
            <div class="hi-card">
                <div class="hi-detail-head">
                    <div>
                        <div class="hi-detail-title">${typeIcon("room")} ${esc(room.title)}</div>
                        <div class="hi-detail-sub">${roomPositions.length} Positionen · ${fmt(value)} · ID ${idTag(room.id)}</div>
                    </div>
                    <div class="hi-detail-actions">
                        ${iconButton({ icon: "pen", label: "Raum bearbeiten", action: "edit-room" })}
                        ${iconButton({ icon: "trash-can", label: "Raum löschen", action: "delete-room", id: room.id })}
                    </div>
                </div>
                <p>${esc(room.beschreibung) || "Keine Beschreibung hinterlegt."}</p>
                <div class="hi-card__title">Raumfotos</div>
                ${photoGrid(room.fotos, "room", room.id)}
            </div>
            <div class="hi-card">
                <div class="hi-card__title">Positionen in diesem Raum</div>
                <div class="hi-stats hi-stats--2">
                    <div class="hi-stat"><div class="hi-stat__value">${roomPositions.length}</div><div class="hi-stat__label">Positionen</div></div>
                    <div class="hi-stat"><div class="hi-stat__value">${fmt(value)}</div><div class="hi-stat__label">Gesamtwert</div></div>
                </div>
                ${roomPositions.slice(0, 5).map((p) => `<div class="hi-list-summary" data-action="open-position" data-id="${p.id}"><span>${esc(p.title)}</span><span>${fmt(positionValue(app, p.id))}</span></div>`).join("")}
                <button type="button" class="hi-btn hi-btn--ghost" data-action="open-positions-for-room" data-id="${room.id}">In der Liste öffnen (gefiltert)</button>
            </div>
        </div>
    </div>`;
}

/* ------------------------------------------------- Duplikat-Prüfung */

/**
 * Ein Gegenstand als Prüfkarte: Ausschnitt, Fundort, Wert und die
 * Herkunftsdatei des Fotos.
 *
 * Der Dateiname steht bewusst dabei — beim Foto-Import ist er das
 * entscheidende Indiz: Zwei Aufnahmen derselben Sache stammen fast immer aus
 * unmittelbar aufeinanderfolgenden Dateien, zwei verschiedene Exemplare
 * dagegen selten.
 */
function duplicatePanel(app: App, item: Item, role: string, actions = ""): string {
    const position = app.positions.find((p) => p.id === item.position);
    const room = position ? app.rooms.find((r) => r.id === position.raum) : undefined;
    const thumb = position ? cropThumb(item, position.fotos) : "";
    const quelle = position?.fotos.find((f) => f.ID === item.bildausschnitt?.media);
    const istDuplikat = item.duplikatStatus === "bestaetigt";

    // Jede Angabe eine eigene Grid-Zeile: Über subgrid liegen Bild, Titel,
    // Fundort und Zahlen aller Einträge eines Falls auf derselben Höhe — sonst
    // vergleicht man Titel mit Fundort, sobald ein Text zweizeilig umbricht.
    return `
    <div class="hi-dup-panel${istDuplikat ? " hi-dup-panel--is-duplicate" : ""}">
        <div class="hi-dup-panel__role">${esc(role)}</div>
        <div class="hi-dup-panel__thumb">${thumb || `<span class="hi-dup-ref__nothumb">${icon("image-slash")}</span>`}</div>
        <div class="hi-dup-panel__title">${typeIcon("item")} ${esc(item.title)} ${idTag(item.id, "hi-dup-panel__id")}</div>
        <div class="hi-dup-panel__where">${typeIcon("position")} ${esc(refLocation({ item, position, room }) || "ohne Position")}</div>
        <div class="hi-dup-panel__meta">${item.anzahl}× · ${fmt(item.wert)} je Stück · <b>${fmt(item.wert * item.anzahl)}</b> gesamt${istDuplikat ? ` · <span class="hi-dup-panel__struck">zählt nicht</span>` : ""}</div>
        <div class="hi-dup-panel__file">${quelle ? `${icon("camera")} ${esc(quelle.filename)}` : ""}</div>
        <div class="hi-dup-panel__actions">
            ${actions}
            ${position ? `<button type="button" class="hi-btn hi-btn--ghost" data-action="open-duplicate" data-id="${position.id}" data-item-id="${item.id}">${icon("arrow-right")} In der Position öffnen</button>` : ""}
        </div>
    </div>`;
}

/**
 * Prüfansicht für alle Duplikat-Verdachte.
 *
 * Nebeneinander statt untereinander: Der Verdacht lässt sich nur ausräumen,
 * wenn beide Ausschnitte gleichzeitig zu sehen sind — genau das ist beim
 * Blättern zwischen zwei Positionen nicht möglich.
 */
export function renderDuplicates(app: App): string {
    const groups = duplicateGroups(app);
    if (groups.length === 0) {
        return `<div class="hi-empty">Kein Duplikat-Verdacht vermerkt. Beim Foto-Import markierte Doppelungen erscheinen hier zur Prüfung.</div>`;
    }
    const offen = groups.filter((g) => g.status === "moeglich").length;
    const entschieden = groups.filter((g) => g.status === "bestaetigt");
    // Abzug über die Gegenstände, nicht die Fälle: Gezählt wird, was wirklich
    // aus der Summe fällt — das sind genau die bestätigten Einträge.
    const abzug = app.items
        .filter((i) => i.duplikatStatus === "bestaetigt")
        .reduce((s, i) => s + i.wert * i.anzahl, 0);

    /**
     * „Ist das Duplikat" je Eintrag, als Umschalter.
     *
     * Gesperrt ist nur der Fall, in dem sonst *alle* Einträge der Gruppe aus
     * der Summe fielen — dann wäre die Sache gar nicht mehr versichert. Einer
     * muss immer stehen bleiben.
     */
    const markButton = (group: DuplicateGroup, item: Item) => {
        if (group.items.length < 2) return "";
        const aktiv = item.duplikatStatus === "bestaetigt";
        const verbleibend = group.items.filter((i) => i.duplikatStatus !== "bestaetigt").length;
        const gesperrt = !aktiv && verbleibend <= 1;
        const ids = group.items.map((i) => i.id).join(",");
        return `
        <button type="button" class="hi-btn${aktiv ? "" : " hi-btn--secondary"} hi-dup-panel__mark"${gesperrt ? " disabled" : ""}
            data-action="toggle-duplicate-mark" data-id="${item.id}" data-group="${ids}"
            title="${gesperrt ? "Mindestens ein Eintrag muss gezählt bleiben — sonst fällt die Sache ganz aus der Aufstellung." : "Diesen Eintrag als Duplikat markieren: Er bleibt erhalten, zählt aber in keiner Summe mehr."}">
            ${icon(aktiv ? "ban" : "circle")} ${aktiv ? "Ist das Duplikat" : "Ist das Duplikat"}
        </button>`;
    };

    const caseButton = (group: DuplicateGroup, mode: string, label: string, ico: string, aktiv: boolean) => `
        <button type="button" class="hi-btn${aktiv ? "" : " hi-btn--secondary"}"
            data-action="set-duplicate-group" data-group="${group.items.map((i) => i.id).join(",")}"
            data-mode="${mode}"${aktiv ? ` title="Nochmal klicken nimmt die Entscheidung zurück"` : ""}>${icon(ico)} ${esc(label)}</button>`;

    return `
    <div class="hi-stack">
        <div class="hi-stats hi-stats--3">
            <div class="hi-stat"><div class="hi-stat__value">${offen}</div><div class="hi-stat__label">Ungeprüfte Fälle</div></div>
            <div class="hi-stat"><div class="hi-stat__value">${entschieden.length}</div><div class="hi-stat__label">Fälle mit Duplikat</div></div>
            <div class="hi-stat"><div class="hi-stat__value">${fmt(abzug)}</div><div class="hi-stat__label">Nicht in der Summe</div></div>
        </div>
        ${groups.map((group) => {
            const mehrere = group.items.length > 1;
            const markiert = group.items.filter((i) => i.duplikatStatus === "bestaetigt").length;
            return `
        <div class="hi-card hi-dup-case hi-dup-case--${group.status}">
            <div class="hi-dup-case__head">
                <span class="hi-dup__badge">${icon(DUPLICATE_ICONS[(group.status || "moeglich") as Exclude<DuplicateStatus, "">])} ${esc(duplicateLabel(group.status))}</span>
                ${mehrere ? `<span class="hi-dup-case__count">${group.items.length} Einträge${markiert ? ` · ${markiert} als Duplikat markiert` : ""}</span>` : ""}
                <div class="hi-dup-case__actions">
                    ${caseButton(
                        group,
                        // Steht die Entscheidung schon, nimmt derselbe Knopf sie
                        // zurück — sonst wäre "Kein Duplikat" eine Sackgasse,
                        // aus der nur noch "Verdacht löschen" herausführt, und
                        // das wirft den Verdacht ganz weg.
                        group.status === "kein_duplikat" ? "reopen" : "none",
                        "Kein Duplikat",
                        "circle-check",
                        group.status === "kein_duplikat"
                    )}
                    ${caseButton(group, "clear", "Verdacht löschen", "xmark", false)}
                </div>
            </div>
            ${mehrere
                ? `<p class="hi-dup-case__hint">Markieren Sie die Einträge, die aus der Summe fallen sollen. Mindestens einer muss gezählt bleiben — sonst fehlt die Sache ganz in der Aufstellung.</p>`
                : ""}
            <div class="hi-dup-case__panels">
                ${group.items
                    .map((item, i) => duplicatePanel(app, item, `Eintrag ${String.fromCharCode(65 + i)}`, markButton(group, item)))
                    .join("")}
            </div>
            ${group.missing.length
                ? `<div class="hi-dup-panel__missing">${icon("triangle-exclamation")} Verweis ohne auffindbares Ziel: ${esc(group.missing.join(" · "))}</div>`
                : ""}
            ${group.notes.length
                ? `<div class="hi-dup-case__notes">${group.notes.map((n) => `<div class="hi-dup-case__note">${icon("quote-left")} ${esc(n)}</div>`).join("")}</div>`
                : ""}
        </div>`;
        }).join("")}
    </div>`;
}

/* ------------------------------------------------- Import-Hinweise */

const NOTE_LABELS: Record<NoteLevel, string> = {
    kritisch: "Kritisch",
    warnung: "Warnung",
    hinweis: "Hinweis",
};

const NOTE_ICONS: Record<NoteLevel, string> = {
    kritisch: "circle-exclamation",
    warnung: "triangle-exclamation",
    hinweis: "circle-info",
};

/** Reihenfolge der Dringlichkeit — auch die Sortierung der Liste. */
const NOTE_ORDER: NoteLevel[] = ["kritisch", "warnung", "hinweis"];

export function noteLabel(level: NoteLevel): string {
    return NOTE_LABELS[level] ?? NOTE_LABELS.hinweis;
}

/** Offene (nicht erledigte) Hinweise — die Zahl im Menü. */
export function openNoteCount(app: App): number {
    return app.notes.filter((n) => !n.erledigt).length;
}

/**
 * Hinweise nach Filter und Dringlichkeit.
 *
 * Erledigte stehen immer am Ende, nie zwischen den offenen: Sie sind Nachweis,
 * keine Aufgabe.
 */
function filteredNotes(app: App): Note[] {
    const { level, q, state } = app.noteFilter;
    const suche = q.trim().toLocaleLowerCase("de");
    return app.notes
        .filter((n) => (level ? n.level === level : true))
        .filter((n) => (state === "offen" ? !n.erledigt : state === "erledigt" ? n.erledigt : true))
        .filter((n) =>
            suche
                ? `${n.title} ${n.text} ${n.imageKey}`.toLocaleLowerCase("de").includes(suche)
                : true
        )
        .sort(
            (a, b) =>
                Number(a.erledigt) - Number(b.erledigt) ||
                NOTE_ORDER.indexOf(a.level) - NOTE_ORDER.indexOf(b.level) ||
                a.order - b.order ||
                a.id - b.id
        );
}

/** Die Sprungziele eines Hinweises als Knöpfe — nur die gesetzten. */
function noteTargets(app: App, note: Note): string {
    const ziele: string[] = [];

    if (note.item) {
        const item = app.items.find((i) => i.id === note.item);
        const pos = item ? app.positions.find((p) => p.id === item.position) : undefined;
        // Ein Gegenstand hat keine eigene Ansicht — er wird in seiner Position
        // angesprungen und dort markiert (wie aus der Suche).
        if (item && pos) {
            ziele.push(`
            <button type="button" class="hi-btn hi-btn--ghost hi-btn--sm" data-action="open-search-hit"
                data-view="position" data-id="${pos.id}" data-item-id="${item.id}">
                ${typeIcon("item")} ${esc(item.title)}
            </button>`);
        }
    }
    if (note.position) {
        const pos = app.positions.find((p) => p.id === note.position);
        if (pos) {
            ziele.push(`
            <button type="button" class="hi-btn hi-btn--ghost hi-btn--sm" data-action="open-search-hit"
                data-view="position" data-id="${pos.id}">
                ${typeIcon("position")} ${pos.nummer ? `Pos. ${pos.nummer}` : "Position"} · ${esc(pos.title)}
            </button>`);
        }
    }
    if (note.raum) {
        const room = app.rooms.find((r) => r.id === note.raum);
        if (room) {
            ziele.push(`
            <button type="button" class="hi-btn hi-btn--ghost hi-btn--sm" data-action="open-search-hit"
                data-view="room" data-id="${room.id}">
                ${typeIcon("room")} ${esc(room.title)}
            </button>`);
        }
    }
    return ziele.join("");
}

/**
 * Ansicht „Import-Hinweise".
 *
 * Was hier steht, stand früher nur im Abschlussbericht des Chats und war nach
 * der Sitzung verloren — offene Preisschilder, ein Möbel, das auf keinem Foto
 * vollständig zu sehen ist, eine ungeklärte Raumfrage. Die `import_log`-Felder
 * an Position und Gegenstand können das nicht auffangen: Sie erklären einen
 * einzelnen Datensatz, während es hier um das geht, was zwischen ihnen liegt.
 */
export function renderNotes(app: App): string {
    if (app.notes.length === 0 && app.editingNoteId !== -1) {
        return `
        <div class="hi-empty">
            <p>Keine Import-Hinweise. Der Foto-Import legt hier ab, was er nicht abschließend klären konnte — offene Preisschilder, angeschnittene Möbel, ungeklärte Raumnamen.</p>
            <button type="button" class="hi-btn hi-btn--secondary" data-action="note-add">${icon("plus")} Hinweis von Hand anlegen</button>
        </div>`;
    }
    const zaehler = (level: NoteLevel) => app.notes.filter((n) => n.level === level && !n.erledigt).length;
    const erledigt = app.notes.filter((n) => n.erledigt).length;

    const filterButton = (value: string, label: string, count: number) => `
        <button type="button" class="hi-chip${app.noteFilter.level === value ? " hi-chip--active" : ""}"
            data-action="filter-notes" data-level="${value}">${esc(label)}${count ? ` <span class="hi-chip__count">${count}</span>` : ""}</button>`;

    return `
    <div class="hi-stack">
        <div class="hi-stats hi-stats--3">
            <div class="hi-stat"><div class="hi-stat__value">${zaehler("kritisch")}</div><div class="hi-stat__label">Kritisch</div></div>
            <div class="hi-stat"><div class="hi-stat__value">${zaehler("warnung")}</div><div class="hi-stat__label">Warnungen</div></div>
            <div class="hi-stat"><div class="hi-stat__value">${erledigt} / ${app.notes.length}</div><div class="hi-stat__label">Erledigt</div></div>
        </div>

        <div class="hi-card hi-card--flush">
            <div class="hi-toolbar">
                <div class="hi-chips">
                    ${filterButton("", "Alle", 0)}
                    ${NOTE_ORDER.map((l) => filterButton(l, NOTE_LABELS[l], zaehler(l))).join("")}
                </div>
                <select class="hi-input hi-input--slim" data-note-filter="state">
                    <option value="offen"${app.noteFilter.state === "offen" ? " selected" : ""}>Nur offene</option>
                    <option value=""${app.noteFilter.state === "" ? " selected" : ""}>Offene und erledigte</option>
                    <option value="erledigt"${app.noteFilter.state === "erledigt" ? " selected" : ""}>Nur erledigte</option>
                </select>
                <input class="hi-input hi-input--slim" type="search" data-note-filter="q"
                    value="${esc(app.noteFilter.q)}" placeholder="Hinweise durchsuchen …" aria-label="Hinweise durchsuchen">
                <button type="button" class="hi-btn hi-btn--secondary" data-action="note-add">${icon("plus")} Hinweis</button>
            </div>

            <div id="hi-note-results">${renderNotesResults(app)}</div>
        </div>
    </div>`;
}

/**
 * Nur der Ergebnisbereich — getrennt, damit die Textsuche ihn austauschen
 * kann, ohne die ganze Ansicht neu zu bauen: Ein render() würde dem Suchfeld
 * mitten im Tippen den Fokus nehmen.
 */
export function renderNotesResults(app: App): string {
    const gefiltert = filteredNotes(app);
    // Was der Filter gerade wegnimmt, muss dastehen. Ein abgehakter Hinweis
    // verschwindet sonst im selben Moment aus der Liste, in dem man ihn
    // abhakt — das sieht aus, als wäre er gelöscht worden.
    const versteckt = app.notes.length - gefiltert.length;
    const nurOffen = app.noteFilter.state === "offen";
    const erledigt = app.notes.filter((n) => n.erledigt).length;
    const hinweisZeile =
        versteckt > 0
            ? `<div class="hi-note-hidden">
                ${icon("eye-slash")} ${versteckt} ${versteckt === 1 ? "Hinweis ist" : "Hinweise sind"} ausgeblendet${nurOffen && erledigt ? ` (davon ${erledigt} erledigt)` : ""}.
                <button type="button" class="hi-btn hi-btn--ghost hi-btn--sm" data-action="notes-show-all">${icon("eye")} Alle anzeigen</button>
            </div>`
            : "";

    if (gefiltert.length === 0 && app.editingNoteId !== -1) {
        return `<div class="hi-empty">Kein Hinweis passt zum Filter.${hinweisZeile}</div>`;
    }
    return `
    <div class="hi-table-scroll">
        <table class="hi-table hi-table--notes">
            <thead>
                <tr>
                    <th>Wichtigkeit</th>
                    <th>Hinweis</th>
                    <th>Betrifft</th>
                    <th>Herkunft</th>
                    <th class="c-num">Erledigt</th>
                </tr>
            </thead>
            <tbody>
                ${app.editingNoteId === -1 ? noteForm(app, null) : ""}
                ${gefiltert.map((note) => noteRow(app, note)).join("")}
            </tbody>
        </table>
    </div>
    ${hinweisZeile}`;
}

/**
 * Bearbeiten-Formular eines Hinweises, direkt in der Tabellenzeile.
 *
 * Die drei Verweise sind Auswahllisten statt Suchfelder: Ein Hinweis entsteht
 * fast immer beim Import und trägt seine Bezüge schon; von Hand ergänzt wird
 * hier eher selten, und dann genügt die Liste.
 */
function noteForm(app: App, note: Note | null): string {
    const n: Note = note ?? {
        id: 0, title: "", order: 0, level: "hinweis", text: "",
        erledigt: false, imageKey: "", raum: 0, position: 0, item: 0,
    };
    const posLabel = (p: Position) => `${p.nummer ? `Pos. ${p.nummer} · ` : ""}${p.title}`;

    return `
    <tr class="hi-note-row hi-note-row--form">
        <td colspan="5">
            <form class="hi-form" data-form="note-save"${note ? ` data-id="${note.id}"` : ""}>
                <div class="hi-grid-3">
                    <label class="hi-field">Wichtigkeit *
                        <select class="hi-input" name="wichtigkeit" required>
                            ${NOTE_ORDER.map((l) => `<option value="${l}"${l === n.level ? " selected" : ""}>${esc(NOTE_LABELS[l])}</option>`).join("")}
                        </select>
                    </label>
                    <label class="hi-field">Herkunftsbild
                        <input class="hi-input" type="text" name="image_key" value="${esc(n.imageKey)}" placeholder="Dateiname ohne Endung">
                    </label>
                    <label class="hi-field hi-field--check">
                        <input type="checkbox" name="erledigt"${n.erledigt ? " checked" : ""}> Erledigt
                    </label>
                </div>
                <label class="hi-field">Kurzfassung *
                    <input class="hi-input" type="text" name="title" required value="${esc(n.title)}"
                        placeholder="z. B. Preisschilder auf Pos. 22 nicht lesbar">
                </label>
                <label class="hi-field">Hinweis
                    <p class="hi-field__hint">Vollständige Sätze — der Hinweis muss Monate später ohne den Chatverlauf verständlich sein.</p>
                    <textarea class="hi-input" name="text" rows="3">${esc(n.text)}</textarea>
                </label>
                <div class="hi-grid-3">
                    <label class="hi-field">Raum
                        <select class="hi-input" name="raum">
                            <option value="0">—</option>
                            ${app.rooms.map((r) => `<option value="${r.id}"${r.id === n.raum ? " selected" : ""}>${esc(r.title)}</option>`).join("")}
                        </select>
                    </label>
                    <label class="hi-field">Position
                        <select class="hi-input" name="position">
                            <option value="0">—</option>
                            ${app.positions.map((p) => `<option value="${p.id}"${p.id === n.position ? " selected" : ""}>${esc(posLabel(p))}</option>`).join("")}
                        </select>
                    </label>
                    <label class="hi-field">Gegenstand
                        <select class="hi-input" name="item">
                            <option value="0">—</option>
                            ${app.items.map((i) => `<option value="${i.id}"${i.id === n.item ? " selected" : ""}>#${i.id} ${esc(i.title)}</option>`).join("")}
                        </select>
                    </label>
                </div>
                <div class="hi-form__actions">
                    <button type="submit" class="hi-btn">${icon("check")} Speichern</button>
                    <button type="button" class="hi-btn hi-btn--secondary" data-action="note-cancel">Abbrechen</button>
                </div>
            </form>
        </td>
    </tr>`;
}

function noteRow(app: App, note: Note): string {
    if (app.editingNoteId === note.id) return noteForm(app, note);
    const ziele = noteTargets(app, note);
    return `
    <tr class="hi-note-row hi-note-row--${note.level}${note.erledigt ? " hi-note-row--done" : ""}" data-id="${note.id}">
        <td>
            <span class="hi-note-level hi-note-level--${note.level}">${icon(NOTE_ICONS[note.level])} ${esc(NOTE_LABELS[note.level])}</span>
        </td>
        <td>
            <div class="hi-note-row__title">${esc(note.title)} ${idTag(note.id, "hi-cell-sub")}</div>
            ${note.text ? `<div class="hi-note-row__text">${esc(note.text)}</div>` : ""}
        </td>
        <td>${ziele ? `<div class="hi-note-row__targets">${ziele}</div>` : "—"}</td>
        <td>${note.imageKey ? `<span class="hi-note-row__file">${icon("camera")} ${esc(note.imageKey)}</span>` : "—"}</td>
        <td class="c-num">
            <button type="button" class="hi-btn hi-btn--icon${note.erledigt ? "" : " hi-btn--secondary"}"
                data-action="note-toggle" data-id="${note.id}"
                title="${note.erledigt ? "Wieder als offen markieren" : "Als erledigt abhaken"}"
                aria-label="${note.erledigt ? "Wieder als offen markieren" : "Als erledigt abhaken"}">${icon(note.erledigt ? "circle-check" : "circle")}</button>
            <button type="button" class="hi-btn hi-btn--icon hi-btn--secondary" data-action="note-edit" data-id="${note.id}"
                title="Hinweis bearbeiten" aria-label="Hinweis bearbeiten">${icon("pen")}</button>
            <button type="button" class="hi-btn hi-btn--icon hi-btn--secondary" data-action="note-delete" data-id="${note.id}"
                title="Hinweis löschen" aria-label="Hinweis löschen">${icon("trash")}</button>
        </td>
    </tr>`;
}

/* -------------------------------------------------------------- Export */

export function renderExport(app: App): string {
    const items = app.items;
    const total = items.reduce((s, i) => s + itemValue(i), 0);
    const formats: { key: string; label: string; desc: string }[] = [
        { key: "pdf", label: "PDF-Dokument", desc: "Formatierte Liste für die Versicherung" },
        { key: "xlsx", label: "Excel (.xlsx)", desc: "Zum Weiterbearbeiten" },
        { key: "csv", label: "CSV", desc: "Rohdaten, semikolon-getrennt" },
    ];

    return `
    <div class="hi-export-grid">
        <div class="hi-card">
            <div class="hi-card__title">Liste exportieren</div>
            <div class="hi-format-list">
                ${formats.map((f) => `
                    <button type="button" class="hi-format${app.exportFormat === f.key ? " hi-format--active" : ""}" data-action="set-export-format" data-format="${f.key}">
                        <div><div class="hi-format__label">${f.label}</div><div class="hi-format__desc">${f.desc}</div></div>
                    </button>`).join("")}
            </div>
            <div class="hi-export-options">
            <label class="hi-field">Welche Gegenstände
                <select class="hi-input" data-action="set-export-scope">
                    <option value="alle"${app.exportScope === "alle" ? " selected" : ""}>Alle Gegenstände</option>
                    <option value="geprueft"${app.exportScope === "geprueft" ? " selected" : ""}>Nur bereits geprüfte Gegenstände</option>
                </select>
                <span class="hi-format__desc">${app.exportScope === "geprueft"
                    ? "Noch offene Gegenstände bleiben außen vor."
                    : "Auch Gegenstände, die im Prüf-Assistenten noch offen sind."}</span>
            </label>
            ${app.exportFormat === "pdf" ? `
            <label class="hi-field">Versicherungsnehmer
                <input class="hi-input" type="text" data-action="set-export-owner" value="${esc(app.exportOwner)}" placeholder="Vor- und Nachname">
                <span class="hi-format__desc">Steht im Kopf jedes Blattes, über der Unterschriftszeile am Fuß.</span>
            </label>
            <div class="hi-grid-2">
                <label class="hi-field">Ort
                    <input class="hi-input" type="text" data-action="set-export-place" value="${esc(app.exportPlace)}" placeholder="z. B. Bremen">
                </label>
                <label class="hi-field">Datum
                    <input class="hi-input" type="date" data-action="set-export-date" value="${esc(app.exportDate)}">
                </label>
            </div>
            <span class="hi-format__desc">Erscheinen über der Zeile „Ort, Datum" am Fuß jedes Blattes. Ohne Datum wird der heutige Tag eingesetzt (${esc(formatExportDate(""))}).</span>` : ""}
            ${app.exportFormat === "xlsx" ? `
            <p class="hi-format__desc">Die Excel-Liste enthält eine Zeile je Gegenstand mit eigener Raum-Spalte. Gliederung und Zusammenfassen entfallen deshalb — sortieren und filtern übernimmt Excel.</p>
            <label class="hi-checkbox">
                <input type="checkbox" data-action="set-export-embed-images"${app.exportEmbedImages ? " checked" : ""}>
                Fotos der Positionen in die Datei einbetten
            </label>
            <span class="hi-format__desc">Macht die Datei deutlich größer, dafür ist alles in einem Dokument.</span>` : `
            <label class="hi-field">Gliederung nach Räumen
                <select class="hi-input" data-action="set-export-room-mode">
                    <option value="page"${app.exportRoomMode === "page" ? " selected" : ""}>Raum im Seitenkopf, je Raum neue Seite</option>
                    <option value="row"${app.exportRoomMode === "row" ? " selected" : ""}>Raum als Zwischenzeile in der Tabelle</option>
                    <option value="none"${app.exportRoomMode === "none" ? " selected" : ""}>Ohne Raumangabe, durchgehende Liste</option>
                </select>
                <span class="hi-format__desc">${{
                    page: "Die von Versicherungen übliche Form: Jeder Raum beginnt auf einem neuen Blatt, sein Name steht oben unter dem Versicherungsnehmer. Innerhalb eines Raums stehen die Positionen nach Nummer.",
                    row: "Alle Räume laufen durch; ein neuer Raum wird nur durch eine hervorgehobene Zeile in der Tabelle angekündigt. Innerhalb eines Raums stehen die Positionen nach Nummer.",
                    none: "Eine einzige fortlaufende Liste, streng nach Positionsnummer von der ersten bis zur letzten. Die Räume werden nicht getrennt, und im Seitenkopf steht keine Raumzeile.",
                }[app.exportRoomMode]}</span>
            </label>
            <label class="hi-field">Euro-Spalte
                <select class="hi-input" data-action="set-export-value-mode">
                    <option value="gesamt"${app.exportValueMode === "gesamt" ? " selected" : ""}>Gesamtwert der Zeile (Anzahl × Stückpreis)</option>
                    <option value="stueck"${app.exportValueMode === "stueck" ? " selected" : ""}>Preis je Stück</option>
                </select>
                <span class="hi-format__desc">${app.exportValueMode === "gesamt"
                    ? "Bei 25 Shirts zu 15 € steht 375 € in der Spalte."
                    : "Bei 25 Shirts zu 15 € steht 15 € in der Spalte; die Anzahl steht daneben."}</span>
            </label>
            <label class="hi-checkbox">
                <input type="checkbox" data-action="set-export-group-items"${app.exportGroupItems ? " checked" : ""}>
                Positionsnummer je Position nur einmal nennen
            </label>
            <span class="hi-format__desc">${app.exportGroupItems
                ? "Die Nummer steht einmal links und gilt für alle Gegenstände der Position. Jeder Gegenstand behält seine eigene Zeile."
                : "Jede Zeile wiederholt die Positionsnummer."}</span>`}
            ${app.exportFormat === "pdf" ? `
            <label class="hi-checkbox">
                <input type="checkbox" data-action="set-export-show-alter"${app.exportShowAlter ? " checked" : ""}>
                Spalte „Alter“ mit ausgeben
            </label>
            <span class="hi-format__desc">${app.exportShowAlter
                ? "Die Versicherung ermittelt aus dem Alter den Zeitwert. Der Wert in der Euro-Spalte bleibt davon unberührt — das ist der Neuwert."
                : "Die Aufstellung zeigt nur die Wiederbeschaffungswerte; der frei werdende Platz geht an die Spalte „Sonstiges“."}</span>` : ""}
            ${app.exportFormat === "pdf" ? `
            <label class="hi-checkbox">
                <input type="checkbox" data-action="set-export-intro"${app.exportIntro ? " checked" : ""}>
                Erläuterung voranstellen
            </label>
            ${app.exportIntro ? `
            <label class="hi-field">
                <textarea class="hi-input" rows="14" data-action="set-export-intro-text">${esc(app.exportIntroText)}</textarea>
                <span class="hi-format__desc">Steht auf eigenen Blättern vor der Liste und wird bei Bedarf über mehrere Seiten umbrochen. Ein „# " am Zeilenanfang setzt eine Zwischenüberschrift, eine Leerzeile trennt Absätze. Änderungen werden gemerkt.</span>
            </label>
            <div class="hi-form__actions">
                <button type="button" class="hi-btn hi-btn--secondary" data-action="reset-export-intro">Ursprünglichen Text wiederherstellen</button>
            </div>` : `
            <span class="hi-format__desc">Ein Vorspann, der erklärt, worauf die Beträge beruhen, was in der Alters-Spalte steht und wie mit mehrfach abgebildeten Gegenständen verfahren wurde. Ohne Anforderung durch die Versicherung meist entbehrlich.</span>`}` : ""}
            ${app.exportFormat === "pdf" ? `
            <label class="hi-checkbox">
                <input type="checkbox" data-action="set-export-signature-last"${app.exportSignatureLastPage ? " checked" : ""}>
                Unterschrift nur auf dem letzten Blatt
            </label>
            <span class="hi-format__desc">${app.exportSignatureLastPage
                ? "Ort, Datum und Unterschrift stehen einmal am Ende der Aufstellung."
                : "Jedes Blatt bekommt eine eigene Unterschriftszeile — bei vielen Seiten entsprechend oft zu unterschreiben."}</span>` : ""}
            ${app.exportFormat === "pdf" ? `
            <div class="hi-callout hi-callout--info hi-callout--compact">
                <b>Einstellungen im Druckdialog</b>
                <ul>
                    <li>Papierformat <b>A4</b>, Ausrichtung <b>Hochformat</b></li>
                    <li>Ränder auf <b>Keine</b> — die Seitenränder bringt das Dokument selbst mit</li>
                    <li><b>Kopf- und Fußzeilen abschalten</b>, sonst druckt der Browser URL und Datum darüber</li>
                    <li><b>Hintergrundgrafiken</b> aktivieren, damit die Kopfzeilen der Tabelle grau hinterlegt bleiben</li>
                    <li>Skalierung auf <b>100 %</b> bzw. <b>Standard</b>, nicht „An Seite anpassen"</li>
                </ul>
            </div>` : ""}
            </div>
            <div class="hi-form__actions">
                <button type="button" class="hi-btn" data-action="run-export">Exportieren</button>
            </div>
        </div>
        <div class="hi-card hi-card--sunken">
            <div class="hi-card__title">Zusammenfassung</div>
            <div class="hi-list-summary"><span>Positionen</span><b>${app.positions.length}</b></div>
            <div class="hi-list-summary"><span>Gegenstände</span><b>${items.length}</b></div>
            <div class="hi-list-summary"><span>Geprüft</span><b>${items.filter((i) => i.geprueft).length}</b></div>
            <div class="hi-list-summary"><span>Räume</span><b>${app.rooms.length}</b></div>
            ${duplicateExportNote(app)}
            <div class="hi-list-summary"><span>Gesamtwert</span><b>${fmt(total)}</b></div>
        </div>
    </div>`;
}

/**
 * Hinweis in der Export-Zusammenfassung, was wegen Duplikat-Verdachts fehlt
 * bzw. ungeprüft mitgezählt wird. Ein stiller Abzug in der Summe wäre beim
 * Abgleich mit der Positionsliste nicht nachvollziehbar.
 */
function duplicateExportNote(app: App): string {
    const bestaetigt = app.items.filter((i) => i.duplikatStatus === "bestaetigt");
    const offen = openDuplicateCount(app);
    const zeilen: string[] = [];
    if (bestaetigt.length) {
        const summe = bestaetigt.reduce((s, i) => s + i.wert * i.anzahl, 0);
        zeilen.push(`<div class="hi-list-summary"><span>Duplikate (nicht im Export)</span><b>${bestaetigt.length} · ${fmt(summe)}</b></div>`);
    }
    if (offen) {
        zeilen.push(`<div class="hi-list-summary"><span>Ungeprüfte Duplikat-Verdachte</span><b>${offen}</b></div>`);
    }
    return zeilen.join("");
}

function scopedItems(app: App): Item[] {
    // Bestätigte Duplikate gehen gar nicht erst in den Export: Der Empfänger
    // ist die Versicherung, und dieselbe Sache zweimal aufzuführen wäre eine
    // Doppelforderung — auch wenn die Summe sie ohnehin nicht mitzählt.
    const items = app.items.filter(countsAsValue);
    if (app.exportScope !== "geprueft") {
        return items;
    }
    return items.filter((i) => i.geprueft);
}

interface PositionGroup {
    position: Position | undefined;
    items: Item[];
}

interface RoomGroup {
    room: Room | undefined;
    positions: PositionGroup[];
}

/** Gruppiert Gegenstände nach ihrer Position, sortiert nach Positionsnummer. */
function groupItemsByPosition(app: App, items: Item[]): PositionGroup[] {
    const groups = new Map<number, Item[]>();
    for (const item of items) {
        const list = groups.get(item.position) || [];
        list.push(item);
        groups.set(item.position, list);
    }
    return Array.from(groups.entries())
        .map(([positionId, groupItems]) => ({
            position: positionOf(app, positionId),
            items: groupItems.sort((a, b) => a.order - b.order || a.id - b.id),
        }))
        .sort((a, b) => (a.position?.nummer || 0) - (b.position?.nummer || 0));
}

/**
 * Gruppiert Positions-Gruppen nach ihrem Raum.
 *
 * **Reihenfolge der Räume: die kleinste Positionsnummer entscheidet.** Der
 * Raum, in dem die niedrigste Nummer vorkommt, steht vorn — die Aufstellung
 * folgt damit der Reihenfolge, in der fotografiert wurde.
 *
 * Vorher wurde nach `room.order` sortiert. Das steht bei allen Räumen auf 0,
 * die Reihenfolge kam also aus der Einfügereihenfolge der Map und stimmte nur
 * zufällig — sobald ein Raum in WordPress eine Sortiernummer bekäme, wäre sie
 * eine andere, ohne dass jemand den Export angefasst hätte.
 *
 * Positionen ohne Nummer gelten dabei als "ganz hinten" (nicht als 0), sonst
 * risse ein einziger Nachtrag ohne Nummer seinen ganzen Raum an den Anfang
 * des Dokuments. Innerhalb eines Raums bleibt es bei der bisherigen Ordnung,
 * dort stehen unnummerierte Positionen weiterhin vorn.
 */
function groupPositionsByRoom(app: App, positionGroups: PositionGroup[]): RoomGroup[] {
    const groups = new Map<number, PositionGroup[]>();
    for (const g of positionGroups) {
        const roomId = g.position?.raum || 0;
        const list = groups.get(roomId) || [];
        list.push(g);
        groups.set(roomId, list);
    }
    const kleinsteNummer = (positions: PositionGroup[]): number =>
        positions.reduce((min, g) => {
            const nr = g.position?.nummer;
            return nr && nr > 0 && nr < min ? nr : min;
        }, Number.POSITIVE_INFINITY);
    return Array.from(groups.entries())
        .map(([roomId, positions]) => ({
            room: app.rooms.find((r) => r.id === roomId),
            positions: positions.sort((a, b) => (a.position?.nummer || 0) - (b.position?.nummer || 0)),
        }))
        .sort((a, b) => kleinsteNummer(a.positions) - kleinsteNummer(b.positions));
}

/**
 * Die Blöcke, aus denen ein Export besteht — die eine Stelle, an der über
 * Gliederung und Reihenfolge entschieden wird (PDF, CSV und Excel greifen
 * alle hier zu).
 *
 * Ohne Raumgliederung ist es **ein** Block über alle Positionen, streng nach
 * Positionsnummer. Vorher lief auch dieser Fall über die Raumgruppierung: Die
 * Liste war dann nach Raum sortiert, ohne dass man den Raum sah — die
 * Positionsnummern sprangen auf jedem Blatt, und die Aufstellung sah aus, als
 * fehlten hunderte Positionen. „Durchgehende Liste" heißt jetzt auch
 * durchgehend.
 *
 * Mit Raumgliederung bleibt es bei einem Block je Raum; innerhalb eines Raums
 * ist nach Positionsnummer sortiert (das erledigt groupPositionsByRoom).
 */
function exportRoomGroups(app: App, items: Item[]): RoomGroup[] {
    const positions = groupItemsByPosition(app, items);
    if (app.exportRoomMode === "none") {
        return [{ room: undefined, positions }];
    }
    return groupPositionsByRoom(app, positions);
}

/** Raum einer Position — für Ausgaben, die den Raum je Zeile führen (Excel). */
function roomTitleOfPosition(app: App, position: Position | undefined): string {
    const room = position ? app.rooms.find((r) => r.id === position.raum) : undefined;
    return room ? room.title : "Ohne Raum";
}

function csvCell(v: unknown): string {
    return '"' + String(v ?? "").replace(/"/g, '""') + '"';
}

function buildCsv(app: App): string {
    const wertOf = (i: Item) => (app.exportValueMode === "stueck" ? i.wert : i.wert * i.anzahl);
    const lines = [csvColumns(app).map(csvCell).join(";")];
    const roomGroups = exportRoomGroups(app, scopedItems(app));

    for (const roomGroup of roomGroups) {
        if (app.exportRoomMode !== "none") {
            const leer = new Array(csvColumns(app).length - 1).fill("");
            lines.push([`Raum: ${roomGroup.room ? roomGroup.room.title : "Ohne Raum"}`, ...leer].map(csvCell).join(";"));
        }
        for (const posGroup of roomGroup.positions) {
            const pos = posGroup.position;
            if (app.exportGroupItems) {
                lines.push([
                    pos?.nummer || "",
                    posGroup.items.map((i) => i.anzahl).join("\n"),
                    posGroup.items.map((i) => i.title).join("\n"),
                    posGroup.items.map((i) => i.alter).join("\n"),
                    posGroup.items.map(wertOf).join("\n"),
                    posGroup.items.map((i) => i.sonstiges).join("\n"),
                    posGroup.items.map((i) => i.versandkosten || "").join("\n"),
                    posGroup.items.map((i) => i.wertBegruendung).join("\n"),
                ].map(csvCell).join(";"));
            } else {
                for (const i of posGroup.items) {
                    lines.push(
                        [pos?.nummer || "", i.anzahl, i.title, i.alter, wertOf(i), i.sonstiges, i.versandkosten || "", i.wertBegruendung]
                            .map(csvCell)
                            .join(";")
                    );
                }
            }
        }
        if (app.exportRoomMode !== "none") {
            lines.push("");
        }
    }
    return lines.join("\r\n");
}

function downloadBlob(content: BlobPart, filename: string, mime: string): void {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

interface XlsxImagePlacement {
    row: number;
    col: number;
    data: Uint8Array;
    ext: "png" | "jpeg";
}

async function fetchImageBytes(url: string): Promise<{ data: Uint8Array; ext: "png" | "jpeg" } | null> {
    try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const data = new Uint8Array(await res.arrayBuffer());
        const ext: "png" | "jpeg" = /\.png(\?|$)/i.test(url) ? "png" : "jpeg";
        return { data, ext };
    } catch {
        return null;
    }
}

/**
 * Ergänzt Zell-Ausrichtung (Zeilenumbruch, oben ausgerichtet) und eine fette
 * Kopfzeile.
 *
 * Nötig, weil die Community Edition von SheetJS beim Schreiben keine
 * Zell-Styles ausgibt — Zahlenformate (`z`) landen zwar in der Datei, alles
 * Weitere ist dort ein Pro-Feature. Deshalb wird styles.xml nachträglich
 * angereichert.
 */
async function applyXlsxStyles(zip: any, headerColumns: number): Promise<void> {
    const stylesPath = "xl/styles.xml";
    const stylesFile = zip.file(stylesPath);
    if (!stylesFile) return;
    let xml: string = await stylesFile.async("string");

    const alignment = '<alignment vertical="top" wrapText="1"/>';

    // 1. Fett-Schrift für die Kopfzeile anlegen.
    let boldFontId = 0;
    const fontsMatch = xml.match(/<fonts count="(\d+)"([^>]*)>/);
    if (fontsMatch) {
        boldFontId = Number(fontsMatch[1]);
        xml = xml.replace(fontsMatch[0], `<fonts count="${boldFontId + 1}"${fontsMatch[2]}>`);
        xml = xml.replace("</fonts>", '<font><b/><sz val="11"/><name val="Calibri"/></font></fonts>');
    }

    // 2. Bestehende Formate um die Ausrichtung erweitern und einen
    //    zusätzlichen Kopfzeilen-Stil anhängen.
    const xfsMatch = xml.match(/<cellXfs count="(\d+)"([^>]*)>([\s\S]*?)<\/cellXfs>/);
    let headerStyleId = -1;
    if (xfsMatch) {
        const count = Number(xfsMatch[1]);
        // Nur selbstschließende <xf .../> anfassen; solche mit Kindelementen
        // hätten bereits eine eigene Ausrichtung.
        const body = xfsMatch[3].replace(/<xf([^>]*?)\/>/g, `<xf$1 applyAlignment="1">${alignment}</xf>`);
        headerStyleId = count;
        const headerXf = `<xf numFmtId="0" fontId="${boldFontId}" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1">${alignment}</xf>`;
        xml = xml.replace(xfsMatch[0], `<cellXfs count="${count + 1}"${xfsMatch[2]}>${body}${headerXf}</cellXfs>`);
    }
    zip.file(stylesPath, xml);

    if (headerStyleId < 0 || headerColumns <= 0) return;

    // 3. Kopfzeile auf den neuen Stil zeigen lassen. Das Muster trifft nur
    //    Zeile 1 — bei "A11" folgt der 1 keine schließende Klammer.
    const sheetPath = "xl/worksheets/sheet1.xml";
    const sheetFile = zip.file(sheetPath);
    if (!sheetFile) return;
    let sheetXml: string = await sheetFile.async("string");
    sheetXml = sheetXml.replace(/<c r="([A-Z]+)1"([^>]*?)(\/?)>/g, (_m, col, attrs, selfClose) => {
        const cleaned = String(attrs).replace(/\s*s="\d+"/, "");
        return `<c r="${col}1"${cleaned} s="${headerStyleId}"${selfClose}>`;
    });

    // 4. Kopfzeile beim Scrollen stehen lassen.
    const frozenView = '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/><selection pane="bottomLeft"/></sheetView></sheetViews>';
    if (/<sheetViews>[\s\S]*?<\/sheetViews>/.test(sheetXml)) {
        sheetXml = sheetXml.replace(/<sheetViews>[\s\S]*?<\/sheetViews>/, frozenView);
    } else {
        sheetXml = sheetXml.replace(/(<sheetFormatPr|<cols|<sheetData)/, `${frozenView}$1`);
    }

    zip.file(sheetPath, sheetXml);
}

/**
 * Schließt die Excel-Datei ab: Ausrichtung/Kopfzeile formatieren und — falls
 * gewünscht — Fotos einbetten.
 *
 * SheetJS (Community Edition) kann selbst keine Bilder in eine xlsx schreiben —
 * das ist dort ein Pro-Feature. Der Workaround: die von SheetJS erzeugte xlsx
 * (ein ZIP-Archiv) nachträglich mit JSZip öffnen und die fehlenden OOXML-Teile
 * (Bild-Dateien, Drawing-XML, Relationships, Content-Types) selbst ergänzen.
 */
async function finalizeXlsx(baseArray: ArrayBuffer, images: XlsxImagePlacement[], headerColumns: number): Promise<Blob> {
    const zip = await JSZip.loadAsync(baseArray);
    await applyXlsxStyles(zip, headerColumns);

    if (images.length === 0) {
        return zip.generateAsync({ type: "blob", mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    }

    const rels: string[] = [];
    const anchors: string[] = [];
    images.forEach((img, i) => {
        const n = i + 1;
        zip.file(`xl/media/image${n}.${img.ext}`, img.data);
        rels.push(`<Relationship Id="rId${n}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="../media/image${n}.${img.ext}"/>`);
        anchors.push(`<xdr:twoCellAnchor>
            <xdr:from><xdr:col>${img.col}</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${img.row}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:from>
            <xdr:to><xdr:col>${img.col + 1}</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${img.row + 1}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:to>
            <xdr:pic>
                <xdr:nvPicPr><xdr:cNvPr id="${n}" name="Foto${n}"/><xdr:cNvPicPr/></xdr:nvPicPr>
                <xdr:blipFill>
                    <a:blip xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:embed="rId${n}"/>
                    <a:stretch><a:fillRect/></a:stretch>
                </xdr:blipFill>
                <xdr:spPr>
                    <a:xfrm xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"><a:off x="0" y="0"/><a:ext cx="0" cy="0"/></a:xfrm>
                    <a:prstGeom xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" prst="rect"><a:avLst/></a:prstGeom>
                </xdr:spPr>
            </xdr:pic>
            <xdr:clientData/>
        </xdr:twoCellAnchor>`);
    });

    zip.file(
        "xl/drawings/drawing1.xml",
        `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">${anchors.join("")}</xdr:wsDr>`
    );
    zip.file(
        "xl/drawings/_rels/drawing1.xml.rels",
        `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${rels.join("")}</Relationships>`
    );

    const sheetPath = "xl/worksheets/sheet1.xml";
    let sheetXml: string = await zip.file(sheetPath).async("string");
    if (!sheetXml.includes("xmlns:r=")) {
        sheetXml = sheetXml.replace("<worksheet ", '<worksheet xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" ');
    }
    sheetXml = sheetXml.replace("</worksheet>", `<drawing r:id="rIdDrawing1"/></worksheet>`);
    zip.file(sheetPath, sheetXml);

    const sheetRelsPath = "xl/worksheets/_rels/sheet1.xml.rels";
    const existingRelsFile = zip.file(sheetRelsPath);
    const drawingRel = `<Relationship Id="rIdDrawing1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing" Target="../drawings/drawing1.xml"/>`;
    if (existingRelsFile) {
        const existing: string = await existingRelsFile.async("string");
        zip.file(sheetRelsPath, existing.replace("</Relationships>", `${drawingRel}</Relationships>`));
    } else {
        zip.file(
            sheetRelsPath,
            `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${drawingRel}</Relationships>`
        );
    }

    const ctPath = "[Content_Types].xml";
    let ctXml: string = await zip.file(ctPath).async("string");
    const additions: string[] = [];
    if (!/Extension="png"/.test(ctXml) && images.some((i) => i.ext === "png")) {
        additions.push('<Default Extension="png" ContentType="image/png"/>');
    }
    if (!/Extension="jpeg"/.test(ctXml) && images.some((i) => i.ext === "jpeg")) {
        additions.push('<Default Extension="jpeg" ContentType="image/jpeg"/>');
    }
    if (!ctXml.includes("drawing+xml")) {
        additions.push('<Override PartName="/xl/drawings/drawing1.xml" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/>');
    }
    if (additions.length) {
        zip.file(ctPath, ctXml.replace("</Types>", `${additions.join("")}</Types>`));
    }

    return zip.generateAsync({ type: "blob", mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
}

/** Euro-Format mit Tausenderpunkt, damit Excel die Werte als Geld rechnet. */
const XLSX_EURO = '#,##0.00\\ "€"';

/**
 * Spalten der Excel-Aufstellung. Anders als bei PDF/CSV steht hier bewusst
 * eine Zeile je Gegenstand mit eigener Raum-Spalte — so bleibt die Liste
 * sortier- und filterbar, was gruppierte Zwischenüberschriften verhindern
 * würden.
 */
const XLSX_COLUMNS: { title: string; width: number; format?: string }[] = [
    { title: "Pos.", width: 6 },
    { title: "Raum", width: 20 },
    { title: "Gegenstand", width: 42 },
    { title: "Anzahl", width: 8 },
    { title: "Wert je Stück", width: 14, format: XLSX_EURO },
    { title: "Gesamtwert", width: 14, format: XLSX_EURO },
    // Eigene Spalte, nicht im Gesamtwert enthalten: Versandkosten fallen je
    // Bestellung an, nicht je Stück — eingerechnet würden sie mit der Anzahl
    // multipliziert und die Summe verfälschen.
    { title: "Versandkosten", width: 14, format: XLSX_EURO },
    { title: "Alter", width: 20 },
    { title: "Quelle", width: 14 },
    { title: "Geprüft", width: 9 },
    { title: "Sonstiges", width: 50 },
    { title: "Wertbegründung", width: 60 },
];

async function buildXlsxBlob(app: App, embedImages: boolean): Promise<Blob> {
    const columns = embedImages ? [...XLSX_COLUMNS, { title: "Foto", width: 22 }] : XLSX_COLUMNS;
    const rows: (string | number)[][] = [columns.map((c) => c.title)];
    const pending: { row: number; url: string }[] = [];
    // Zeilen mit Foto brauchen eine feste Höhe, sonst wird das eingebettete
    // Bild in eine Standardzeile gequetscht.
    const imageRowHeights = new Map<number, number>();

    for (const roomGroup of exportRoomGroups(app, scopedItems(app))) {
        for (const posGroup of roomGroup.positions) {
            const pos = posGroup.position;
            // Excel führt den Raum als eigene Spalte. Ohne Raumgliederung gibt
            // es keinen Block-Raum mehr, deshalb kommt er hier je Position aus
            // der Position selbst — sonst stünde in der ganzen Tabelle
            // "Ohne Raum".
            const roomName = roomGroup.room ? roomGroup.room.title : roomTitleOfPosition(app, pos);
            for (const i of posGroup.items) {
                const rowIndex = rows.length;
                const row: (string | number)[] = [
                    pos?.nummer || "",
                    roomName,
                    i.title,
                    i.anzahl,
                    i.wert,
                    i.wert * i.anzahl,
                    i.versandkosten || "",
                    i.alter,
                    i.quelle,
                    i.geprueft ? "ja" : "nein",
                    i.sonstiges,
                    i.wertBegruendung,
                ];
                if (embedImages) {
                    row.push("");
                    // Foto der Position an der ersten Zeile des jeweiligen
                    // Gegenstands-Blocks, nicht an jeder Zeile erneut.
                    if (pos && pos.fotos.length > 0 && i === posGroup.items[0]) {
                        const foto = pos.fotos[0];
                        // Die Vorschaugröße genügt für eine ~120 px hohe Zelle.
                        // Mit den Originalen käme die Datei schnell auf 40 MB
                        // und wäre per Mail nicht mehr versendbar.
                        pending.push({ row: rowIndex, url: foto.sizes?.medium || foto.url });
                        imageRowHeights.set(rowIndex, 90);
                    }
                }
                rows.push(row);
            }
        }
    }

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = columns.map((c) => ({ wch: c.width }));

    // Zahlenformate zellweise setzen — "z" wird auch von der Community
    // Edition in die Datei geschrieben (im Gegensatz zu vollen Zell-Styles).
    for (let c = 0; c < columns.length; c++) {
        const format = columns[c].format;
        if (!format) continue;
        for (let r = 1; r < rows.length; r++) {
            const cell = ws[XLSX.utils.encode_cell({ r, c })];
            if (cell && typeof cell.v === "number") {
                cell.t = "n";
                cell.z = format;
            }
        }
    }

    if (imageRowHeights.size > 0) {
        const maxRow = Math.max(...imageRowHeights.keys());
        ws["!rows"] = Array.from({ length: maxRow + 1 }, (_, r) =>
            imageRowHeights.has(r) ? { hpt: imageRowHeights.get(r) } : {}
        );
    }

    // Filterzeile über den gesamten Datenbereich. Das Fixieren der Kopfzeile
    // passiert in applyXlsxStyles, weil die Community Edition beim Schreiben
    // keine Freeze-Panes erzeugt.
    ws["!autofilter"] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: rows.length - 1, c: columns.length - 1 } }) };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Aufstellung");
    const baseArray: ArrayBuffer = XLSX.write(wb, { type: "array", bookType: "xlsx" });

    const images = embedImages && pending.length > 0
        ? (await Promise.all(pending.map(async (p) => {
              const img = await fetchImageBytes(p.url);
              return img ? { row: p.row, col: columns.length - 1, ...img } : null;
          }))).filter((x): x is XlsxImagePlacement => x !== null)
        : [];

    try {
        return await finalizeXlsx(baseArray, images, columns.length);
    } catch (error) {
        // Lieber eine schlicht formatierte Datei als gar keine.
        console.warn("Excel-Nachbearbeitung fehlgeschlagen, Export ohne Zusatzformatierung:", error);
        return new Blob([baseArray], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    }
}

/**
 * Spaltenköpfe der Aufstellung — je Blatt wiederholt.
 *
 * Ohne Wertbegründung: Das PDF ist das Dokument, das der Versicherung
 * vorgelegt wird, und dort trägt die Begründung nichts zur Aufstellung bei.
 * In CSV und Excel steht sie weiter — siehe {@link csvColumns}.
 */
function pdfColumns(app: App, withAlter = app.exportShowAlter): string[] {
    return [
        "Pos.",
        "Anzahl",
        "Gegenstand",
        ...(withAlter ? ["Alter"] : []),
        app.exportValueMode === "stueck" ? "€ je Stück" : "Gesamt €",
        "Sonstiges",
    ];
}

/**
 * Spaltenköpfe der CSV-Rohdaten: die Spalten des PDF plus Versandkosten und
 * Wertbegründung.
 *
 * Die Versandkosten fehlen im PDF bewusst — das ist die Aufstellung für die
 * Versicherung, und dort stünde eine Spalte, die in keiner Summe auftaucht,
 * nur zur Verwirrung. In den Rohdaten gehört sie hin, weil sie sich später
 * nicht mehr rekonstruieren lässt.
 */
function csvColumns(app: App): string[] {
    // Immer mit Alter, auch wenn die Spalte im PDF abgeschaltet ist: Die
    // Rohdaten sollen vollständig sein, und was hier fehlt, lässt sich später
    // nicht mehr aus der Datei rekonstruieren.
    return [...pdfColumns(app, true), "Versandkosten", "Wertbegründung"];
}

/**
 * Datum für die Unterschriftszeile. Ohne Eingabe der heutige Tag.
 *
 * Die Uhrzeit 12:00 verhindert, dass ein reines Datum als UTC-Mitternacht
 * gelesen und in westlichen Zeitzonen zum Vortag wird.
 */
export function formatExportDate(iso: string): string {
    const raw = iso.trim();
    const date = raw ? new Date(`${raw}T12:00:00`) : new Date();
    const valid = !Number.isNaN(date.getTime()) ? date : new Date();
    // Führende Nullen ausdrücklich: die Standardausgabe liefert "31.7.2026",
    // auf einem Formular ist "31.07.2026" üblich.
    return valid.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}
/** Datum als "YYYY-MM-DD" — dieselbe Grundlage wie formatExportDate. */
function isoExportDate(iso: string): string {
    const raw = iso.trim();
    const date = raw ? new Date(`${raw}T12:00:00`) : new Date();
    const valid = !Number.isNaN(date.getTime()) ? date : new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${valid.getFullYear()}-${pad(valid.getMonth() + 1)}-${pad(valid.getDate())}`;
}

/**
 * Titel des Druckfensters — und damit der vorgeschlagene Dateiname, wenn der
 * Browser das Blatt als PDF speichert.
 *
 * Aufbau: `schadenaufstellung__<Datum>__<vorname-nachname>`, durchgehend
 * klein. Umlaute werden ausgeschrieben und alles Übrige auf Buchstaben,
 * Ziffern und Bindestriche reduziert: Der Name landet in einem Dateinamen,
 * und ein "ö" oder ein Punkt darin bringt je nach Betriebssystem und
 * Mailprogramm die Datei durcheinander.
 */
export function pdfDocumentTitle(app: App): string {
    const name = app.exportOwner
        .toLowerCase()
        // Deutsche Umlaute zuerst und ausgeschrieben — die Zerlegung unten
        // würde sonst "ö" zu "o" machen, und aus Jörg würde Jorg.
        .replace(/ä/g, "ae")
        .replace(/ö/g, "oe")
        .replace(/ü/g, "ue")
        .replace(/ß/g, "ss")
        // Übrige Akzente abtrennen und verwerfen, damit aus "Élodie" nicht
        // "lodie" wird: Ohne diesen Schritt fiele der Buchstabe ganz weg.
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    const teile = ["schadenaufstellung", isoExportDate(app.exportDate)];
    if (name) teile.push(name);
    return teile.join("__");
}

/**
 * Feste Spaltenbreiten. Ohne sie verteilt der Browser nach Inhalt, und die
 * schmalen Zahlenspalten brechen um ("180" / "€"). Leerer Wert = Restbreite.
 */
// "Sonstiges" am Ende bekommt die Restbreite; "Gegenstand" ist mit 45 mm die
// zweite Textspalte, denn der Name des Gegenstands trägt die Aufstellung.
//
// "Pos." braucht 13 mm: Abzüglich Zellenpolster bleiben von 9 mm nur gut 4 mm
// Textbreite — zu wenig für eine dreistellige Positionsnummer, die dann trotz
// `nowrap` über die Spaltengrenze in den Nachbarn läuft.
// Ohne die Alters-Spalte fallen ihre 14 mm an "Sonstiges" (Restbreite);
// "Gegenstand" bleibt bei 45 mm, damit die Aufstellung mit und ohne Alter
// denselben Satzspiegel hat.
function pdfColumnWidths(app: App): string[] {
    return ["13mm", "13mm", "45mm", ...(app.exportShowAlter ? ["14mm"] : []), "18mm", ""];
}

/**
 * Klassen der Spaltenköpfe, index-gleich zu {@link pdfColumnWidths} und
 * {@link pdfColumns}.
 *
 * Die Euro-Spalte ist mit 18 mm die schmalste Textspalte. Ohne `nowrap` bricht
 * ihre Überschrift dort um ("Gesamt" / "€" bzw. "€ je" / "Stück") und der
 * Tabellenkopf wird eine Zeile höher als nötig. Die Datenzellen darunter haben
 * über `.c-wert` schon dieselbe Regel — der Kopf zieht hier nach.
 *
 * Dasselbe gilt für die beiden Zahlenspalten links: "Pos." und "Anzahl" sind
 * schmal genug, dass ihre Überschriften sonst umbrechen.
 */
function pdfColumnClasses(app: App): string[] {
    return ["c-num", "c-num", "", ...(app.exportShowAlter ? [""] : []), "c-wert-head", ""];
}

/**
 * Verteilt die Zeilen auf A4-Blätter und beschriftet Kopf und Fuß.
 *
 * Der Umbruch wird selbst gerechnet, statt ihn dem Browser zu überlassen:
 * "Blatt X von Y" setzt voraus, dass die Seitenzahl bekannt ist, und die
 * einzige CSS-Möglichkeit dafür (Seitenzähler in `@page`-Randbereichen)
 * unterstützt kein Browser. Mit eigenen Blättern fester Höhe steht die
 * Gesamtzahl dagegen fest, bevor gedruckt wird.
 */
function paginateSheets(
    doc: Document,
    meta: {
        owner: string;
        signature: string;
        columns: string[];
        widths: string[];
        classes: string[];
        signatureLastPageOnly: boolean;
        intro: string;
    }
): void {
    const container = doc.getElementById("hi-sheets");
    const source = doc.getElementById("hi-source") as HTMLTableElement | null;
    if (!container || !source) return;

    const pending = Array.from(source.tBodies[0].rows);
    source.remove();

    const newSheet = (room: string | null): HTMLTableSectionElement => {
        const sheet = doc.createElement("section");
        sheet.className = "hi-sheet";
        sheet.innerHTML = `
            <header class="hi-sheet__head">
                <div class="hi-sheet__line">
                    <span class="hi-sheet__title">Schadenaufstellung</span>
                    <span class="hi-sheet__pages"></span>
                </div>
                <div class="hi-sheet__owner"><span>Versicherungsnehmer:</span> <b>${esc(meta.owner) || "&nbsp;"}</b></div>
                ${room === null ? "" : `<div class="hi-sheet__room"><span>Raum:</span> <b>${esc(room)}</b></div>`}
            </header>
            <div class="hi-sheet__body">
                <table>
                    <colgroup>${meta.widths.map((w) => `<col${w ? ` style="width:${w}"` : ""}>`).join("")}</colgroup>
                    <thead><tr>${meta.columns
                        .map((c, i) => {
                            const cls = meta.classes[i];
                            return `<th${cls ? ` class="${cls}"` : ""}>${esc(c)}</th>`;
                        })
                        .join("")}</tr></thead>
                    <tbody></tbody>
                </table>
            </div>
            <footer class="hi-sheet__foot">
                <div class="hi-sign"><span class="hi-sign__value">${esc(meta.signature)}</span><span class="hi-sign__line"></span><span class="hi-sign__label">Ort, Datum</span></div>
                <div class="hi-sign"><span class="hi-sign__value">&nbsp;</span><span class="hi-sign__line"></span><span class="hi-sign__label">Unterschrift Versicherungsnehmer</span></div>
            </footer>`;
        container.appendChild(sheet);
        return sheet.querySelector("tbody") as HTMLTableSectionElement;
    };

    /**
     * Ein Blatt ohne Tabelle, für die Erläuterung vor der Aufstellung.
     * Kopf und Fuß sind dieselben wie bei den Listenblättern, damit die
     * Erläuterung in der Seitenzählung mitläuft und der Satzspiegel steht.
     */
    const newProseSheet = (): HTMLElement => {
        const sheet = doc.createElement("section");
        sheet.className = "hi-sheet";
        sheet.innerHTML = `
            <header class="hi-sheet__head">
                <div class="hi-sheet__line">
                    <span class="hi-sheet__title">Schadenaufstellung</span>
                    <span class="hi-sheet__pages"></span>
                </div>
                <div class="hi-sheet__owner"><span>Versicherungsnehmer:</span> <b>${esc(meta.owner) || "&nbsp;"}</b></div>
            </header>
            <div class="hi-sheet__body hi-sheet__body--prose"></div>
            <footer class="hi-sheet__foot">
                <div class="hi-sign"><span class="hi-sign__value">${esc(meta.signature)}</span><span class="hi-sign__line"></span><span class="hi-sign__label">Ort, Datum</span></div>
                <div class="hi-sign"><span class="hi-sign__value">&nbsp;</span><span class="hi-sign__line"></span><span class="hi-sign__label">Unterschrift Versicherungsnehmer</span></div>
            </footer>`;
        container.appendChild(sheet);
        return sheet.querySelector(".hi-sheet__body") as HTMLElement;
    };

    // Die Erläuterung steht vor der Liste und darf mehrere Blätter füllen.
    // Umbrochen wird blockweise nach demselben Verfahren wie die Tabelle:
    // anhängen, messen, bei Überlauf zurücknehmen und neu ansetzen. Eine
    // Zwischenüberschrift allein am Seitenende wandert mit ihrem ersten Absatz
    // auf das nächste Blatt.
    const queue = meta.intro
        .split(/\n\s*\n/)
        .map((b) => b.trim())
        .filter(Boolean);
    if (queue.length > 0) {
        let prose = newProseSheet();
        // Obergrenze gegen eine Endlosschleife, falls ein Block auch nach dem
        // Aufteilen nicht unterzubringen ist. Zweihundert Blöcke sind weit mehr,
        // als eine Erläuterung je hat.
        let guard = 0;
        while (queue.length > 0 && guard++ < 200) {
            const block = queue.shift() as string;
            const heading = block.startsWith("# ");
            const text = heading ? block.slice(2).trim() : block;
            const el = doc.createElement(heading ? "h2" : "p");
            el.className = heading ? "hi-prose__head" : "hi-prose__text";
            el.textContent = text;
            prose.appendChild(el);
            if (prose.scrollHeight <= prose.clientHeight) continue;
            prose.removeChild(el);

            // Ist das Blatt noch leer, hilft ein neues nicht weiter: Der Absatz
            // ist für sich schon zu lang. Dann wird wortweise so viel gesetzt,
            // wie hineingeht, und der Rest als eigener Block nachgereicht —
            // sonst fiele der überzählige Text stillschweigend weg.
            if (prose.childElementCount === 0 && !heading) {
                prose.appendChild(el);
                const words = text.split(/\s+/);
                let fit = words.length - 1;
                while (fit > 0) {
                    el.textContent = words.slice(0, fit).join(" ");
                    if (prose.scrollHeight <= prose.clientHeight) break;
                    fit--;
                }
                if (fit <= 0) {
                    // Selbst ein einzelnes Wort passt nicht — der Rest bliebe
                    // ohnehin unsichtbar. Lieber ganz stehen lassen.
                    el.textContent = text;
                    continue;
                }
                queue.unshift(words.slice(fit).join(" "));
                prose = newProseSheet();
                continue;
            }

            // Eine Zwischenüberschrift allein am Seitenende wandert mit ihrem
            // ersten Absatz auf das nächste Blatt.
            const last = prose.lastElementChild;
            const carry = last && last.classList.contains("hi-prose__head") ? last : null;
            if (carry) carry.remove();
            prose = newProseSheet();
            if (carry) prose.appendChild(carry);
            prose.appendChild(el);
        }
        // Ein leer gebliebenes Erläuterungsblatt würde sonst als leere Seite
        // mitgedruckt und in der Blattzählung mitgezählt.
        if (prose.childElementCount === 0) prose.closest(".hi-sheet")?.remove();
    }

    const bodyOf = (tb: HTMLTableSectionElement) => tb.closest(".hi-sheet__body") as HTMLElement;
    // Steht der Raum im Seitenkopf, tragen die Zeilen ihn als data-room. Dann
    // beginnt jeder Raum auf einem eigenen Blatt — so erwartet es die
    // Versicherung. Ohne das Attribut bleibt der Kopf ohne Raumzeile.
    let room: string | null = pending.find((r) => r.hasAttribute("data-room"))?.getAttribute("data-room") ?? null;
    let tbody = newSheet(room);

    for (const row of pending) {
        const rowRoom = row.getAttribute("data-room");
        if (rowRoom !== null && rowRoom !== room) {
            room = rowRoom;
            tbody = newSheet(room);
        }

        tbody.appendChild(row);
        const body = bodyOf(tbody);
        if (body.scrollHeight <= body.clientHeight) continue;

        // Passt nicht mehr: Zeile zurücknehmen und auf einem neuen Blatt
        // desselben Raums fortsetzen. Eine Raumüberschrift wandert mit, statt
        // allein am Seitenende zurückzubleiben.
        tbody.removeChild(row);
        const last = tbody.lastElementChild;
        const carry = last && last.classList.contains("hi-export-room") ? last : null;
        if (carry) carry.remove();
        tbody = newSheet(room);
        if (carry) tbody.appendChild(carry);
        tbody.appendChild(row);
    }

    // Positionsnummern je Blatt zusammenfassen: Die erste Zeile einer Gruppe
    // behält ihre Zelle und überspannt den Rest, bei den übrigen fällt sie
    // weg. Weil das pro Blatt geschieht, bekommt eine über den Seitenwechsel
    // laufende Position auf dem Folgeblatt wieder ihre Nummer, und der
    // rowspan zählt nur die Zeilen, die dort tatsächlich stehen.
    for (const sheet of Array.from(container.children)) {
        const rows = Array.from(sheet.querySelectorAll<HTMLTableRowElement>("tbody tr"));
        let runStart: HTMLTableRowElement | null = null;
        const run: HTMLTableRowElement[] = [];
        const closeRun = () => {
            if (runStart && run.length > 0) {
                (runStart.firstElementChild as HTMLTableCellElement).rowSpan = run.length;
                runStart.classList.add("hi-export-pos-start");
                // Die überzähligen Nummernzellen der Folgezeilen entfernen.
                for (const row of run.slice(1)) row.firstElementChild?.remove();
            }
            runStart = null;
            run.length = 0;
        };
        for (const row of rows) {
            const pos = row.getAttribute("data-pos");
            if (pos === null) {
                closeRun();
                continue;
            }
            if (runStart && runStart.getAttribute("data-pos") === pos) {
                run.push(row);
            } else {
                closeRun();
                runStart = row;
                run.push(row);
            }
        }
        closeRun();
    }

    const sheets = Array.from(container.children);
    sheets.forEach((sheet, i) => {
        const label = sheet.querySelector(".hi-sheet__pages");
        if (label) label.textContent = `Blatt ${i + 1} von ${sheets.length} Blättern`;
    });

    // Unterschrift nur auf dem letzten Blatt: Auf jedem Blatt zu unterschreiben
    // ist bei einer Aufstellung über zwanzig Seiten eine Zumutung, und die
    // Versicherung verlangt es in aller Regel nur einmal. Die Fußzeile bleibt
    // auf den übrigen Blättern stehen, aber leer — sonst wäre der Satzspiegel
    // der letzten Seite ein anderer als der aller vorigen.
    if (meta.signatureLastPageOnly) {
        sheets.slice(0, -1).forEach((sheet) => {
            const foot = sheet.querySelector(".hi-sheet__foot");
            foot?.classList.add("hi-sheet__foot--blank");
        });
    }
}

export async function runExport(app: App): Promise<void> {
    const data = scopedItems(app);
    if (app.exportFormat === "pdf") {
        const win = window.open("", "_blank");
        if (!win) return;

        // Immer eine Tabellenzeile je Gegenstand.
        //
        // Zusammengefasste Positionen standen früher in einer einzigen Zeile,
        // in der jede Zelle die Werte aller Gegenstände als <br>-Liste
        // enthielt. Das hielt nur, solange kein Wert umbrach: Sobald etwa
        // "Sonstiges" über zwei Zeilen ging — der Normalfall —, verschob sich
        // diese Spalte gegen die übrigen und die Angaben eines Gegenstands
        // standen nicht mehr nebeneinander. Beim Zusammenfassen überspannt die
        // Positionsnummer die Gegenstände jetzt per rowspan, statt die Werte
        // in eine Zelle zu pressen.
        const rows = exportRoomGroups(app, data).map((roomGroup) => {
            const roomName = roomGroup.room ? roomGroup.room.title : "Ohne Raum";
            // Bei "page" steht der Raum im Seitenkopf statt in der Tabelle;
            // paginateSheets() liest ihn dafür von den Zeilen ab und beginnt
            // bei jedem Wechsel ein neues Blatt.
            const roomAttr = app.exportRoomMode === "page" ? ` data-room="${esc(roomName)}"` : "";
            const roomHeader = app.exportRoomMode === "row"
                ? `<tr class="hi-export-room"><td colspan="${pdfColumns(app).length}">${esc(roomName)}</td></tr>`
                : "";
            const positionRows = roomGroup.positions.map((posGroup) => {
                const nummer = posGroup.position?.nummer || "";
                return posGroup.items.map((i) => {
                    // Jede Zeile bekommt ihre eigene Positionszelle. Beim
                    // Zusammenfassen legt paginateSheets() sie später je Blatt
                    // zusammen — erst dort steht fest, wie viele Zeilen einer
                    // Gruppe auf welcher Seite landen. Wichtig ist, dass schon
                    // beim Ausmessen genauso viele Zellen stehen wie
                    // pdfColumns() Spalten meldet — sonst rutschen die Inhalte
                    // in die falschen Spalten und die gemessenen Zeilenhöhen
                    // stimmen nicht. Die Alters-Zelle entfällt deshalb unter
                    // derselben Bedingung wie ihre Überschrift.
                    const posAttr = app.exportGroupItems ? ` data-pos="${nummer}"` : "";
                    return `<tr${posAttr}${roomAttr}>
                        <td class="c-num">${nummer}</td>
                        <td class="c-num">${i.anzahl}</td>
                        <td>${esc(i.title)}</td>
                        ${app.exportShowAlter ? `<td>${esc(i.alter) || "—"}</td>` : ""}
                        <td class="c-wert">${fmt(app.exportValueMode === "stueck" ? i.wert : i.wert * i.anzahl)}</td>
                        <td>${esc(i.sonstiges)}</td>
                    </tr>`;
                }).join("");
            }).join("");
            return roomHeader + positionRows;
        }).join("");
        const total = data.reduce((s, i) => s + i.wert * i.anzahl, 0);
        const printCss = [
            "@page{size:A4;margin:0}",
            "*{box-sizing:border-box}",
            "body{margin:0;font-family:sans-serif;background:#f0f0f0}",
            // Ein Blatt entspricht genau einer A4-Seite. Kopf und Fuß sind
            // Teil jedes Blattes und stehen dadurch auf jeder Seite — ohne
            // sich auf Browser-Kopfzeilen verlassen zu müssen.
            ".hi-sheet{width:210mm;height:297mm;padding:12mm 14mm;background:#fff;display:flex;flex-direction:column;margin:0 auto 8mm}",
            ".hi-sheet:last-child{margin-bottom:0}",
            "@media print{body{background:#fff}.hi-sheet{margin:0}.hi-sheet+.hi-sheet{break-before:page;page-break-before:always}}",
            ".hi-sheet__head{flex:0 0 auto;border-bottom:1.5px solid #333;padding-bottom:3mm;margin-bottom:4mm}",
            ".hi-sheet__line{display:flex;justify-content:space-between;align-items:baseline;gap:10mm}",
            ".hi-sheet__title{font-size:16pt;font-weight:700}",
            ".hi-sheet__pages{font-size:10pt;white-space:nowrap}",
            ".hi-sheet__owner{margin-top:2mm;font-size:10pt}",
            ".hi-sheet__room{margin-top:1mm;font-size:11pt}",
            ".hi-sheet__owner span,.hi-sheet__room span{color:#555}",
            // Der Rumpf bekommt die Resthöhe; an ihm misst die Aufteilung,
            // ob eine weitere Zeile noch passt.
            ".hi-sheet__body{flex:1 1 auto;min-height:0;overflow:hidden}",
            // Erläuterungsblätter: Fließtext statt Tabelle. Die Zeilenlänge ist
            // auf 150 mm begrenzt — über die volle Blattbreite gesetzt wäre der
            // Absatz mit rund 110 Zeichen je Zeile schwer zu lesen.
            ".hi-sheet__body--prose{max-width:150mm;font-size:10.5pt;line-height:1.5}",
            ".hi-prose__head{margin:6mm 0 2mm;font-size:12pt;font-weight:700}",
            ".hi-prose__head:first-child{margin-top:0}",
            ".hi-prose__text{margin:0 0 3mm;text-align:justify;hyphens:auto}",
            ".hi-sheet__foot{flex:0 0 auto;display:flex;gap:12mm;padding-top:8mm;margin-top:4mm}",
            // Unterschrift nur auf dem letzten Blatt: Die Fußzeile bleibt
            // stehen, aber unsichtbar — nähme man sie ganz heraus, hätte das
            // letzte Blatt einen anderen Satzspiegel als alle vorigen und die
            // Zeilen säßen von Seite zu Seite unterschiedlich hoch.
            ".hi-sheet__foot--blank{visibility:hidden}",
            ".hi-sign{flex:1;display:flex;flex-direction:column;justify-content:flex-end}",
            // Der eingetragene Ort samt Datum steht über der Linie, so wie er
            // handschriftlich dort stünde.
            ".hi-sign__value{min-height:10mm;padding:0 1mm 1mm;font-size:10pt}",
            ".hi-sign__line{border-top:1px solid #333}",
            ".hi-sign__label{margin-top:1.5mm;font-size:9pt;color:#555}",
            "table{border-collapse:collapse;width:100%}",
            "th{border:1px solid #999;padding:1.5mm 2mm;font-size:9pt;text-align:left;background:#f4f6f6}",
            "table{table-layout:fixed}",
            "td{border:1px solid #ccc;padding:1.5mm 2mm;font-size:9pt;text-align:left;vertical-align:top;word-wrap:break-word}",
            // Über Klassen statt nth-child, weil Zeilen mit und ohne
            // Positionszelle unterschiedlich viele Zellen haben und sich der
            // Spaltenindex dadurch verschiebt. "Alter" darf umbrechen
            // ("ca. 2-3 Jahre"), Zahl und Eurozeichen gehören zusammen.
            ".c-num{white-space:nowrap}",
            ".c-wert{white-space:nowrap;text-align:right}",
            // Kopf der Euro-Spalte: 18 mm sind zu schmal fuer "Gesamt €" bzw.
            // "€ je Stück" — ohne nowrap braucht der Tabellenkopf eine Zeile
            // mehr, nur wegen dieser einen Spalte.
            "th.c-wert-head{white-space:nowrap;text-align:right}",
            ".hi-export-room td{background:#eef4f2;font-weight:700}",
            // Grenze zwischen zwei Positionen, da die Nummer beim
            // Zusammenfassen nur in der ersten Zeile der Gruppe steht.
            ".hi-export-pos-start td{border-top:1.5px solid #9aa4a2}",
            ".hi-total td{border-top:1.5px solid #333;font-weight:700;background:#f4f6f6}",
            // Hintergründe auch im Ausdruck, nicht nur am Bildschirm.
            "body{-webkit-print-color-adjust:exact;print-color-adjust:exact}",
        ].join("");

        const columns = pdfColumns(app);
        // Die Endsumme ist immer Anzahl × Stückpreis, auch wenn die Spalte
        // darüber Stückpreise zeigt — sonst stünde dort eine Zahl, die
        // niemanden interessiert.
        // Die Wertspalte ist die fünfte; davor läuft die Beschriftung durch,
        // dahinter bleiben die Textspalten leer. Aus columns.length gerechnet,
        // damit eine weitere Spalte die Zeile nicht wieder verschiebt.
        const WERT_SPALTE = 5;
        const leereZellen = "<td></td>".repeat(columns.length - WERT_SPALTE);
        const summaryRow = `<tr class="hi-total"><td colspan="${WERT_SPALTE - 1}">Gesamtwert der Aufstellung</td><td class="c-wert">${fmt(total)}</td>${leereZellen}</tr>`;
        const signature = [app.exportPlace.trim(), formatExportDate(app.exportDate)].filter(Boolean).join(", ");

        win.document.write(`<!doctype html><html lang="de"><head><meta charset="utf-8"><title>${esc(pdfDocumentTitle(app))}</title><style>${printCss}</style></head><body><div id="hi-sheets"></div><table id="hi-source" hidden><tbody>${rows}${summaryRow}</tbody></table></body></html>`);
        win.document.close();

        // Erst messen, wenn die Schrift steht — sonst fällt die Aufteilung
        // anders aus als der spätere Ausdruck.
        try {
            await win.document.fonts.ready;
        } catch {
            // Ältere Engines ohne Font Loading API: kurz warten genügt.
            await new Promise((r) => setTimeout(r, 200));
        }
        paginateSheets(win.document, {
            owner: app.exportOwner.trim(),
            signature,
            columns,
            widths: pdfColumnWidths(app),
            classes: pdfColumnClasses(app),
            signatureLastPageOnly: app.exportSignatureLastPage,
            intro: app.exportIntro ? app.exportIntroText : "",
        });
        win.focus();
        setTimeout(() => win.print(), 200);
    } else if (app.exportFormat === "xlsx") {
        const blob = await buildXlsxBlob(app, app.exportEmbedImages);
        // Gleiches Namensschema wie beim PDF — die Dateien einer Aufstellung
        // gehören zusammen und sollen im Ordner nebeneinander stehen.
        downloadBlob(
            blob,
            `${pdfDocumentTitle(app)}.xlsx`,
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        );
    } else {
        const bom = String.fromCharCode(0xfeff);
        downloadBlob(bom + buildCsv(app), `${pdfDocumentTitle(app)}.csv`, "text/csv;charset=utf-8");
    }
}

/* ------------------------------------------------------------- Router */

/* ------------------------------------------------------ MCP-Verbindungen */

/** Basic-Auth-Wert eines Zugangs — Baustein aller Kopiervorlagen. */
function mcpBasic(cred: McpCredential): string {
    return btoa(`${cred.user}:${cred.secret}`);
}

/**
 * Der Start-Prompt zu einem Zugang: alles, was eine KI braucht, um die
 * Verbindung einzurichten und sofort loszulegen — Zugangsdaten, die zwei
 * gängigen Einrichtungswege und die wichtigsten Konventionen des Inventars.
 * Wird 1:1 in die Zwischenablage kopiert und in Claude/ChatGPT eingefügt.
 */
export function buildMcpPrompt(cred: McpCredential): string {
    return [
        "Ich möchte, dass du mit meinem Hausinventar arbeitest. Die Verbindung läuft über MCP (Model Context Protocol).",
        "",
        "Verbindungsdaten:",
        `- MCP-Endpoint (Streamable HTTP): ${cred.endpoint}`,
        "- Authentifizierung: HTTP Basic Auth",
        `- Benutzer: ${cred.user}`,
        `- Passwort: ${cred.secret}`,
        `- Fertiger Authorization-Header: Basic ${mcpBasic(cred)}`,
        "",
        "Einrichtung, je nachdem wo du läufst:",
        `- Claude Code: führe aus: ${buildMcpClaudeCommand(cred)}`,
        "- Claude Desktop / claude.ai / ChatGPT: lege in den Einstellungen einen Konnektor (Custom Connector) mit der obigen Endpoint-URL an. Ohne Header-Unterstützung nutze die mcp-remote-Brücke:",
        `  npx -y mcp-remote ${cred.endpoint} --header "Authorization: Basic ${mcpBasic(cred)}"`,
        "",
        "Danach stehen dir MCP-Tools für das komplette Inventar zur Verfügung: Räume, Positionen und Gegenstände lesen, anlegen, ändern und löschen (update_item für Preisänderungen, search_inventory, get_stats, get_item_photos für Fotos, …).",
        "",
        "Wichtige Konventionen:",
        "- Gegenstände und Räume sprichst du über ihre Post-ID an (in der App überall als #ID sichtbar), Positionen über ihre Positions-Nummer.",
        "- Preise (wert) sind EUR je Einzelstück; der Zeilenwert ist wert × anzahl.",
        "- Bestätigte Duplikate zählen in keinen Summen.",
        "",
        "Richte die Verbindung jetzt ein, bestätige kurz, dass sie steht, und frag mich dann, was ich ändern möchte.",
    ].join("\n");
}

/** Einzeiler für die Registrierung in Claude Code. */
export function buildMcpClaudeCommand(cred: McpCredential): string {
    return `claude mcp add --transport http hausinventar ${cred.endpoint} --header "Authorization: Basic ${mcpBasic(cred)}"`;
}

/** Eintrag für die claude_desktop_config.json (mcp-remote-Brücke). */
export function buildMcpDesktopConfig(cred: McpCredential): string {
    return JSON.stringify(
        {
            mcpServers: {
                hausinventar: {
                    command: "npx",
                    // Header über env durchgereicht und ohne Leerzeichen nach
                    // dem Doppelpunkt — bekannter Leerzeichen-Bug von
                    // mcp-remote unter Windows.
                    args: ["-y", "mcp-remote", cred.endpoint, "--header", 'Authorization:${AUTH_HEADER}'],
                    env: { AUTH_HEADER: `Basic ${mcpBasic(cred)}` },
                },
            },
        },
        null,
        4
    );
}

function mcpDate(ts: number): string {
    return new Date(ts * 1000).toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function mcpCredentialCard(app: App, cred: McpCredential): string {
    const revealed = app.mcpRevealed.has(cred.uuid);
    const verwendet = cred.lastUsed
        ? `Zuletzt verwendet ${mcpDate(cred.lastUsed)}${cred.lastIp ? ` von ${esc(cred.lastIp)}` : ""}`
        : "Noch nie verwendet";

    return `
    <div class="hi-card hi-mcp-cred">
        <div class="hi-mcp-cred__head">
            <div>
                <div class="hi-mcp-cred__name">${icon("key")} ${esc(cred.name)}</div>
                <div class="hi-mcp-cred__meta">Angelegt ${mcpDate(cred.created)} · ${verwendet}</div>
            </div>
            <button type="button" class="hi-icon-btn hi-icon-btn--danger" data-action="mcp-delete" data-uuid="${cred.uuid}" title="Verbindung widerrufen" aria-label="Verbindung widerrufen">${icon("trash-can")}</button>
        </div>
        <div class="hi-mcp-cred__secret">
            <span class="hi-mcp-cred__meta">Benutzer</span>
            <code class="hi-code">${esc(cred.user)}</code>
            <span class="hi-mcp-cred__meta">Passwort</span>
            <code class="hi-code">${revealed ? esc(cred.secret) : "•••• •••• ••••"}</code>
            <button type="button" class="hi-icon-btn" data-action="mcp-reveal" data-uuid="${cred.uuid}" title="${revealed ? "Passwort verbergen" : "Passwort anzeigen"}" aria-label="${revealed ? "Passwort verbergen" : "Passwort anzeigen"}">${icon(revealed ? "eye-slash" : "eye")}</button>
        </div>
        <div class="hi-mcp-cred__actions">
            <button type="button" class="hi-btn" data-action="mcp-copy-prompt" data-uuid="${cred.uuid}">${icon("copy")} Start-Prompt kopieren</button>
            <button type="button" class="hi-btn hi-btn--ghost" data-action="mcp-copy-command" data-uuid="${cred.uuid}">${icon("terminal")} Claude-Code-Befehl</button>
            <button type="button" class="hi-btn hi-btn--ghost" data-action="mcp-copy-config" data-uuid="${cred.uuid}">${icon("file-code")} Desktop-Konfiguration</button>
        </div>
    </div>`;
}

/**
 * Der Rundumschlag: löscht Räume, Positionen, Gegenstände, Import-Hinweise und
 * Fotos, damit ein Import von vorn beginnen kann.
 *
 * Erscheint nur, wenn der Server `canReset` gesetzt hat — also für einen
 * namentlichen Zugang auf einer `.test`-Domain. Auf der Produktivseite gibt es
 * diesen Abschnitt nicht, auch nicht versteckt.
 *
 * Der Vorgang ist endgültig, deshalb reicht ein Klick nicht: Das Wort muss
 * getippt werden. Ein Textfeld statt eines zweiten Knopfes, weil sich ein
 * „Wirklich?"-Dialog nach dem dritten Mal blind wegklicken lässt — abtippen
 * nicht.
 */
function dangerZone(app: App): string {
    if (!app.canReset) return "";

    if (app.resetProgress !== null) {
        return `
        <div class="hi-card hi-danger">
            <div class="hi-card__title hi-danger__title">${icon("trash-can")} Alles löschen</div>
            <p class="hi-danger__progress">${icon("spinner-third")}
                <span class="hi-danger__progress-text">${esc(app.resetProgress)}</span></p>
            <p class="hi-danger__hint">Das Fenster bitte offen lassen, bis der Vorgang durch ist.</p>
        </div>`;
    }

    const bestand = [
        `${app.rooms.length} ${app.rooms.length === 1 ? "Raum" : "Räume"}`,
        `${app.positions.length} ${app.positions.length === 1 ? "Position" : "Positionen"}`,
        `${app.items.length} ${app.items.length === 1 ? "Gegenstand" : "Gegenstände"}`,
        `${app.notes.length} ${app.notes.length === 1 ? "Hinweis" : "Hinweise"}`,
    ].join(", ");

    return `
    <div class="hi-card hi-danger">
        <div class="hi-card__title hi-danger__title">${icon("triangle-exclamation")} Alles löschen</div>
        <p>Löscht den <strong>gesamten</strong> Bestand — ${esc(bestand)} samt aller Fotos in der
        Mediathek — damit ein Import von vorn laufen kann. Der Vorgang lässt sich nicht rückgängig
        machen; es gibt keinen Papierkorb, aus dem sich das zurückholen ließe.</p>
        <form data-form="reset-all" class="hi-form hi-danger__form">
            <!-- Ohne Auszeichnungselement: .hi-field ist eine Flex-Spalte, jedes
                 Kindelement im Beschriftungstext bekäme dort eine eigene Zeile. -->
            <label class="hi-field">Zum Bestätigen das Wort „LÖSCHEN" eintippen
                <input class="hi-input" type="text" name="bestaetigung" autocomplete="off"
                       spellcheck="false" placeholder="LÖSCHEN">
            </label>
            <div class="hi-form__actions">
                <button type="submit" class="hi-btn hi-btn--danger">${icon("trash-can")} Alles unwiderruflich löschen</button>
            </div>
        </form>
        <p class="hi-danger__hint">Dieser Abschnitt erscheint nur auf dem Entwicklungsrechner.</p>
    </div>`;
}

export function renderMcp(app: App): string {
    const creds = app.mcpCredentials;

    const liste = creds === null
        ? `<div class="hi-empty">Verbindungen werden geladen …</div>`
        : creds.length === 0
            ? `<div class="hi-empty">Noch keine Verbindung angelegt. Legen Sie oben die erste an —
               danach genügt „Start-Prompt kopieren" und Einfügen in Claude oder ChatGPT.</div>`
            : creds.map((c) => mcpCredentialCard(app, c)).join("");

    return `
    <div class="hi-stack" style="max-width:960px">
        <div class="hi-card">
            <div class="hi-card__title">${icon("plug")} KI-Zugriff über MCP</div>
            <p>Über das Model Context Protocol kann eine KI (Claude, ChatGPT, …) dieses Inventar lesen und
            ändern — Preise anpassen, Werte recherchieren, Statistiken beantworten. In drei Schritten:
            Verbindung anlegen, Start-Prompt kopieren, in den KI-Chat einfügen.</p>
            <div class="hi-mcp-cred__secret">
                <span class="hi-mcp-cred__meta">Endpoint</span>
                <code class="hi-code">${esc(mcpEndpoint())}</code>
            </div>
            <p class="hi-mcp-note">Der Endpoint muss für den KI-Dienst erreichbar sein: Claude Code und
            Claude Desktop auf diesem Rechner erreichen ihn direkt; Cloud-Dienste wie claude.ai oder
            ChatGPT brauchen eine öffentlich erreichbare Adresse.</p>
        </div>

        <div class="hi-card">
            <div class="hi-card__title">Neue Verbindung</div>
            <form data-form="mcp-create" class="hi-form">
                <label class="hi-field">Name der Verbindung *
                    <input class="hi-input" type="text" name="name" required placeholder="z. B. Claude Desktop, ChatGPT unterwegs">
                </label>
                <div class="hi-form__actions">
                    <button type="submit" class="hi-btn">${icon("plus")} Verbindung anlegen</button>
                </div>
            </form>
            <p class="hi-mcp-note">Jede Verbindung ist ein eigenes WordPress-Anwendungspasswort und lässt
            sich hier einzeln widerrufen, ohne die anderen zu stören.</p>
        </div>

        ${liste}

        <p class="hi-mcp-note">Damit der Start-Prompt jederzeit — nicht nur beim Anlegen — kopiert werden
        kann, speichert die Verwaltung die Passwörter im Klartext in der WordPress-Datenbank. Widerrufen
        ist jederzeit möglich: hier über den Papierkorb oder im WordPress-Profil unter
        „Anwendungspasswörter".</p>

        ${dangerZone(app)}
    </div>`;
}

/* ------------------------------------------------------------ Vergleich */

/** Beschriftung und Farbe je Kategorie — einmal hier, überall gleich. */
const COMPARE_KATEGORIEN: { key: CompareCategory; label: string; hilfe: string }[] = [
    { key: "konflikt", label: "Konflikte", hilfe: "Gleiche Nummer, aber offensichtlich verschiedene Datensätze" },
    { key: "neu", label: "Nur in der Referenz", hilfe: "Dort angelegt, hier nicht vorhanden" },
    { key: "fehlt", label: "Nur hier", hilfe: "Hier vorhanden, in der Referenz nicht" },
    { key: "geaendert", label: "Geändert", hilfe: "Auf beiden Seiten vorhanden, mit abweichenden Feldern" },
];

const COMPARE_TYPEN: { key: CompareKind; label: string }[] = [
    { key: "item", label: "Gegenstände" },
    { key: "position", label: "Positionen" },
    { key: "room", label: "Räume" },
    { key: "note", label: "Import-Hinweise" },
];

function kategorieLabel(kategorie: CompareCategory): string {
    return COMPARE_KATEGORIEN.find((k) => k.key === kategorie)?.label ?? kategorie;
}

/**
 * Ansicht „Vergleich": stellt den eigenen Stand dem einer anderen Instanz
 * gegenüber.
 *
 * Die Reihenfolge der Karten folgt dem, was zu tun ist: erst der Zugang, dann
 * die Zahlen im Überblick, dann die Unterschiede im Einzelnen. Geschrieben
 * wird nichts — die Ansicht zeigt nur, was anders ist.
 */
export function renderCompare(app: App): string {
    const ref = app.compareReference;
    const zeigeFormular = !ref || app.compareEditOpen;

    return `
    <div class="hi-stack" style="max-width:1100px">
        <div class="hi-card">
            <div class="hi-card__title">${icon("code-compare")} Referenz-Instanz</div>
            <p>Dieselbe Aufstellung läuft an mehreren Orten. Tragen Sie hier die Instanz ein, gegen die
            verglichen werden soll — die Ansicht zeigt dann, was dort neu ist, was hier fehlt und welche
            Felder sich unterscheiden. Geändert wird dabei nichts, auf keiner der beiden Seiten.</p>
            ${zeigeFormular ? compareForm(app) : compareConnection(app)}
        </div>

        ${app.compareError ? `<div class="hi-card hi-compare__error">${icon("triangle-exclamation")} ${esc(app.compareError)}</div>` : ""}

        ${app.compareResult ? compareSummary(app, app.compareResult) : ""}

        ${app.compareResult ? `
        <div class="hi-card hi-card--flush">
            <div class="hi-toolbar">
                <input type="search" class="hi-input" data-compare-filter="q" placeholder="In den Unterschieden suchen …" value="${esc(app.compareFilter.q)}">
                <select class="hi-input" data-compare-filter="kind">
                    <option value="">Alle Typen</option>
                    ${COMPARE_TYPEN.map((t) => option(t.key, t.label, app.compareFilter.kind)).join("")}
                </select>
                <select class="hi-input" data-compare-filter="kategorie">
                    <option value="">Alle Unterschiede</option>
                    ${COMPARE_KATEGORIEN.map((k) => option(k.key, k.label, app.compareFilter.kategorie)).join("")}
                </select>
                <button type="button" class="hi-chip${app.compareShowMinor ? " hi-chip--active" : ""}"
                    data-action="compare-minor"
                    title="Reihenfolge, Bilddatei und Import-Protokolle mitzeigen — sie ändern sich bei jedem Import, ohne dass die Aufstellung selbst anders wird">
                    ${icon("eye")} Nebensachen
                </button>
            </div>
            <div id="hi-compare-results">${renderCompareResults(app)}</div>
        </div>` : ""}
    </div>`;
}

/** Eingabe des Zugangs — Vorlage ist die Karte unter „KI-Zugriff". */
function compareForm(app: App): string {
    const ref = app.compareReference;
    return `
    <form data-form="compare-reference" class="hi-form">
        <label class="hi-field">Adresse der Referenz *
            <input class="hi-input" type="url" name="url" required placeholder="https://hausinventar.example.de"
                value="${esc(ref?.url ?? "")}">
        </label>
        <div class="hi-grid-2">
            <label class="hi-field">Benutzername dort *
                <input class="hi-input" type="text" name="user" required autocomplete="off" value="${esc(ref?.user ?? "")}">
            </label>
            <label class="hi-field">Anwendungspasswort *
                <input class="hi-input" type="password" name="secret" autocomplete="off"
                    placeholder="${ref?.secret ? "unverändert lassen" : "xxxx xxxx xxxx xxxx xxxx xxxx"}"
                    ${ref?.secret ? "" : "required"}>
            </label>
        </div>
        <div class="hi-form__actions">
            ${ref ? `<button type="button" class="hi-btn hi-btn--ghost" data-action="compare-cancel-edit">Abbrechen</button>
            <button type="button" class="hi-btn hi-btn--ghost" data-action="compare-forget">Zugang vergessen</button>` : ""}
            <button type="submit" class="hi-btn">${icon("arrow-right")} Verbinden und vergleichen</button>
        </div>
    </form>
    <p class="hi-mcp-note">Das Anwendungspasswort legen Sie auf der Referenz im WordPress-Profil unter
    „Anwendungspasswörter" an; der Zugang braucht dort mindestens die Rolle Redakteur. Es wird in diesem
    Browser gespeichert, damit der Abruf ohne erneute Eingabe läuft — „Zugang vergessen" löscht es wieder.
    Fotos und Bildausschnitte bleiben beim Vergleich außen vor: Ihre Nummern werden je Instanz vergeben,
    ein Vergleich wäre bloßes Rauschen.</p>`;
}

/** Zustandszeile, sobald ein Zugang hinterlegt ist. */
function compareConnection(app: App): string {
    const ref = app.compareReference!;
    const snap = app.compareSnapshot;
    return `
    <div class="hi-compare__conn">
        <code class="hi-code">${esc(ref.url)}</code>
        <span class="hi-cell-sub">Benutzer: ${esc(ref.user)}</span>
        ${snap ? `<span class="hi-status hi-status--geprueft">${icon("check")} ${esc(snap.site.name)}${snap.site.version ? ` · Fassung ${esc(snap.site.version)}` : ""}</span>` : ""}
    </div>
    ${snap ? `<p class="hi-mcp-note">Stand der Referenz abgerufen am ${esc(abrufZeit(snap.site.erzeugt))}. Ein erneuter Abruf holt ihn frisch.</p>` : ""}
    <div class="hi-form__actions">
        <button type="button" class="hi-btn hi-btn--ghost" data-action="compare-edit">${icon("pen")} Zugang ändern</button>
        <button type="button" class="hi-btn"${app.compareLoading ? " disabled" : ""} data-action="compare-run">
            ${app.compareLoading ? `${icon("spinner-third", "fa-spin")} Referenz wird gelesen …` : `${icon("code-compare")} ${app.compareResult ? "Erneut vergleichen" : "Unterschiede ermitteln"}`}
        </button>
    </div>`;
}

function abrufZeit(iso: string): string {
    const datum = new Date(iso);
    return Number.isNaN(datum.getTime()) ? iso : datum.toLocaleString("de-DE");
}

/** Kacheln: was ist anders, und was macht es in Euro aus. */
function compareSummary(app: App, result: CompareResult): string {
    const s = result.summary;
    const differenz = s.wertReferenz - s.wertHier;
    const geprueft = s.gleich + result.entries.length;

    return `
    <div class="hi-card">
        <div class="hi-stats">
            ${COMPARE_KATEGORIEN.map((k) => `
            <div class="hi-stat${app.compareFilter.kategorie === k.key ? " hi-stat--aktiv" : ""}"
                data-action="compare-category" data-value="${k.key}" title="${esc(k.hilfe)}">
                <div class="hi-stat__value">${s.kategorien[k.key]}</div>
                <div class="hi-stat__label">${esc(k.label)}</div>
            </div>`).join("")}
        </div>
        <div class="hi-stats">
            <div class="hi-stat"><div class="hi-stat__value">${fmt(s.wertHier)}</div><div class="hi-stat__label">Gesamtwert hier</div></div>
            <div class="hi-stat"><div class="hi-stat__value">${fmt(s.wertReferenz)}</div><div class="hi-stat__label">Gesamtwert Referenz</div></div>
            <div class="hi-stat"><div class="hi-stat__value">${differenz > 0 ? "+" : ""}${fmt(differenz)}</div><div class="hi-stat__label">Differenz</div></div>
            <div class="hi-stat"><div class="hi-stat__value">${s.gleich}</div><div class="hi-stat__label">von ${geprueft} unverändert</div></div>
        </div>
    </div>`;
}

/**
 * Die gefilterten Unterschiede.
 *
 * Ein Eintrag, dessen sämtliche Abweichungen Nebensachen sind, fällt heraus,
 * solange die Nebensachen ausgeblendet sind — sonst stünde dort eine Karte
 * ohne sichtbaren Inhalt.
 */
export function filteredCompareEntries(app: App): CompareEntry[] {
    const { kind, kategorie, q } = app.compareFilter;
    const suche = q.trim().toLowerCase();

    return (app.compareResult?.entries ?? []).filter((e) => {
        if (kind && e.kind !== kind) return false;
        if (kategorie && e.kategorie !== kategorie) return false;
        if (!app.compareShowMinor && e.felder.length > 0 && e.felder.every((f) => f.minor)) return false;
        if (suche && !matchesId(suche, e.id) && !matchesFilter(suche, e.titel, e.ort, ...e.felder.map((f) => f.label))) return false;
        return true;
    });
}

export function renderCompareResults(app: App): string {
    const result = app.compareResult;
    if (!result) return "";

    const treffer = filteredCompareEntries(app);
    if (treffer.length === 0) {
        return result.entries.length === 0
            ? `<div class="hi-empty">Kein Unterschied gefunden — beide Stände sind gleich (${result.summary.gleich} Datensätze geprüft).</div>`
            : `<div class="hi-empty">Keine Treffer. Passen Sie Suche oder Filter an.</div>`;
    }

    const sichtbar = treffer.slice(0, app.compareLimit);
    const hierName = app.instanceLabel || "diese Instanz";
    const dortName = app.compareSnapshot?.site.name || "Referenz";

    return `
    <div class="hi-compare-head">
        <span>Feld</span>
        <span>Hier · ${esc(hierName)}</span>
        <span>Referenz · ${esc(dortName)}</span>
    </div>
    ${sichtbar.map((e) => compareEntryCard(app, e)).join("")}
    ${treffer.length > sichtbar.length ? `
    <div class="hi-compare-more">
        <span>${sichtbar.length} von ${treffer.length} Unterschieden</span>
        <button type="button" class="hi-btn hi-btn--secondary" data-action="compare-more">Weitere 200 anzeigen</button>
    </div>` : `<div class="hi-list-summary"><span>${treffer.length} Unterschiede</span></div>`}`;
}

function compareEntryCard(app: App, entry: CompareEntry): string {
    const felder = entry.felder.filter((f) => app.compareShowMinor || !f.minor);
    const ref = app.compareReference;

    // "Hier öffnen" gibt es nur für das, was hier existiert; der Weg in die
    // Referenz führt in deren WordPress-Verwaltung — eine eigene Ansicht für
    // fremde Daten wäre eine zweite Wahrheit.
    const hierLink = entry.kategorie === "neu" ? "" : compareOpenButton(entry);
    const dortLink = entry.kategorie === "fehlt" || !ref ? "" :
        `<a class="hi-btn hi-btn--ghost" href="${esc(ref.url)}/wp-admin/post.php?post=${entry.id}&action=edit" target="_blank" rel="noopener">${icon("arrow-up-right-from-square")} In der Referenz öffnen</a>`;

    return `
    <div class="hi-diff hi-diff--${entry.kategorie}">
        <div class="hi-diff__head">
            <span class="hi-diff__title">${entry.kind === "note" ? icon("triangle-exclamation") : typeIcon(entry.kind)} ${esc(entry.titel)} ${idTag(entry.id, "hi-diff__id")}</span>
            ${entry.ort ? `<span class="hi-diff__ort">${esc(entry.ort)}</span>` : ""}
            <span class="hi-status hi-status--diff-${entry.kategorie}">${esc(kategorieLabel(entry.kategorie))}</span>
            ${entry.wertDelta ? `<span class="hi-diff__delta">${entry.wertDelta > 0 ? "+" : ""}${fmt(entry.wertDelta)}</span>` : ""}
        </div>
        ${entry.kategorie === "konflikt" ? `<p class="hi-diff__warn">${icon("triangle-exclamation")} Beide Seiten führen unter dieser Nummer einen anderen Datensatz. Das passiert, wenn nach dem Kopieren der Datenbank auf beiden Seiten neu angelegt wurde — die Gegenüberstellung unten vergleicht dann zwei verschiedene Dinge.</p>` : ""}
        ${felder.length ? `<div class="hi-diff__fields">${felder.map(compareFieldRow).join("")}</div>` : ""}
        ${entry.felder.length && !felder.length ? `<p class="hi-diff__warn">Nur Nebensachen abweichend — über „Nebensachen" einblendbar.</p>` : ""}
        <div class="hi-diff__actions">${hierLink}${dortLink}</div>
    </div>`;
}

/** Der Weg zum Datensatz in dieser Instanz, je nach Typ. */
function compareOpenButton(entry: CompareEntry): string {
    const ziel: Record<CompareKind, { action: string; label: string }> = {
        item: { action: "open-item", label: "Gegenstand öffnen" },
        position: { action: "open-position", label: "Position öffnen" },
        room: { action: "open-room", label: "Raum öffnen" },
        note: { action: "nav", label: "Zu den Import-Hinweisen" },
    };
    const z = ziel[entry.kind];
    const extra = entry.kind === "note" ? ` data-view="notes"` : ` data-id="${entry.id}"`;
    return `<button type="button" class="hi-btn hi-btn--ghost" data-action="${z.action}"${extra}>${icon("arrow-right")} ${z.label}</button>`;
}

function compareFieldRow(feld: FieldDiff): string {
    return `
        <div class="hi-diff__label">${esc(feld.label)}</div>
        <div class="hi-diff__value hi-diff__value--hier">${compareValue(feld.hier)}</div>
        <div class="hi-diff__value hi-diff__value--referenz">${compareValue(feld.referenz)}</div>`;
}

function compareValue(wert: string | string[]): string {
    if (Array.isArray(wert)) {
        return wert.length
            ? `<ul class="hi-diff__list">${wert.map((z) => `<li>${esc(z)}</li>`).join("")}</ul>`
            : `<span class="hi-diff__leer">—</span>`;
    }
    return wert ? esc(wert) : `<span class="hi-diff__leer">—</span>`;
}

export function renderView(app: App): string {
    switch (app.view) {
        case "positions": return renderPositionsList(app);
        case "review": return renderReview(app);
        case "rooms": return renderRooms(app);
        case "room": return renderRoomDetail(app);
        case "position": return renderPositionDetail(app);
        case "form": return renderPositionForm(app);
        case "export": return renderExport(app);
        case "duplicates": return renderDuplicates(app);
        case "notes": return renderNotes(app);
        case "search": return renderSearch(app);
        case "items": return renderItemsList(app);
        case "mcp": return renderMcp(app);
        case "compare": return renderCompare(app);
        default: return renderDashboard(app);
    }
}
